import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ServiceScheduleList from './ServiceScheduleList';
import { REASON_META, primaryReason, RED_REASONS, RELEASABLE } from '../../utils/shiftReasons';
import { fmtTime12, isoDate } from '../../utils/time';
import {
  format, startOfWeek, addDays, isSameDay, isToday, parseISO,
  addWeeks, subWeeks, addMonths, subMonths, eachDayOfInterval,
  startOfMonth, endOfMonth, startOfDay,
} from 'date-fns';
import {
  DndContext, DragOverlay, useDraggable, useDroppable,
  PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Plus, ChevronLeft, ChevronRight, Users, UserCircle, UserPlus, Building2,
  MoreVertical, RefreshCw, Filter, Search, ChevronDown, X, Save,
  Loader2, Clock, Send, Check, AlertCircle, Briefcase, Eye, Copy,
  Trash2, Calendar, MapPin, RotateCcw, FileText, Pencil, Sparkles,
  Car, PenLine, Bell, Info, Repeat2, Home, CreditCard,
  Bold, Italic, Underline, Strikethrough, Code, Link, List,
  AlignLeft, AlignCenter, AlignRight, Smile, Paperclip, ChevronUp,
  Wand2, ListChecks, Ban, Star, Lock, Unlock, LogIn, LogOut, Flag,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import SchedulingAssistantModal from './SchedulingAssistantModal';
import GroupShiftModal from '../Groups/GroupShiftModal';
import { useConfirm } from '../../components/ConfirmDialog';
import { withComplianceOverride } from '../../utils/complianceOverride';
import PreShiftBriefing from '../../components/PreShiftBriefing';
import Avatar from '../../components/Avatar';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import clsx from 'clsx';
import { useShiftTypes } from '../../hooks/useSettings';
import { useFeatures } from '../../context/FeatureContext';
import { hoursBetween } from '../../utils/format';

// What a cancellation policy actually pays the rostered worker. Both modals used to state
// "paid the full scheduled shift" unconditionally, which is what the backend did and what the
// policy's own max_payment_hours cap contradicted — an admin picking "pay worker, 2 hours maximum"
// was told, and charged, for eight.
const workerPayLabel = (p) => !p || !p.pay_worker ? 'not paid'
  : (p.max_payment_hours != null && Number(p.max_payment_hours) > 0
      ? `paid up to ${Number(p.max_payment_hours)}h`
      : 'paid the full scheduled shift');

// ─── Constants ───────────────────────────────────────────────────────────────

const COLOR_PALETTE = [
  { bg: '#f0fdf4', border: '#16a34a', text: '#15803d' },
  { bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  { bg: '#fff7ed', border: '#f97316', text: '#c2410c' },
  { bg: '#fdf4ff', border: '#a855f7', text: '#7e22ce' },
  { bg: '#f0f9ff', border: '#0891b2', text: '#0e7490' },
  { bg: '#fefce8', border: '#ca8a04', text: '#92400e' },
  { bg: '#fef2f2', border: '#ef4444', text: '#b91c1c' },
  { bg: '#f0fdf4', border: '#10b981', text: '#047857' },
  { bg: '#fdf2f8', border: '#ec4899', text: '#be185d' },
  { bg: '#eef2ff', border: '#6366f1', text: '#4338ca' },
];

function hashColor(str) {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = ((h << 5) - h + (str || '').charCodeAt(i)) | 0;
  return COLOR_PALETTE[Math.abs(h) % COLOR_PALETTE.length];
}

const VIEWS = ['Weekly', '10-Day', 'Fortnightly', 'Monthly', 'Daily'];

const STATUSES = [
  { value: 'booked',    label: 'Booked',    color: '#3b82f6' },
  { value: 'completed', label: 'Completed', color: '#10b981' },
  { value: 'cancelled', label: 'Cancelled', color: '#94a3b8' },
  { value: 'pending',   label: 'Pending',   color: '#f59e0b' },
];

const ROW_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899', '#14b8a6'];

// A row's avatar colour comes from the person's id, so it stays the same however the board is
// sorted or searched (it used to follow the row's position).
const rowColorFor = (id) => {
  const s = String(id || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return ROW_COLORS[Math.abs(h) % ROW_COLORS.length];
};

// A service shown by its Settings name, or its key made readable ('personal_care' → 'Personal care').
const serviceName = (shiftTypes, key) => {
  const k = (key || '').trim();
  if (!k) return '';
  const hit = (shiftTypes || []).find((t) => t.name === k || t.key === k || t.code === k);
  if (hit?.name) return hit.name;
  const words = k.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Card colour for a shift — derive a clearly-visible tint (~20%) from the service's
// border colour so the service is recognisable at a glance (not a near-white pastel).
const getColor = (shift) => { const c = hashColor(shift?.shift_type); return { ...c, bg: c.border + '33' }; };

// Pending-reason diagnostics (REASON_META / primaryReason / RED_REASONS) now live in
// ../../utils/shiftReasons — single source of truth shared with the admin surfaces.

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.slice(0, 5).split(':');
  const hr = parseInt(h);
  const ampm = hr >= 12 ? 'pm' : 'am';
  const h12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return m === '00' ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
};

// An end at or before the start finishes the next day (5pm–12am is 7 hours), the same rule the
// shifts API uses. This used to floor the gap at 0, so overnight shifts showed 0.00 hrs.
const calcHours = (start, end) => (!start || !end ? 0 : hoursBetween(start, end));

const getWeekDays = (date) => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

const getTenDays = (date) => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  return Array.from({ length: 10 }, (_, i) => addDays(start, i));
};

const getFortnightDays = (date) => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  return Array.from({ length: 14 }, (_, i) => addDays(start, i));
};

const getMonthDays = (date) => {
  const start = startOfMonth(date);
  const end = endOfMonth(date);
  const weekStart = startOfWeek(start, { weekStartsOn: 1 });
  const days = [];
  let cur = weekStart;
  while (cur <= end || days.length % 7 !== 0) {
    days.push(cur);
    cur = addDays(cur, 1);
    if (days.length > 42) break;
  }
  return days;
};

const isSameDayStr = (shiftDate, day) => {
  try { return isSameDay(parseISO(shiftDate.split('T')[0]), day); } catch { return false; }
};

const AU_PUBLIC_HOLIDAYS = new Set([
  // 2025
  '2025-01-01','2025-01-27','2025-04-18','2025-04-19','2025-04-21','2025-04-25',
  '2025-06-09','2025-12-25','2025-12-26',
  // 2026
  '2026-01-01','2026-01-26','2026-04-03','2026-04-04','2026-04-06','2026-04-25',
  '2026-06-08','2026-12-25','2026-12-28',
]);

const isWeekendDay = (day) => { const d = day.getDay(); return d === 0 || d === 6; };
const isPublicHoliday = (day) => AU_PUBLIC_HOLIDAYS.has(format(day, 'yyyy-MM-dd'));

// ─── ShiftCard ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  booked:    { color: '#3b82f6', label: 'Booked',    dot: 'bg-blue-400' },
  completed: { color: '#10b981', label: 'Completed', dot: 'bg-emerald-400' },
  cancelled: { color: '#94a3b8', label: 'Cancelled', dot: 'bg-slate-300' },
  cancelled_with_charge: { color: '#f59e0b', label: 'Cancelled (charged)', dot: 'bg-amber-400' },
  pending:   { color: '#f59e0b', label: 'Pending',   dot: 'bg-amber-400' },
};

function getClockStatus(shift) {
  if (!shift.shift_date || !shift.start_time) return null;
  if (['cancelled', 'cancelled_with_charge', 'declined'].includes(shift.status)) return null;
  const now = new Date();
  const shiftStart = new Date(`${shift.shift_date}T${shift.start_time}`);
  if (now < shiftStart) return null; // future — nothing to show yet
  if (shift.clock_in_time) {
    const clockIn = new Date(shift.clock_in_time);
    const lateMin = Math.round((clockIn - shiftStart) / 60000);
    if (lateMin > 5) return { type: 'late', minutes: lateMin, time: format(clockIn, 'h:mm a') };
    return { type: 'ontime', time: format(clockIn, 'h:mm a') };
  }
  return { type: 'missing' };
}

// Per-shift drag lock: a shift can't be dragged until the user unlocks it (prevents accidental
// moves). The unlocked set + toggle are shared via context so we don't prop-drill through every grid.
const DragLockContext = React.createContext({ unlocked: null, toggleLock: () => {} });

function ShiftCard({ shift, compact, isOverlay, onClick, onContextMenu, showStaff, bulkMode, isSelected, onToggleSelect }) {
  const features = useFeatures();
  const dl = React.useContext(DragLockContext);
  const dragLocked = !isOverlay && !!dl?.unlocked && !dl.unlocked.has(shift.id);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `shift_${shift.id}`,
    data: { shift },
    disabled: isOverlay || bulkMode || dragLocked,
  });

  // Card colours — Brevity scheme (two layers):
  //  • TOP BAR  = the SERVICE colour (its identity) — Settings → Service Types colour.
  //  • BODY     = the STATUS colour, so you scan the board for problems at a glance:
  //      grey = draft/cancelled · blue = published/booked · amber = warning or pending ·
  //      red = blocking issue (clash / no fund / no budget) · green = completed.
  const shiftTypes = useShiftTypes();
  const noType = !((shift?.shift_type || '').trim());   // no service type picked → flag it yellow
  const stColor = shiftTypes?.find(t => t.name === shift?.shift_type && t.color)?.color;
  const serviceColor = stColor || (noType ? '#f59e0b' : getColor(shift).border); // top-bar identity colour (yellow when no service type)
  const hours = calcHours(shift.start_time, shift.end_time);
  const isCancelled = shift.status === 'cancelled' || shift.status === 'cancelled_with_charge';
  const cancelCharge = shift.status === 'cancelled_with_charge' ? (parseFloat(shift.cancellation_charge) || 0) : 0;
  const reason = primaryReason(shift);
  // A shift is "completed" for display once it's actually been delivered — the worker clocked out —
  // or it reached a completed lifecycle status (completed/invoiced/paid). This is display-only: it
  // does NOT change the shift's stored status or the invoicing lifecycle.
  const isDelivered = !isCancelled && (!!shift.clock_out_time || ['completed', 'invoiced', 'paid'].includes(shift.status));
  // No worker assigned yet — keep the card yellow and offer an "add worker" affordance.
  const unassigned = !shift.staff_id && !isCancelled && !isDelivered;
  const bodyBg =
      isCancelled                                                  ? '#f1f5f9'  // grey
    : isDelivered                                                  ? '#ecfdf5'  // green — delivered/done
    : noType                                                       ? '#fef9c3'  // yellow — no service type picked (whole card flags it)
    : (reason && RED_REASONS.includes(reason))                     ? '#fef2f2'  // red — blocking
    : unassigned                                                   ? '#fef9c3'  // yellow — needs a worker assigned
    : reason                                                       ? '#fffbeb'  // amber — warning
    : (features.publish_shifts && !shift.is_published)             ? '#f8fafc'  // grey — draft/unpublished
    : shift.status === 'pending'                                   ? '#fffbeb'  // amber — pending
    :                                                               '#eff6ff'; // blue — published/booked
  const sc = isDelivered ? STATUS_CONFIG.completed : (STATUS_CONFIG[shift.status] || STATUS_CONFIG.booked);
  const style = transform ? { transform: CSS.Translate.toString(transform), zIndex: 50 } : {};
  const displayName = showStaff
    ? (shift.staff_name || null)
    : (shift.client_names ? shift.client_names.split(',')[0].trim() : null);
  // The task the worker is asked to do — shown on the card so admins don't open each shift.
  // Fall back to the service name when no activity/roster note, so a card is never blank.
  const task = (shift.activity || shift.roster_comments || '').trim();
  const cardLabel = task || serviceName(shiftTypes, shift.shift_type);
  // Company vehicle assigned to this shift → show a van chip so admins see it at a glance.
  const vehicleLabel = (shift.vehicle_make || shift.vehicle_model || shift.vehicle_registration)
    ? ([shift.vehicle_make, shift.vehicle_model].filter(Boolean).join(' ').trim() || shift.vehicle_registration)
    : null;
  // App login/location status (from the worker's mobile clock-in/out timesheet).
  // Left icon = clocked in, right icon = clocked out. Green = on location (geofence-verified), red = off
  // location, grey = no GPS fix. clock_*_verified DEFAULT false, so "no GPS" is read from the distance
  // (NULL), never from verified === false — an unknown location must not look like a violation.
  const clockedIn  = !!shift.clock_in_time;
  const clockedOut = !!shift.clock_out_time;
  const inOnLoc    = shift.clock_in_verified === true;
  const outOnLoc   = shift.clock_out_verified === true;
  const inDist     = shift.clock_in_distance_m  != null ? Number(shift.clock_in_distance_m)  : null;
  const outDist    = shift.clock_out_distance_m != null ? Number(shift.clock_out_distance_m) : null;
  const geoTitle = (verb, onLoc, dist) => dist == null
    ? (onLoc ? `${verb} — verified (manual)` : `${verb} — no GPS`)
    : `${verb} ${dist} m from client${onLoc ? '' : ' — outside geofence'}`;
  const geoCls = (onLoc, dist) => onLoc ? 'text-emerald-600' : dist == null ? 'text-slate-400' : 'text-red-500';
  const inTitle  = geoTitle('Clocked in', inOnLoc, inDist);
  const outTitle = geoTitle('Clocked out', outOnLoc, outDist);
  const clamp2 = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        ...style,
        backgroundColor: bodyBg,
        // Cancelled shifts read as fully grey/"dead" (Brevity); else the service-colour bar.
        borderTop: `4px solid ${isCancelled ? '#cbd5e1' : serviceColor}`,
        opacity: isDragging ? 0.3 : (isCancelled ? 0.8 : 1),
        cursor: isOverlay || bulkMode ? 'pointer' : dragLocked ? 'default' : (isDragging ? 'grabbing' : 'grab'),
      }}
      className={clsx(
        'rounded-lg border border-slate-200/70 select-none',
        'group relative overflow-hidden transition-all duration-150',
        'hover:shadow-md hover:-translate-y-px',
        compact ? 'p-1 mb-0.5' : 'p-2 mb-1.5',
        bulkMode && isSelected && 'ring-2 ring-violet-400',
      )}
      onClick={(e) => { e.stopPropagation(); if (bulkMode) { onToggleSelect?.(shift.id); } else { onClick?.(shift); } }}
      // Enter / Space open (or select) a focused card, like a click.
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault(); e.stopPropagation();
          if (bulkMode) onToggleSelect?.(shift.id); else onClick?.(shift);
        }
      }}
      onContextMenu={(e) => { e.preventDefault(); onContextMenu?.(e, shift); }}
      title={cardLabel || undefined}
    >
      {bulkMode && (
        <div className="absolute top-1 right-1 z-10">
          <div className={clsx('w-3.5 h-3.5 rounded border-2 flex items-center justify-center',
            isSelected ? 'bg-violet-500 border-violet-500' : 'bg-white border-slate-400')}>
            {isSelected && <Check size={8} className="text-white" />}
          </div>
        </div>
      )}
      {!bulkMode && !isOverlay && dl?.unlocked && (
        // Lock toggle — a shift must be unlocked before it can be dragged (then re-locks after a move).
        <button
          onClick={(e) => { e.stopPropagation(); dl.toggleLock(shift.id); }}
          onPointerDown={(e) => e.stopPropagation()}
          title={dragLocked ? 'Locked — click to unlock, then drag to move' : 'Unlocked — drag to move (click to re-lock)'}
          className={clsx('absolute top-0.5 right-0.5 z-20 p-0.5 rounded',
            dragLocked ? 'text-slate-300 opacity-0 group-hover:opacity-100 hover:text-slate-600 bg-white/70' : 'text-amber-600 bg-amber-100')}
        >
          {dragLocked ? <Lock size={10} /> : <Unlock size={10} />}
        </button>
      )}
      {compact ? (
        // Dense card (Fortnightly / narrow columns): the time range owns its own line so it
        // never truncates mid-string; duration + status live in the hover tooltip. Status
        // icons sit on a second micro-row with the name so nothing fights the time for width.
        <div className="min-w-0"
          title={`${fmtTime(shift.start_time)}–${fmtTime(shift.end_time)} · ${hours.toFixed(2)} hrs${displayName ? ` · ${displayName}` : ''}`}>
          <p className={clsx('text-[10px] font-black truncate leading-tight tracking-tight', isCancelled && 'line-through')}
            style={{ color: isCancelled ? '#94a3b8' : '#334155' }}>
            {fmtTime(shift.start_time)}–{fmtTime(shift.end_time)}
          </p>
          <div className="flex items-center gap-0.5 mt-0.5 min-w-0">
            {clockedIn && <LogIn size={8} className={clsx('shrink-0', geoCls(inOnLoc, inDist))} title={inTitle} />}
            {clockedOut && <LogOut size={8} className={clsx('shrink-0', geoCls(outOnLoc, outDist))} title={outTitle} />}
            {!isCancelled && shift.has_note && <FileText size={8} className="shrink-0 text-emerald-600" title="Progress note written" />}
            {!isCancelled && !shift.has_note && (isDelivered || clockedIn) && <FileText size={8} className="shrink-0 text-slate-300" title="No progress note yet" />}
            {!isCancelled && shift.has_incident && <AlertCircle size={8} className="shrink-0 text-rose-500" title="Incident reported on this shift" />}
            {!isCancelled && shift.weekly_covered && <span className="shrink-0 text-[8px] font-black leading-none px-0.5 rounded bg-teal-100 text-teal-800" title="SIL Weekly: $0.00, covered by the weekly SIL claim">SIL</span>}
            {reason && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: RED_REASONS.includes(reason) ? '#ef4444' : '#f59e0b' }} title={shift.pending_detail || REASON_META[reason]?.tip} />}
            {vehicleLabel && <Car size={9} className="shrink-0 text-sky-600" title={`Vehicle: ${vehicleLabel}`} />}
            {shift.shift_address && <MapPin size={9} className="shrink-0 text-violet-600" title={`Custom location: ${shift.shift_address}`} />}
            {shift.shift_end_address && <Flag size={9} className="shrink-0 text-violet-600" title={`Ends at: ${shift.shift_end_address}`} />}
            {displayName
              ? <span className="text-[9px] font-semibold italic truncate leading-tight text-slate-500 min-w-0">{displayName}</span>
              : unassigned && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold italic truncate leading-tight text-amber-700 min-w-0"><UserPlus size={9} className="shrink-0" />Add</span>}
          </div>
        </div>
      ) : (
        <>
          {/* Time + duration — primary (Brevity-style) */}
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1 min-w-0">
              {clockedIn && (
                <LogIn size={11} className={clsx('shrink-0', geoCls(inOnLoc, inDist))} title={inTitle} />
              )}
              <p className={clsx('text-[11px] font-black leading-tight', isCancelled && 'line-through')} style={{ color: isCancelled ? '#94a3b8' : '#334155' }}>
                {fmtTime(shift.start_time)}–{fmtTime(shift.end_time)}
                <span className="font-semibold text-slate-400 ml-1">· {hours.toFixed(2)} hrs</span>
              </p>
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              {clockedOut && (
                <LogOut size={11} className={clsx('shrink-0', geoCls(outOnLoc, outDist))} title={outTitle} />
              )}
              {shift.confirmation_required && <AlertCircle size={10} className="text-amber-400" />}
              {shift.is_recurring && <RefreshCw size={9} className="text-slate-300" />}
            </div>
          </div>

          {/* Task — activity / roster comments, else the service name (never blank) */}
          {cardLabel && (
            <p className="text-[10px] text-slate-500 leading-snug mt-1" style={clamp2}>{cardLabel}</p>
          )}

          {/* Person name — italic (worker in Client view, client in Staff view) */}
          {displayName && (
            <p className={clsx('text-[10.5px] font-bold italic truncate leading-tight mt-1', isCancelled ? 'text-slate-400 line-through' : 'text-slate-700')}>
              {displayName}
            </p>
          )}

          {/* No worker yet → prompt to assign one (opens the shift editor). */}
          {unassigned && !isOverlay && (
            <button
              onClick={(e) => { e.stopPropagation(); onClick?.(shift); }}
              onPointerDown={(e) => e.stopPropagation()}
              title="No worker assigned — click to assign"
              className="mt-1 inline-flex items-center gap-1 text-[9.5px] font-bold text-amber-800 bg-amber-200/70 hover:bg-amber-300 rounded px-1.5 py-0.5 transition-colors"
            >
              <UserPlus size={10} /> Add worker
            </button>
          )}

          {/* Badges row */}
          <div className="flex items-center gap-1 flex-wrap mt-1.5">
            {/* Status badge — hidden on unpublished shifts (a DRAFT isn't "booked"); the DRAFT +
                any NO FUND/NO BUDGET flag convey the real state instead of a contradictory "BOOKED". */}
            {(!features.publish_shifts || shift.is_published || isDelivered) && (
              <span className="text-[10px] font-bold px-1 py-0.5 rounded leading-none uppercase tracking-wide"
                style={{ backgroundColor: sc.color + '18', color: sc.color }}>
                {sc.label}
              </span>
            )}
            {/* Note status — a green "Notes" chip whenever a progress note exists on the shift,
                regardless of status. For a worked/completed shift with no note, show grey "No notes". */}
            {!isCancelled && shift.has_note && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none bg-emerald-100 text-emerald-700" title="Progress note written">
                <FileText size={9} className="shrink-0" />Notes
              </span>
            )}
            {!isCancelled && !shift.has_note && (isDelivered || clockedIn) && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none bg-slate-100 text-slate-600" title="No progress note yet">
                <FileText size={9} className="shrink-0" />No notes
              </span>
            )}
            {/* Paid by the client's weekly SIL claim: $0 on the shift; the worker is still paid. */}
            {!isCancelled && shift.weekly_covered && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none bg-teal-100 text-teal-800" title="Covered by weekly SIL claim: client charge $0.00">SIL Weekly · $0</span>
            )}
            {!isCancelled && shift.irregular_sil && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none bg-violet-100 text-violet-800" title="Irregular SIL: claimed on its own (IRSS), outside the weekly amount">Irregular SIL</span>
            )}
            {/* Incident reported on this shift — always flag it, regardless of status. */}
            {!isCancelled && shift.has_incident && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none bg-rose-100 text-rose-700" title="Incident reported on this shift">
                <AlertCircle size={9} className="shrink-0" />Incident
              </span>
            )}
            {cancelCharge > 0 && (
              <span className="text-[10px] font-bold px-1 py-0.5 rounded leading-none bg-rose-100 text-rose-600" title={`Short-notice charge: $${cancelCharge.toFixed(2)}`}>${cancelCharge.toFixed(0)}</span>
            )}
            {isCancelled && shift.cancellation_policy_name && (
              <span className="text-[10px] px-1 py-0.5 rounded leading-none bg-slate-100 text-slate-500 truncate max-w-[90px]" title={`Policy: ${shift.cancellation_policy_name}`}>{shift.cancellation_policy_name}</span>
            )}
            {(() => {
              const code = primaryReason(shift);
              if (!code) return null;
              const m = REASON_META[code];
              return <span className={clsx('text-[10px] px-1 py-0.5 rounded font-bold leading-none', m.cls)} title={shift.pending_detail || m.tip}>{m.label}</span>;
            })()}
            {features.publish_shifts && !shift.is_published && !isDelivered && (
              <span className="text-[10px] bg-slate-100 text-slate-600 px-1 py-0.5 rounded font-bold leading-none">DRAFT</span>
            )}
            {vehicleLabel && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none bg-sky-100 text-sky-700 max-w-[110px]" title={`Vehicle: ${vehicleLabel}${shift.vehicle_registration ? ` (${shift.vehicle_registration})` : ''}`}>
                <Car size={9} className="shrink-0" /><span className="truncate">{vehicleLabel}</span>
              </span>
            )}
            {shift.shift_address && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none bg-violet-100 text-violet-700 max-w-[120px]" title={`Starts at ${shift.shift_address}. Workers clock in here.`}>
                <MapPin size={9} className="shrink-0" /><span className="truncate">Custom location</span>
              </span>
            )}
            {shift.shift_end_address && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none bg-violet-100 text-violet-700 max-w-[120px]" title={`Ends at ${shift.shift_end_address}. Workers clock out here.`}>
                <Flag size={9} className="shrink-0" /><span className="truncate">Ends elsewhere</span>
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── DayCell ─────────────────────────────────────────────────────────────────

function DayCell({ id, shifts = [], date, compact, expand, onAdd, onShiftClick, onContextMenu, showStaff, onLeave, bulkMode, selectedIds, onToggleSelect }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  const isCurrentDay = isToday(date);

  return (
    <div
      ref={setNodeRef}
      onClick={() => onAdd?.(date)}
      className={clsx(
        'border-r border-b border-slate-200 transition-all relative group cursor-pointer',
        compact ? 'min-h-[60px] px-0.5 py-1' : expand ? 'min-h-[120px] px-1.5 py-2' : 'min-h-[88px] px-1.5 py-2',
        isOver && 'bg-primary-50 ring-2 ring-inset ring-primary-400',
        onLeave && !isOver && 'bg-amber-50/40',
        isCurrentDay && !onLeave && !isOver && 'bg-blue-50/25',
        !isOver && !isCurrentDay && !onLeave && isPublicHoliday(date) && 'bg-amber-50/70',
        !isOver && !isCurrentDay && !onLeave && !isPublicHoliday(date) && isWeekendDay(date) && 'bg-slate-100/50',
        !isOver && !isCurrentDay && !onLeave && !isPublicHoliday(date) && !isWeekendDay(date) && 'hover:bg-slate-50/70',
      )}
    >
      {/* Leave indicator — colored top stripe */}
      {onLeave && <div className="absolute inset-x-0 top-0 h-[3px] bg-amber-400/60 rounded-t" />}
      {onLeave && !compact && (
        <div className="absolute top-2 right-1.5 z-10 pointer-events-none">
          <span className="text-[8px] font-bold text-amber-600 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full leading-none tracking-wide">LEAVE</span>
        </div>
      )}

      {shifts.map(s => (
        <ShiftCard
          key={s.id}
          shift={s}
          compact={compact}
          showStaff={showStaff}
          onClick={onShiftClick}
          onContextMenu={onContextMenu}
          bulkMode={bulkMode}
          isSelected={selectedIds?.has(s.id)}
          onToggleSelect={onToggleSelect}
        />
      ))}

      {/* Add button — visible on hover */}
      <div className="absolute bottom-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button
          onClick={(e) => { e.stopPropagation(); onAdd?.(date); }}
          aria-label={`Add shift on ${format(date, 'EEE d MMM')}`}
          className="w-5 h-5 bg-primary-500 hover:bg-primary-600 rounded-full flex items-center justify-center shadow-sm transition-colors"
        >
          <Plus size={10} className="text-white" />
        </button>
      </div>
    </div>
  );
}

// ─── ContextMenu ─────────────────────────────────────────────────────────────

function ContextMenu({ x, y, shift, onClose, onEdit, onDelete, onDuplicate, onUncancel }) {
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const isCancelled = shift?.status === 'cancelled' || shift?.status === 'cancelled_with_charge';
  const items = [
    { icon: Eye, label: 'Edit Shift', action: onEdit },
    { icon: Copy, label: 'Duplicate', action: onDuplicate },
    isCancelled
      ? { icon: RotateCcw, label: 'Uncancel Shift', action: onUncancel }
      : { icon: Trash2, label: 'Cancel Shift', action: onDelete, danger: true },
  ];

  return (
    <div ref={ref}
      className="fixed z-[100] bg-white rounded-xl shadow-xl border border-slate-100 py-1 w-44"
      style={{ left: x, top: y }}>
      {items.map(({ icon: Icon, label, action, danger }) => (
        <button key={label} onClick={() => { action(); onClose(); }}
          className={clsx('w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 transition-colors', danger ? 'text-red-500 hover:bg-red-50' : 'text-slate-700')}>
          <Icon size={13} /> {label}
        </button>
      ))}
    </div>
  );
}

// ─── ShiftModal ───────────────────────────────────────────────────────────────

// Shift drawer building blocks. Each section carries one soft colour for what it holds
// (the client, the service, the time, the worker…), so the long form is easy to find your way in.
const SECTION_ACCENTS = {
  indigo:  'bg-indigo-50 text-indigo-600 ring-indigo-100',
  violet:  'bg-violet-50 text-violet-600 ring-violet-100',
  amber:   'bg-amber-50 text-amber-600 ring-amber-100',
  sky:     'bg-sky-50 text-sky-600 ring-sky-100',
  rose:    'bg-rose-50 text-rose-600 ring-rose-100',
  emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
  orange:  'bg-orange-50 text-orange-600 ring-orange-100',
  blue:    'bg-blue-50 text-blue-600 ring-blue-100',
  teal:    'bg-teal-50 text-teal-600 ring-teal-100',
  slate:   'bg-slate-100 text-slate-600 ring-slate-200',
};
function SectionCard({ id, icon: Icon, accent = 'indigo', title, subtitle, extra, children, allowOverflow }) {
  const tint = SECTION_ACCENTS[accent] || SECTION_ACCENTS.indigo;
  return (
    <section id={id} aria-label={title}
      className={`scroll-mt-3 bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${allowOverflow ? '' : 'overflow-hidden'}`}>
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-8 h-8 rounded-lg flex items-center justify-center ring-1 ring-inset shrink-0 ${tint}`}>
            {Icon && <Icon size={16} aria-hidden="true" />}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900 leading-tight">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500 truncate mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {extra}
      </div>
      <div className="px-4 sm:px-5 py-4 space-y-4">{children}</div>
    </section>
  );
}

// Label beside the field on a wide drawer, above it on a phone.
function FormRow({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-4">
      <span className={clsx('text-[13px] font-medium text-slate-600 sm:w-32 shrink-0 sm:pt-2.5 leading-tight', !label && 'max-sm:hidden')}>{label}</span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <button type="button" role="switch" aria-checked={!!value} aria-label={label} onClick={() => onChange(!value)}
      className={`w-10 h-6 rounded-full transition-colors relative shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 ${value ? 'bg-primary-600' : 'bg-slate-300'}`}>
      <span className={`absolute top-1 left-0 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}

// A yes/no setting as one row: what it does on the left, the switch on the right.
function SwitchRow({ label, hint, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm text-slate-800">{label}</p>
        {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
      </div>
      <Toggle value={value} onChange={onChange} label={label} />
    </div>
  );
}

// A fact in the drawer's header (date, time, worker, service).
function HeaderChip({ icon: Icon, dot, children, title }) {
  return (
    <span title={title} className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-white/10 ring-1 ring-inset ring-white/15 text-[12px] font-medium text-white max-w-full">
      {Icon && <Icon size={13} className="text-indigo-200 shrink-0" aria-hidden="true" />}
      {dot && <span className="w-2 h-2 rounded-full shrink-0 ring-1 ring-white/40" style={{ backgroundColor: dot }} aria-hidden="true" />}
      <span className="truncate">{children}</span>
    </span>
  );
}

const DRAWER_SECTIONS = [
  { id: 'sd-client', label: 'Client' },
  { id: 'sd-shift', label: 'Service' },
  { id: 'sd-when', label: 'When & where' },
  { id: 'sd-carer', label: 'Worker' },
  { id: 'sd-more', label: 'More' },
];
const AI_BTN = 'inline-flex items-center gap-1.5 h-8 px-3 border border-slate-300 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 transition-colors disabled:opacity-60';

function ModalSelect({ value, onChange, placeholder, options }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full appearance-none border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 pr-10"
      >
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  );
}

function ClientSearch({ clients, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const selected = clients.find(c => c.id === value);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = clients.filter(c =>
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 8);

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white"
          placeholder="Type to search clients by name."
          value={selected ? `${selected.first_name} ${selected.last_name}` : query}
          onFocus={() => { setOpen(true); if (selected) setQuery(''); }}
          onChange={e => { setQuery(e.target.value); onChange(''); setOpen(true); }}
        />
        {selected && (
          <button onClick={() => { onChange(''); setQuery(''); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X size={13} />
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {filtered.map(c => (
            <button key={c.id}
              onClick={() => { onChange(c.id); setQuery(''); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                {(c.first_name || '?')[0]}{(c.last_name || '?')[0]}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">{c.first_name} {c.last_name}</p>
                {c.ndis_number && <p className="text-[10px] text-slate-400 font-mono">NDIS: {c.ndis_number}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CarerSearch({ staff, value, onChange, preferredIds = new Set(), excludedIds = new Set() }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const selected = staff.find(s => s.id === value);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Rank preferred (⭐) first, excluded (🚫) last, so the right worker surfaces on top.
  const rank = s => excludedIds.has(s.id) ? 2 : preferredIds.has(s.id) ? 0 : 1;
  const filtered = staff.filter(s =>
    `${s.first_name} ${s.last_name}`.toLowerCase().includes(query.toLowerCase())
  ).sort((a, b) => rank(a) - rank(b)).slice(0, 8);

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="w-full pl-9 pr-10 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white"
          placeholder="Select"
          value={selected ? `${selected.first_name} ${selected.last_name}` : query}
          onFocus={() => { setOpen(true); if (selected) setQuery(''); }}
          onChange={e => { setQuery(e.target.value); onChange(''); setOpen(true); }}
        />
        {selected && (
          <button onClick={() => { onChange(''); setQuery(''); }}
            className="absolute right-9 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X size={13} />
          </button>
        )}
        <ChevronDown size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {filtered.map(s => {
            const isExcluded = excludedIds.has(s.id);
            const isPreferred = preferredIds.has(s.id);
            return (
              <button key={s.id}
                onClick={() => { onChange(s.id); setQuery(''); setOpen(false); }}
                className={clsx('w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors',
                  isExcluded ? 'hover:bg-rose-50' : 'hover:bg-slate-50')}>
                <div className={clsx('w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0',
                  isExcluded ? 'bg-gradient-to-br from-rose-300 to-rose-500' : 'bg-gradient-to-br from-blue-400 to-indigo-600')}>
                  {(s.first_name || '?')[0]}{(s.last_name || '?')[0]}
                </div>
                <p className={clsx('text-sm font-semibold flex-1', isExcluded ? 'text-rose-700' : 'text-slate-800')}>
                  {s.first_name} {s.last_name}
                </p>
                {isPreferred && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full"><Star size={9} className="fill-amber-400 text-amber-400" />Preferred</span>}
                {isExcluded && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full"><Ban size={9} />Excluded</span>}
                {!isExcluded && Number(s.compliance_gaps) > 0 && <span className="text-[10px] font-semibold text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full" title="Missing/expired required qualifications">Non-compliant</span>}
                {!isExcluded && s.on_hold && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">On hold</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RichTextEditor({ value, onChange }) {
  const editorRef = useRef();

  // Sync external value into the editor ONLY when it isn't focused — binding
  // dangerouslySetInnerHTML to `value` re-set innerHTML on every keystroke, which
  // reset the caret to the start and scrambled typed text.
  useEffect(() => {
    const el = editorRef.current;
    if (el && document.activeElement !== el && el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const tools = [
    { icon: <Bold size={13} />,          cmd: 'bold',          title: 'Bold' },
    { icon: <Italic size={13} />,        cmd: 'italic',        title: 'Italic' },
    { icon: <Underline size={13} />,     cmd: 'underline',     title: 'Underline' },
    { icon: <Strikethrough size={13} />, cmd: 'strikeThrough', title: 'Strikethrough' },
    { icon: <Code size={13} />,          cmd: 'formatBlock',   val: 'pre', title: 'Code' },
    { sep: true },
    { icon: <List size={13} />,          cmd: 'insertUnorderedList', title: 'Bullet list' },
    { sep: true },
    { icon: <AlignLeft size={13} />,     cmd: 'justifyLeft',   title: 'Align left' },
    { icon: <AlignCenter size={13} />,   cmd: 'justifyCenter', title: 'Align center' },
    { icon: <AlignRight size={13} />,    cmd: 'justifyRight',  title: 'Align right' },
  ];

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-slate-50 border-b border-slate-200 flex-wrap">
        <select
          className="text-xs border-0 bg-transparent text-slate-600 pr-1 focus:outline-none cursor-pointer"
          onChange={e => exec('formatBlock', e.target.value)}
          defaultValue="p"
        >
          <option value="p">Normal text</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>
        <div className="w-px h-4 bg-slate-200 mx-1" />
        {tools.map((t, i) =>
          t.sep ? (
            <div key={i} className="w-px h-4 bg-slate-200 mx-1" />
          ) : (
            <button key={i} type="button" title={t.title}
              onMouseDown={(e) => { e.preventDefault(); exec(t.cmd, t.val); }}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-200 text-slate-600 transition-colors">
              {t.icon}
            </button>
          )
        )}
      </div>
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={e => onChange(e.currentTarget.innerHTML)}
        className="min-h-[100px] px-3 py-2.5 text-sm text-slate-700 focus:outline-none"
        style={{ lineHeight: 1.6 }}
      />
    </div>
  );
}

// Support at Home counts notice in business days: the days after today up to and including the visit,
// skipping weekends. Public holidays are also skipped on the server, which has the final say.
function businessDaysUntil(ymd) {
  const target = String(ymd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(target)) return 0;
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());
  if (target <= today) return 0;
  const d = new Date(`${today}T00:00:00Z`);
  let n = 0;
  for (let i = 0; i < 400; i++) {
    d.setUTCDate(d.getUTCDate() + 1);
    const day = d.getUTCDay();
    if (day !== 0 && day !== 6) n++;
    if (d.toISOString().slice(0, 10) >= target) break;
  }
  return n;
}

function CancelShiftModal({ shift, onClose, onDone }) {
  const [policies, setPolicies] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [policyId, setPolicyId] = useState('');
  const [reasonIds, setReasonIds] = useState([]);
  const [comments, setComments] = useState('');
  const [scope, setScope] = useState('single');
  const [noShow, setNoShow] = useState(false);
  const [busy, setBusy] = useState(false);
  // Support at Home's two-business-day rule. A visit whose aged care participants are all CHSP that
  // day follows the provider's own policy instead, applied to the participant's contribution.
  const isChsp = !!shift?.chsp_only;
  const isAgedCare = !!shift?.has_aged_care && !isChsp;
  const noticeDays = isAgedCare ? businessDaysUntil(shift?.shift_date) : null;
  const sahShortNotice = isAgedCare && (noShow || noticeDays < 2);
  useEffect(() => {
    api.get('/cancellations/policies?active=true').then(r => { setPolicies(r.data || []); const d = (r.data || []).find(p => p.is_default); if (d) setPolicyId(d.id); }).catch(() => {});
    api.get('/cancellations/reasons?active=true').then(r => setReasons(r.data || [])).catch(() => {});
  }, []);
  const policy = policies.find(p => p.id === policyId);
  const baseCost = parseFloat(shift?.total_client_charge) || 0;
  const previewCharge = policy ? (baseCost * (Number(policy.charge_percentage) || 0) / 100) : 0;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shifts/${shift.id}/cancel`, { policyId: policyId || null, reasonIds, comments, scope, noShow });
      toast.success(data.totalCharge ? `${noShow ? 'No-show recorded' : 'Cancelled'}: $${data.totalCharge.toFixed(2)} charged` : `${noShow ? 'No-show recorded' : 'Cancelled'} (no charge)`);
      if (data.sah?.notChargeable) {
        toast(`Support at Home: ${data.sah.noticeDays} business days' notice was given, so this visit can't be charged.`, { icon: 'ℹ️', duration: 6000 });
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to cancel'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Cancel Shift</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Cancellation policy</label>
            <select className="input-field" value={policyId} onChange={e => setPolicyId(e.target.value)}>
              <option value="">No charge</option>
              {policies.map(p => <option key={p.id} value={p.id}>{p.name} ({p.charge_percentage}%)</option>)}
            </select>
            <p className="text-xs mt-1.5 text-slate-500">
              {policy ? (<>
                Bills client: <b className="text-slate-700">${(isAgedCare && !sahShortNotice ? 0 : Math.min(previewCharge, baseCost || previewCharge)).toFixed(2)}</b> ({policy.charge_percentage}% of ${baseCost.toFixed(2)}).{' '}
                Worker: <b className="text-slate-700">{workerPayLabel(policy)}</b>.
                {!baseCost && <span className="text-amber-600"> Charge calculated on cancel.</span>}
              </>) : 'No charge to the client, and the worker is not paid.'}
            </p>
            {isAgedCare && (
              <p className={`text-xs mt-2 rounded-lg px-3 py-2 ${sahShortNotice ? 'bg-amber-50 text-amber-800' : 'bg-teal-50 text-teal-800'}`}>
                {noShow
                  ? 'Support at Home: a no-show can be charged in full. Choose "No charge" to waive it.'
                  : sahShortNotice
                    ? `Support at Home: ${noticeDays} business day${noticeDays === 1 ? '' : 's'}' notice, which is short notice. The visit can be charged in full. Choose "No charge" to waive it, for example for a hospital admission.`
                    : `Support at Home: ${noticeDays} business days' notice. Two or more days' notice can't be charged, so no charge will apply.`}
                {' '}It is listed on the participant's monthly statement.
              </p>
            )}
            {isChsp && (
              <p className="text-xs mt-2 rounded-lg px-3 py-2 bg-teal-50 text-teal-800">
                CHSP: your cancellation policy applies to the participant's contribution for the visit. A cancelled visit is not reported to DEX.
              </p>
            )}
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={noShow} onChange={e => setNoShow(e.target.checked)} />
            <span>The participant wasn't there when the worker arrived (no-show)</span>
          </label>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Reason(s)</label>
            <div className="flex flex-wrap gap-1.5">
              {reasons.map(r => (
                <button key={r.id} type="button" onClick={() => setReasonIds(ids => ids.includes(r.id) ? ids.filter(x => x !== r.id) : [...ids, r.id])}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${reasonIds.includes(r.id) ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>{r.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Comments</label>
            <textarea className="input-field" rows={2} value={comments} onChange={e => setComments(e.target.value)} />
          </div>
          {shift?.recurring_group_id && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Apply to</label>
              <div className="flex gap-1.5">
                {[['single', 'This shift'], ['future', 'This + future'], ['all', 'All in series']].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setScope(v)} className={`px-3 py-1.5 rounded-lg text-sm border ${scope === v ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-200 text-slate-500'}`}>{l}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="btn-secondary btn-sm">Keep shift</button>
          <button onClick={submit} disabled={busy} className="btn-sm px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-500 disabled:opacity-50">{busy ? 'Cancelling…' : 'Cancel shift'}</button>
        </div>
      </div>
    </div>
  );
}

// Bulk cancel WITH a cancellation policy — applies the chosen policy/reasons to every
// selected shift (reuses the per-shift POST /shifts/:id/cancel so charges/snapshots are identical).
function BulkCancelPolicyModal({ shiftIds, onClose, onDone }) {
  const [policies, setPolicies] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [policyId, setPolicyId] = useState('');
  const [reasonIds, setReasonIds] = useState([]);
  const [comments, setComments] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/cancellations/policies?active=true').then(r => { setPolicies(r.data || []); const d = (r.data || []).find(p => p.is_default); if (d) setPolicyId(d.id); }).catch(() => {});
    api.get('/cancellations/reasons?active=true').then(r => setReasons(r.data || [])).catch(() => {});
  }, []);
  const policy = policies.find(p => p.id === policyId);
  const submit = async () => {
    setBusy(true);
    let ok = 0, charged = 0;
    for (const id of shiftIds) {
      try { const { data } = await api.post(`/shifts/${id}/cancel`, { policyId: policyId || null, reasonIds, comments, scope: 'single' }); ok++; charged += data.totalCharge || 0; }
      catch { /* skip individual failures */ }
    }
    setBusy(false);
    toast.success(`Cancelled ${ok} shift${ok === 1 ? '' : 's'}${charged ? ` — $${charged.toFixed(2)} charged` : ''}`);
    onDone();
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Cancel {shiftIds.length} shift{shiftIds.length === 1 ? '' : 's'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Cancellation policy</label>
            <select className="input-field" value={policyId} onChange={e => setPolicyId(e.target.value)}>
              <option value="">No charge</option>
              {policies.map(p => <option key={p.id} value={p.id}>{p.name} ({p.charge_percentage}%)</option>)}
            </select>
            <p className="text-xs mt-1.5 text-slate-500">
              {policy
                ? <>Bills each client {policy.charge_percentage}% of their shift cost. Worker: <b className="text-slate-700">{workerPayLabel(policy)}</b>.</>
                : 'No charge to the client, and the worker is not paid.'}
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Reason(s)</label>
            <div className="flex flex-wrap gap-1.5">
              {reasons.map(r => (
                <button key={r.id} type="button" onClick={() => setReasonIds(ids => ids.includes(r.id) ? ids.filter(x => x !== r.id) : [...ids, r.id])}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${reasonIds.includes(r.id) ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>{r.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Comments</label>
            <textarea className="input-field" rows={2} value={comments} onChange={e => setComments(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="btn-secondary btn-sm">Keep shifts</button>
          <button onClick={submit} disabled={busy} className="btn-sm px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-500 disabled:opacity-50">{busy ? 'Cancelling…' : `Cancel ${shiftIds.length}`}</button>
        </div>
      </div>
    </div>
  );
}

// Release one held aged care visit, with a reason: care has to start before the agreement or care
// plan is recorded, or an overspend is approved. Recorded on the visit's history.
function ReleaseVisit({ shiftId, code, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const release = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/aged-care/bookings/${shiftId}/override`, { codes: [code], reason });
      if (data.released) { toast.success('Released for this visit. The reason is in its history.'); onDone?.(); }
      else toast(data.detail || 'Released, but something else still holds it', { icon: '⚠️' });
    } catch (e) { toast.error(e.response?.data?.message || 'Could not release it'); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2">
      <label className="text-[11px] text-slate-600 flex-1 min-w-[12rem]">Release it for this visit, because
        <input className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1 text-xs" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={code === 'insufficient_budget' ? 'Overspend approved by …' : 'Urgent start: agreement being signed on …'} />
      </label>
      <button type="button" disabled={busy || reason.trim().length < 5} onClick={release} className="rounded-lg bg-slate-900 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50">Release</button>
    </div>
  );
}

function ShiftModal({ shift, defaultDate, defaultStaffId, defaultClientId, defaultJobBoard, clients, staff, facilities, onClose, onSave }) {
  const confirm = useConfirm();
  const isEdit = !!shift?.id;
  const [saving, setSaving] = useState(false);
  const savingRef = React.useRef(false);
  const requestCloseRef = React.useRef(null); // set below, once the form exists
  const shiftTypes = useShiftTypes();
  const features = useFeatures();
  // Paid from Pay Rates (Settings > Pay Rates): the shift can pay travel between shifts.
  const ratesPay = !!features && features.pay_engine === 'rates';
  const [priceBooks, setPriceBooks] = useState([]);

  const [companyVehicles, setCompanyVehicles] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get('/templates').then(r => setTemplates(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/price-books')
      .then(r => setPriceBooks(Array.isArray(r.data) ? r.data : r.data?.value || []))
      .catch(() => {});
    api.get('/vehicles')
      .then(r => setCompanyVehicles((r.data || []).filter(v => v.status !== 'retired')))
      .catch(() => {});
  }, []);

  // Close on Escape (never mid-save, and not while a confirm is open — it handles its own Escape).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || savingRef.current) return;
      if (document.querySelector('[role="alertdialog"]')) return;
      requestCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const defaultDateStr = defaultDate ? format(defaultDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

  const [form, setForm] = useState({
    // Template
    templateId:            '',
    // Facility
    facilityId:            shift?.facility_id || '',
    // Client
    clientIds:             shift?.client_ids?.split(',')[0]?.trim() || shift?.client_id || defaultClientId || '',
    priceBookId:           shift?.price_book_id || '',
    fundId:                shift?.fund_id || '',
    // Shift
    shiftType:             shift?.shift_type || '',
    additionalShiftTypes:  Array.isArray(shift?.additional_shift_types) ? shift.additional_shift_types : [],
    allowance:             '',
    activity:              shift?.activity || '',
    rosterComments:        shift?.roster_comments || '',
    overrideQual:          shift?.override_qual || false,
    overrideQualReason:    shift?.override_qual_reason || '',
    irregularSil:          shift?.irregular_sil || false,
    // Aged care: which account the visit is claimed from, when restorative care runs alongside
    // ongoing services. Empty = decided automatically (clinical to the pathway, the rest ongoing).
    agedCareFundingSource: shift?.aged_care_funding_source || '',
    // Time & Location
    shiftDate:             shift?.shift_date?.split('T')[0] || defaultDateStr,
    nextDay:               false,
    startTime:             shift?.start_time?.slice(0, 5) || '09:00',
    endTime:               shift?.end_time?.slice(0, 5) || '10:00',
    repeat:                false,
    recurringDays:         [],   // weekdays for weekly/fortnightly recurrence (0=Sun..6=Sat); empty = same weekday
    address:               shift?.shift_address || shift?.address || '',
    // Shift Address (SC workflow spec 9): coordinates come from the address picker; when typed
    // by hand they are cleared and the server geocodes the address on save.
    shiftAddressLat:       shift?.shift_address ? (shift?.shift_address_lat ?? null) : null,
    shiftAddressLng:       shift?.shift_address ? (shift?.shift_address_lng ?? null) : null,
    // Shift End Address: where the shift finishes when that is somewhere else. Empty = it ends
    // where it starts. Workers clock out here.
    endAddress:            shift?.shift_end_address || '',
    endAddressLat:         shift?.shift_end_address_lat ?? null,
    endAddressLng:         shift?.shift_end_address_lng ?? null,
    // Carer
    staffId:               shift?.staff_id || defaultStaffId || '',
    addToJobBoard:         shift?.is_on_job_board || defaultJobBoard || false,
    notifyCarer:           true,
    // Instructions
    instructions:          shift?.instructions || '',
    // Mileage
    mileageCap:            0,
    mileage:               0,
    companyVehicle:        false,
    vehicleId:             shift?.vehicle_id || '',
    // Pay Rates (spec 6.5): pay the worker's map km from their previous shift that day to this one.
    payTravel:             !!shift?.pay_travel,
    // Travel & Transport (Build 3a)
    chargeTravel:          shift?.charge_travel || 'none',
    travelHours:           shift?.travel_hours ?? '',
    travelKm:              shift?.travel_km ?? '',
    travelTimeRate:        shift?.travel_time_rate ?? '',
    travelKmRate:          shift?.travel_km_rate ?? '',
    transportKm:           shift?.transport_km ?? '',
    transportKmRate:       shift?.transport_km_rate ?? '',
    transportFundId:       shift?.transport_fund_id || '',
    transportOutOfPocket:  shift?.transport_out_of_pocket || false,
    // Signatures
    requireClientSignature: false,
    requireCarerSignature:  false,
    // Meta
    status:                shift?.status || 'booked',
    isPublished:           shift?.is_published ?? false,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Unsaved changes: the form compared with how it opened. The price book is left out when the
  // shift opened without one, because the form fills that in by itself.
  const initialFormRef = React.useRef(form);
  const isDirty = (() => {
    const a = { ...form };
    const b = { ...initialFormRef.current };
    if (!b.priceBookId) { delete a.priceBookId; delete b.priceBookId; }
    return JSON.stringify(a) !== JSON.stringify(b);
  })();
  const requestClose = async () => {
    if (savingRef.current) return;
    if (isDirty && !(await confirm('Your changes to this shift haven\'t been saved.', {
      title: 'Discard changes to this shift?', danger: true, confirmLabel: 'Discard changes', cancelLabel: 'Keep editing',
    }))) return;
    onClose();
  };
  requestCloseRef.current = requestClose;

  // calcHours floors a negative gap at 0, so an overnight shift (10pm–7am, finishing the next day)
  // came out as 0 + 24 = 24 hours. Take the signed gap and add the day only when it finishes the
  // next day: 22:00 → 07:00 next day is -15 + 24 = 9 hours.
  const hours = (() => {
    if (!form.startTime || !form.endTime) return 0;
    const [sh, sm] = form.startTime.split(':').map(Number);
    const [eh, em] = form.endTime.split(':').map(Number);
    const gap = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
    return form.nextDay ? gap + 24 : Math.max(0, gap);
  })();
  const selectedClient = clients.find(c => c.id === form.clientIds);
  // Aged care funds nothing like NDIS: no per-service funds to pick from, one quarterly budget, and
  // the service decides the contribution category. Without this the form declared "No funded
  // services" over a participant holding a live Support at Home plan, and offered the NDIS price
  // book, which does not price aged care at all.
  const isAgedCareClient = selectedClient?.service_type === 'aged_care';
  const [agedCare, setAgedCare] = useState(null);
  useEffect(() => {
    if (!isAgedCareClient || !form.clientIds) { setAgedCare(null); return undefined; }
    let live = true;
    api.get(`/aged-care/booking-context/${form.clientIds}`, { params: form.shiftDate ? { date: form.shiftDate } : undefined })
      .then(r => { if (live && r.data?.isAgedCare) setAgedCare(r.data); })
      .catch(() => { if (live) setAgedCare(null); });
    return () => { live = false; };
  }, [isAgedCareClient, form.clientIds, form.shiftDate]);
  const isChspBooking = agedCare?.program === 'chsp';

  // When a client is picked but has no price book on their profile, default the Price book
  // field to the newest active price book instead of leaving it on "Select". Only fills an
  // empty field, so an existing shift's book (or a user's manual pick) is never overwritten.
  useEffect(() => {
    // Never auto-assign an NDIS price book to an aged care participant: it would be stamped on the
    // shift silently even though the row is hidden.
    if (isAgedCareClient) { if (form.priceBookId) set('priceBookId', ''); return; }
    if (form.priceBookId || !form.clientIds || selectedClient?.default_price_book_id) return;
    const active = priceBooks.filter(b => b.is_active !== false);
    if (!active.length) return;
    const latest = [...active].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
    if (latest) set('priceBookId', latest.id);
  }, [priceBooks, selectedClient, form.clientIds, form.priceBookId, isAgedCareClient]);

  const shiftColor = hashColor(form.shiftType);
  // Client's own address — a shift inherits it (mobile/admin fall back to it), so we
  // only show the Address field when the client has none, or the admin overrides it.
  const clientAddress = selectedClient
    ? [selectedClient.address, selectedClient.suburb, selectedClient.state, selectedClient.postcode].filter(Boolean).join(', ')
    : '';
  const [overrideAddress, setOverrideAddress] = useState(!!shift?.shift_address);
  const [endsElsewhere, setEndsElsewhere] = useState(!!shift?.shift_end_address);
  const endAddressPayload = () => {
    const a = endsElsewhere ? String(form.endAddress || '').trim() : '';
    return { shiftEndAddress: a, shiftEndAddressLat: a ? form.endAddressLat : null, shiftEndAddressLng: a ? form.endAddressLng : null };
  };
  // A shift address is "custom" when it differs from the client's own address. Old shifts that
  // simply stored the client's address must not become a custom location when edited.
  const normAddr = (a) => String(a || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const isCustomLocation = !!form.address && (overrideAddress || !clientAddress || normAddr(form.address) !== normAddr(clientAddress));
  // Funds for the picker: show service-line budgets only — hide the plan's "general"
  // pool (a shift bills a service, not the catch-all). Auto-routing still uses general
  // as the system fallback. Fall back to all funds if the client has only a general pool.
  const isGeneralFund = (f) => /\(general\)/i.test(f.fund_name || '') || (f.plan_id && !f.plan_line_id && !f.support_category);
  const _allFunds = selectedClient?.funds || [];
  const _serviceFunds = _allFunds.filter(f => !isGeneralFund(f));
  const fundOptions = _serviceFunds.length > 0 ? _serviceFunds : _allFunds;
  // Budget-less services. If the line draws from a real pool (its fund has money behind it) it's
  // BOOKABLE straight from the shared pool. Only lines with no fund at all are truly unbookable.
  const unfunded = selectedClient?.unfunded_services || [];
  const poolOptions = unfunded.filter(s => s.fund_id);      // budget-less but draws from a pool fund → selectable
  const trulyUnfunded = unfunded.filter(s => !s.fund_id);   // no fund behind it → greyed out
  const hasSelectable = fundOptions.length > 0 || poolOptions.length > 0;
  // Aged care draws on one quarterly Support at Home budget, never on NDIS client_funds. Gate the
  // NDIS branches on this rather than on the funds being absent, so attaching NDIS funds to an aged
  // care participant later can never put an NDIS budget in front of a coordinator booking SAH care.
  const useNdisFunds = hasSelectable && !isAgedCareClient;
  const showNdisUnfunded = trulyUnfunded.length > 0 && !isAgedCareClient;
  // The service <select> uses prefixed keys (f:<fundId> for own-budget funds, p:<lineId> for pool
  // services) so two pool services sharing the general fund stay distinct.
  const currentServiceValue = (() => {
    const ff = fundOptions.find(f => f.id === form.fundId);
    if (ff) return `f:${ff.id}`;
    const pp = poolOptions.find(o => o.fund_id === form.fundId && o.service_name === form.shiftType);
    if (pp) return `p:${pp.line_id}`;
    return '';
  })();
  const handleServiceChange = (v) => {
    if (!v) return setForm(f => ({ ...f, fundId: '', shiftType: '' }));
    if (v.startsWith('f:')) { const x = fundOptions.find(o => `f:${o.id}` === v); if (x) setForm(f => ({ ...f, fundId: x.id, shiftType: x.service_name || x.fund_name })); }
    else if (v.startsWith('p:')) { const x = poolOptions.find(o => `p:${o.line_id}` === v); if (x) setForm(f => ({ ...f, fundId: x.fund_id, shiftType: x.service_name })); }
  };

  const [smartMatches, setSmartMatches] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [aiRecs, setAiRecs]             = useState(null);
  const [loadingAiRecs, setLoadingAiRecs] = useState(false);
  const [smartMatchOpen, setSmartMatchOpen] = useState(false);
  const [smartMatchResults, setSmartMatchResults] = useState([]);
  const [smartMatchLoading, setSmartMatchLoading] = useState(false);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [shiftHistory, setShiftHistory] = useState([]);
  const [historyTab, setHistoryTab] = useState(false);
  const [showMore, setShowMore] = useState(false);
  // Section tabs: which section is in view, and scrolling to one.
  const bodyRef = React.useRef(null);
  const [activeSec, setActiveSec] = useState('sd-client');
  useEffect(() => {
    const root = bodyRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver((entries) => {
      const seen = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (seen[0]) setActiveSec(seen[0].target.id);
    }, { root, rootMargin: '0px 0px -65% 0px', threshold: 0 });
    DRAWER_SECTIONS.forEach(s => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [showMore]);
  const jumpTo = (id) => {
    if (id === 'sd-more') setShowMore(true);
    setActiveSec(id);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
  };
  const [workerPrefs, setWorkerPrefs] = useState({ preferred: new Set(), excluded: new Set() });

  // Conflict check: runs when staff+date+times change
  useEffect(() => {
    if (!form.staffId || !form.shiftDate || !form.startTime || !form.endTime) {
      setConflictWarning(null);
      return;
    }
    const params = new URLSearchParams({ staff_id: form.staffId, shift_date: form.shiftDate, start_time: form.startTime, end_time: form.endTime });
    if (shift?.id) params.set('exclude_id', shift.id);
    api.get(`/shifts/conflict-check?${params}`)
      .then(r => setConflictWarning(r.data.hasConflict ? r.data.conflicts : null))
      .catch(() => {});
  }, [form.staffId, form.shiftDate, form.startTime, form.endTime, shift?.id]);

  // Load the selected client's preferred (⭐) + excluded (🚫) workers so the carer
  // picker can flag them. Excluded workers are blocked server-side on save.
  useEffect(() => {
    if (!form.clientIds) { setWorkerPrefs({ preferred: new Set(), excluded: new Set() }); return; }
    api.get(`/clients/${form.clientIds}/worker-preferences`)
      .then(r => setWorkerPrefs({
        preferred: new Set((r.data?.preferred || []).map(p => p.id)),
        excluded:  new Set((r.data?.excluded  || []).map(p => p.id)),
      }))
      .catch(() => setWorkerPrefs({ preferred: new Set(), excluded: new Set() }));
  }, [form.clientIds]);
  const selectedStaffExcluded = form.staffId && workerPrefs.excluded.has(form.staffId);

  // Estimated cost: hours × price book rate (use live form.shiftType, not the stale saved shift type)
  useEffect(() => {
    if (!form.priceBookId || hours <= 0) { setEstimatedCost(0); return; }
    api.get(`/price-books/${form.priceBookId}`)
      .then(r => {
        const items = r.data?.items || [];
        const typeKey = (form.shiftType || '').toLowerCase();
        const match = typeKey
          ? items.find(i => i.support_category?.toLowerCase().includes(typeKey))
          : null;
        const rate = match?.unit_price || items[0]?.unit_price || 0;
        setEstimatedCost(hours * Number(rate));
      })
      .catch(() => {});
  }, [form.priceBookId, hours, form.shiftType]);

  // Load shift history when editing
  useEffect(() => {
    if (!shift?.id) return;
    api.get(`/shifts/${shift.id}/history`).then(r => setShiftHistory(r.data || [])).catch(() => {});
  }, [shift?.id]);

  const runSmartMatchPanel = async () => {
    if (!form.clientIds) return toast.error('Select a client first');
    if (!form.shiftDate) return toast.error('Set a shift date first');
    setSmartMatchOpen(true);
    setSmartMatchLoading(true);
    setSmartMatchResults([]);
    try {
      // Reuse the working /ai/shift-recommendations route — its response shape
      // ({ recommendations: [{ staffId, staffName, rank, reason, confidence }] })
      // already matches what the AI Staff Match panel renders below.
      const { data } = await api.post('/ai/shift-recommendations', {
        clientId:  form.clientIds,
        shiftDate: form.shiftDate,
        startTime: form.startTime,
        endTime:   form.endTime,
        shiftType: form.shiftType,
      });
      setSmartMatchResults(data?.recommendations || []);
    } catch { toast.error('AI Match failed'); setSmartMatchOpen(false); }
    finally { setSmartMatchLoading(false); }
  };

  const runSmartMatch = async () => {
    if (!form.clientIds) return toast.error('Select a client first');
    setLoadingMatch(true);
    try {
      const { data } = await api.get(`/smart-match/matched-staff/${form.clientIds}`);
      setSmartMatches(data);
    } catch { toast.error('Smart match failed'); }
    finally { setLoadingMatch(false); }
  };

  const runAiRecommend = async () => {
    if (!form.clientIds) return toast.error('Select a client first');
    if (!form.shiftDate) return toast.error('Set a shift date first');
    setLoadingAiRecs(true);
    setAiRecs(null);
    try {
      const { data } = await api.post('/ai/shift-recommendations', {
        clientId:  form.clientIds,
        shiftDate: form.shiftDate,
        startTime: form.startTime,
        endTime:   form.endTime,
        shiftType: form.shiftType,
      });
      setAiRecs(data);
    } catch { toast.error('AI recommendation failed'); }
    finally { setLoadingAiRecs(false); }
  };

  // Reset smart matches when client changes
  useEffect(() => { setSmartMatches(null); }, [form.clientIds]);

  // NOTE: we intentionally do NOT auto-pick a fund. Leaving it on "Auto — match the
  // service budget (recommended)" lets the backend route the shift to the service-line
  // budget that matches its service type (and fall back to the general pool only if
  // nothing matches), instead of pre-selecting an arbitrary/general fund.

  // Suggested carers (show all staff, sorted by last shift count)
  const suggested = staff.slice(0, 6);

  const handleSave = async (publish = false) => {
    if (savingRef.current) return;
    if (!form.shiftDate) return toast.error('Date is required');
    if (form.repeat && !form.recurringEndDate) return toast.error('End date is required for recurring shifts');
    // Hard block double-booking: the worker already has a shift overlapping this time slot.
    if (Array.isArray(conflictWarning) && conflictWarning.length) {
      const c = conflictWarning[0];
      const when = c?.start_time && c?.end_time ? ` (${fmtTime(c.start_time)}–${fmtTime(c.end_time)})` : '';
      return toast.error(`This worker is already booked in this time slot${when}. Change the time or assign a different worker.`);
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const numOrNull = v => v === '' || v == null ? null : Number(v);
      // Travel/transport fields — numeric cols reject '' so coerce to null.
      const travelFields = {
        chargeTravel: form.chargeTravel || 'none',
        travelHours: numOrNull(form.travelHours), travelKm: numOrNull(form.travelKm),
        travelTimeRate: numOrNull(form.travelTimeRate), travelKmRate: numOrNull(form.travelKmRate),
        transportKm: numOrNull(form.transportKm), transportKmRate: numOrNull(form.transportKmRate),
        transportFundId: form.transportFundId || null, transportOutOfPocket: !!form.transportOutOfPocket,
      };
      const hasTravel = travelFields.chargeTravel !== 'none' || travelFields.transportKm;
      const payload = {
        ...form,
        ...travelFields,
        // Always send clientIds as array — form stores a single string from ClientSearch
        clientIds: form.clientIds ? [form.clientIds] : [],
        fundIds:   form.fundId    ? [form.fundId]    : [],
        // When the draft/publish workflow isn't enabled for the org, every shift is
        // published immediately — otherwise it would be stuck showing DRAFT with no
        // publish control available.
        isPublished: features.publish_shifts ? (publish || form.isPublished) : true,
        isOnJobBoard: form.addToJobBoard,
        // Wire the "Notify carer" checkbox to the backend's notifyStaff flag (was unwired, so the
        // checkbox did nothing and notifications always fired). Now it defaults on and is respected.
        notifyStaff: form.notifyCarer,
        isRecurring: !!form.repeat,
        recurringPattern: form.repeat ? (form.recurringPattern || 'weekly') : null,
        recurringEndDate: form.repeat ? form.recurringEndDate : null,
        recurringDays: form.repeat && ['weekly', 'fortnightly'].includes(form.recurringPattern || 'weekly') ? (form.recurringDays || []) : null,
        shiftAddress: isCustomLocation ? form.address : '',
        shiftAddressLat: isCustomLocation ? form.shiftAddressLat : null,
        shiftAddressLng: isCustomLocation ? form.shiftAddressLng : null,
        ...endAddressPayload(),
      };
      // Pay travel is a Pay Rates setting: a company on pay groups never sends it.
      if (ratesPay) payload.payTravel = !!form.payTravel; else delete payload.payTravel;
      if (isEdit) {
        const { data: updated } = await withComplianceOverride(confirm, (extra) => api.put(`/shifts/${shift.id}`, { ...payload, ...extra }));
        toast.success(publish ? 'Shift updated & published' : 'Shift updated');
        // Warn if the edit left the shift unable to go live (funding/clash diagnostic).
        if (updated?.pending_reason && REASON_META[updated.pending_reason]) {
          toast(updated.pending_detail || REASON_META[updated.pending_reason].tip, { icon: '⚠️', duration: 6000, style: { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' } });
        }
      } else {
        const { data: created } = await withComplianceOverride(confirm, (extra) => api.post('/shifts', { ...payload, ...extra }));
        // POST insert doesn't persist travel/transport — follow up with a PUT when set.
        if (hasTravel && created?.id) await api.put(`/shifts/${created.id}`, travelFields).catch(() => {});
        if (form.agedCareFundingSource && created?.id) await api.put(`/shifts/${created.id}`, { agedCareFundingSource: form.agedCareFundingSource }).catch(() => {});
        toast.success(publish ? 'Shift created & published' : 'Shift saved as draft');
        // Show fund warnings as separate toasts (non-blocking)
        if (created?.warnings?.length) {
          created.warnings.forEach(w => toast(w, { icon: '⚠️', duration: 6000, style: { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' } }));
        }
      }
      onSave();
    } catch (err) {
      if (!err.cancelled) toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Apply a saved template to THIS shift — prefill service type + times from its first item.
  const applyTemplate = async (templateId) => {
    set('templateId', templateId);
    if (!templateId) return;
    try {
      const { data } = await api.get(`/templates/${templateId}`);
      const it = (data.items || [])[0];
      if (!it) return toast('Template has no items', { icon: 'ℹ️' });
      setForm(f => ({
        ...f,
        shiftType: it.service_type_name || it.shift_type || f.shiftType,
        startTime: it.start_time ? String(it.start_time).slice(0, 5) : f.startTime,
        endTime: it.end_time ? String(it.end_time).slice(0, 5) : f.endTime,
        instructions: it.instructions || f.instructions,
      }));
      toast.success(`Applied template "${data.name}"`);
    } catch { toast.error('Failed to apply template'); }
  };

  const [cancelOpen, setCancelOpen] = useState(false);
  const handleDelete = () => {
    if (!isEdit || !shift?.id) { onClose(); return; }
    setCancelOpen(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/40" onClick={requestClose} />
      {cancelOpen && <CancelShiftModal shift={shift} onClose={() => setCancelOpen(false)} onDone={() => { setCancelOpen(false); onSave(); }} />}

      {/* Drawer */}
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 280 }}
        role="dialog" aria-modal="true" aria-labelledby="shift-drawer-title"
        className="absolute right-0 top-0 bottom-0 w-full max-w-[720px] bg-slate-50 flex flex-col shadow-2xl z-10 overflow-hidden"
      >
        {/* ── Header: the brand navy-to-indigo, with the shift at a glance ── */}
        {(() => {
          let dateLabel = 'No date yet';
          try { if (form.shiftDate) dateLabel = format(parseISO(form.shiftDate), 'EEE d MMM yyyy'); } catch { /* keep the fallback */ }
          const carer = staff.find(s => s.id === form.staffId);
          const st = isEdit ? (STATUS_CONFIG[shift?.status] || null) : null;
          return (
            <div className="shrink-0 text-white bg-gradient-to-br from-[#1e2a78] via-[#312e81] to-[#4f46e5] px-4 sm:px-5 pt-4 pb-3.5">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-indigo-200">{isEdit ? 'Edit shift' : 'New shift'}</p>
                  <h2 id="shift-drawer-title" className="text-lg font-bold leading-tight truncate mt-0.5">
                    {selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Choose a client'}
                  </h2>
                </div>
                {st && (
                  <span className="shrink-0 mt-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white text-slate-800">
                    {features.publish_shifts && !shift?.is_published && shift?.status !== 'completed' ? 'Draft' : st.label}
                  </span>
                )}
                <button onClick={requestClose} title="Close" aria-label="Close shift"
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors shrink-0">
                  <X size={16} />
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <HeaderChip icon={Calendar}>{dateLabel}</HeaderChip>
                <HeaderChip icon={Clock}>
                  {fmtTime(form.startTime)} – {fmtTime(form.endTime)}{form.nextDay ? ' (next day)' : ''}{hours > 0 ? ` · ${hours.toFixed(hours % 1 ? 2 : 0)} h` : ''}
                </HeaderChip>
                <HeaderChip icon={UserCircle}>
                  {carer ? `${carer.first_name} ${carer.last_name}` : form.addToJobBoard ? 'On the Job Board' : 'No worker yet'}
                </HeaderChip>
                {form.shiftType && <HeaderChip dot={shiftColor.border} title="Service">{serviceName(shiftTypes, form.shiftType)}</HeaderChip>}
              </div>
            </div>
          );
        })()}

        {/* ── Section tabs: jump through a long form; the one in view is underlined ── */}
        <nav aria-label="Shift sections" className="shrink-0 flex gap-1 px-2 sm:px-4 bg-white border-b border-slate-200 overflow-x-auto [scrollbar-width:none]">
          {DRAWER_SECTIONS.map(s => (
            <button key={s.id} type="button" onClick={() => jumpTo(s.id)} aria-current={activeSec === s.id ? 'true' : undefined}
              className={clsx('relative shrink-0 px-3 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors',
                activeSec === s.id ? 'text-primary-700' : 'text-slate-500 hover:text-slate-800')}>
              {s.label}
              {activeSec === s.id && <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary-600" aria-hidden="true" />}
            </button>
          ))}
        </nav>

        {/* ── Scrollable body ── */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 space-y-4" style={{ scrollbarWidth: 'thin' }}>

          {/* Why this shift can't go live — shown when editing a flagged shift, with the specific detail */}
          {isEdit && shift?.pending_reason && REASON_META[shift.pending_reason] && (() => {
            const red = RED_REASONS.includes(shift.pending_reason);
            return (
              <div className={clsx('flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 border', red ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200')}>
                <AlertCircle size={15} className={clsx('mt-0.5 shrink-0', red ? 'text-rose-500' : 'text-amber-500')} />
                <div className="min-w-0">
                  <p className={clsx('text-xs font-bold', red ? 'text-rose-700' : 'text-amber-700')}>{REASON_META[shift.pending_reason].label} — this shift can’t go live yet</p>
                  <p className="text-[11px] text-slate-600 mt-0.5">{shift.pending_detail || REASON_META[shift.pending_reason].tip}</p>
                  {RELEASABLE.includes(shift.pending_reason) && <ReleaseVisit shiftId={shift.id} code={shift.pending_reason} onDone={onSave} />}
                </div>
              </div>
            );
          })()}

          {/* ── Apply a template (compact shortcut) ── */}
          {templates.length > 0 && (
            <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
              <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-1 rounded-lg shrink-0">Template</span>
              <select value={form.templateId} onChange={e => applyTemplate(e.target.value)}
                className="flex-1 bg-transparent text-sm text-slate-600 focus:outline-none cursor-pointer">
                <option value="">Apply a template to prefill this shift…</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.item_count ? ` (${t.item_count} item${t.item_count > 1 ? 's' : ''})` : ''}</option>)}
              </select>
            </div>
          )}

          {/* ── Client ── */}
          <SectionCard id="sd-client" icon={Users} accent="orange" title="Client" subtitle="Who the support is for" allowOverflow>
            <FormRow label="Choose client">
              <ClientSearch clients={clients} value={form.clientIds} onChange={v => set('clientIds', v)} />
            </FormRow>
            {form.clientIds && selectedClient && (
              <FormRow label="">
                <PreShiftBriefing
                  clientId={form.clientIds}
                  clientName={`${selectedClient.first_name} ${selectedClient.last_name}`}
                  shiftId={shift?.id}
                />
              </FormRow>
            )}
            {/* Price book — inherited from the client's profile; only pickable when they have none.
                Hidden entirely for aged care: an NDIS price book does not price Support at Home, and
                the charge for an aged care visit comes from the service's own rate. Gating only the
                first branch was not enough — it fell through to a pickable NDIS book. */}
            {isAgedCareClient ? null : selectedClient?.default_price_book_id ? (
              <FormRow label="Price book">
                <div className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 flex items-center gap-2">
                  <CreditCard size={14} className="text-slate-400 shrink-0" />
                  <span className="flex-1">{selectedClient.price_book_name || 'Client price book'}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">from client profile</span>
                </div>
              </FormRow>
            ) : form.clientIds ? (
              <FormRow label="Price book">
                <ModalSelect
                  value={form.priceBookId}
                  onChange={v => set('priceBookId', v)}
                  placeholder="Select"
                  options={priceBooks.filter(b => b.is_active !== false).map(b => ({ value: b.id, label: b.name }))}
                />
              </FormRow>
            ) : null}
            {/* Funding row only for clients without a funding plan — funded clients pick the
                service (which carries its budget) in the Service field below. */}
            {/* Aged care: their real Support at Home position, not the NDIS fund check. */}
            {isAgedCareClient && agedCare && (
              <FormRow label="Funding">
                <div className="w-full">
                  {isChspBooking && (
                    <div className="px-4 py-3 rounded-xl border border-teal-200 bg-teal-50/60">
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <span className="text-sm font-semibold text-teal-900">CHSP</span>
                        <span className="text-[12px] text-teal-800">
                          {agedCare.chsp?.feeReductionPct >= 100 ? 'Contribution waived'
                            : agedCare.chsp?.feeReductionPct > 0 ? `Contribution reduced ${agedCare.chsp.feeReductionPct}%` : 'Pays the contribution'}
                        </span>
                      </div>
                      <div className="text-[12px] text-teal-800/80 mt-0.5">
                        Grant-funded, no budget to draw. Enrolled from {agedCare.chsp?.startDate || '—'}{agedCare.chsp?.endDate ? ` to ${agedCare.chsp.endDate}` : ''}. The visit is reported to DEX.
                      </div>
                    </div>
                  )}
                  {agedCare.plan ? (
                    <div className="px-4 py-3 rounded-xl border border-teal-200 bg-teal-50/60">
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <span className="text-sm font-semibold text-teal-900">
                          Support at Home{agedCare.plan.level ? `, classification ${agedCare.plan.level}` : ''}
                        </span>
                        <span className="text-sm tabular-nums text-teal-900">
                          ${Number(agedCare.plan.remaining).toLocaleString('en-AU', { minimumFractionDigits: 2 })} left
                        </span>
                      </div>
                      <div className="text-[12px] text-teal-800/80 mt-0.5 tabular-nums">
                        ${Number(agedCare.plan.spent).toLocaleString('en-AU', { minimumFractionDigits: 2 })} of
                        ${Number(agedCare.plan.budget).toLocaleString('en-AU', { minimumFractionDigits: 2 })} used this {agedCare.plan.quarter ? 'quarter' : String(agedCare.plan.period || 'quarter').toLowerCase().replace(/ly$/, '')}
                      </div>
                    </div>
                  ) : null}
                  {agedCare.blocker && (
                    <div className="mt-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 flex items-start gap-2">
                      <AlertCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                      <span>{agedCare.blocker}</span>
                    </div>
                  )}
                </div>
              </FormRow>
            )}
            {form.clientIds && !hasSelectable && !isAgedCareClient && (
              <FormRow label="Funding">
                <div className="w-full px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-semibold flex items-center gap-2">
                  <AlertCircle size={14} className="text-red-500 shrink-0" />
                  No funded services — add funding to this client before billing
                </div>
              </FormRow>
            )}
          </SectionCard>

          {/* ── Shift ── */}
          <SectionCard id="sd-shift" icon={Briefcase} accent="violet" title="Service" subtitle="What's delivered, and how it's funded">
            <FormRow label="Service">
              {!form.clientIds ? (
                <div className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-500">
                  Select a client first — their funded services appear here.
                </div>
              ) : (
                <>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full pointer-events-none"
                      style={{ backgroundColor: shiftColor.border }} />
                    {useNdisFunds ? (
                      // Funded client: the funded services ARE the services. One pick = service + budget.
                      // Own-budget services bill against their ring-fenced fund; pool services draw from
                      // the plan's shared pool. Both are selectable; only fund-less lines are greyed out.
                      <select
                        value={currentServiceValue}
                        onChange={e => handleServiceChange(e.target.value)}
                        className="w-full appearance-none border border-slate-300 rounded-lg pl-10 pr-10 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      >
                        <option value="">Select a funded service…</option>
                        {fundOptions.map(f => {
                          const bal = f.fund_amount != null ? Number(f.fund_amount) - Number(f.spent_amount || 0) : null;
                          const code = f.support_item_number ? ` · ${f.support_item_number}` : '';
                          return <option key={f.id} value={`f:${f.id}`}>{f.service_name || f.fund_name}{code}{bal != null ? ` — $${bal.toFixed(0)} left` : ''}</option>;
                        })}
                        {poolOptions.length > 0 && (
                          <optgroup label="From the shared plan pool">
                            {poolOptions.map(s => {
                              const code = s.support_item_number ? ` · ${s.support_item_number}` : '';
                              const bal = s.available != null ? ` ($${Number(s.available).toFixed(0)} left)` : '';
                              return <option key={s.line_id} value={`p:${s.line_id}`}>{s.service_name}{code} — from pool{bal}</option>;
                            })}
                          </optgroup>
                        )}
                        {trulyUnfunded.length > 0 && (
                          <optgroup label="No funds available — add a budget to book">
                            {trulyUnfunded.map(s => <option key={s.line_id} value="" disabled>{s.service_name}{s.support_item_number ? ` · ${s.support_item_number}` : ''} — no funds available</option>)}
                          </optgroup>
                        )}
                      </select>
                    ) : showNdisUnfunded ? (
                      // Has a plan, but every service is unbudgeted AND has no pool to draw from → none
                      // bookable yet. Show them greyed with a clear reason (add a budget to book).
                      <select
                        value=""
                        onChange={() => {}}
                        className="w-full appearance-none border border-slate-300 rounded-lg pl-10 pr-10 py-2.5 text-sm text-slate-400 bg-white focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 shadow-sm"
                      >
                        <option value="">No funds available — add a budget…</option>
                        {trulyUnfunded.map(s => <option key={s.line_id} value="" disabled>{s.service_name}{s.support_item_number ? ` · ${s.support_item_number}` : ''} — no funds available</option>)}
                      </select>
                    ) : (
                      // Client has no funding plan: fall back to the configured service-types list
                      <select
                        value={form.shiftType}
                        onChange={e => set('shiftType', e.target.value)}
                        className="w-full appearance-none border border-slate-300 rounded-lg pl-10 pr-10 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      >
                        <option value="">Select service…</option>
                        {(isAgedCareClient
                          // Only Support at Home services, labelled with the category that decides
                          // what this participant contributes. With none mapped, none are offered:
                          // an unmapped service is care that never reaches a budget, a statement
                          // or a claim. The note below says how to fix it.
                          ? shiftTypes.filter(t => t.is_active && (agedCare?.services || []).some(a => a.id === t.id))
                          : shiftTypes.filter(t => t.is_active)
                        ).map(t => {
                          const ac = isAgedCareClient ? agedCare?.services?.find(a => a.id === t.id) : null;
                          const left = isAgedCareClient && agedCare?.plan
                            ? ` · $${Number(agedCare.plan.remaining).toFixed(0)} left${agedCare.plan.quarter ? ' this quarter' : ''}`
                            : '';
                          const notApproved = ac && ac.approved === false ? (isChspBooking ? ' (not in their referral)' : ' (not in their Notice of Decision)') : '';
                          // CHSP: what the participant pays per unit, after any hardship reduction.
                          const fee = isChspBooking && ac ? (Number(ac.fee) > 0 ? ` · $${Number(ac.fee).toFixed(2)}${ac.unit === 'hour' ? ' an hour' : ' each'}` : ' · no contribution') : '';
                          return <option key={t.id} value={t.name}>{t.name}{ac?.category ? ` — ${ac.category}` : ''}{isChspBooking && ac?.item ? ` — ${ac.item}` : ''}{left}{fee}{notApproved}</option>;
                        })}
                      </select>
                    )}
                    <ChevronDown size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {isChspBooking
                      ? 'CHSP services from your set-up. The participant is invoiced their contribution, and the visit is counted against your targets and reported to DEX.'
                      : isAgedCareClient
                      // Aged care has no per-service funds, so the NDIS wording ("no funding plan")
                      // would be both wrong and alarming for a participant who has one.
                      ? 'Support at Home services draw on the one quarterly budget. The category decides what the participant contributes — clinical is fully funded.'
                      : useNdisFunds
                        ? "From the client's funding plan — own-budget services bill their fund; “from pool” services draw from the shared plan budget."
                        : showNdisUnfunded
                          ? 'These services have no budget or shared pool — add a budget on the client’s funding plan to make them bookable.'
                          : 'This client has no funding plan — pick a configured service type (or add a funding plan on their profile).'}
                  </p>
                  {isAgedCareClient && (() => {
                    // A restorative care episode alongside ongoing services: the provider chooses which
                    // account each visit is claimed from. An end-of-life episode replaces the ongoing
                    // classification, so there is nothing to choose.
                    const d = form.shiftDate;
                    const w = (agedCare?.pathways || []).find((x) => d && x.start && d >= x.start && (!x.end || d <= x.end));
                    if (!w) return null;
                    if (w.type === 'end_of_life') return (
                      <label className="flex items-center gap-2 mt-2 text-[12px] text-slate-700">
                        <span>Claim from</span>
                        <select value={form.agedCareFundingSource || ''} onChange={(e) => set('agedCareFundingSource', e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-[12px]">
                          <option value="">End-of-Life Pathway</option>
                          <option value="AT">Assistive technology (for an AT item)</option>
                          <option value="HM">Home modifications (for an HM item)</option>
                        </select>
                      </label>
                    );
                    if (!agedCare?.hasOngoing) return <p className="text-[12px] text-slate-600 mt-1">Claimed from the Restorative Care Pathway.</p>;
                    return (
                      <label className="flex items-center gap-2 mt-2 text-[12px] text-slate-700">
                        <span>Claim from</span>
                        <select value={form.agedCareFundingSource || ''} onChange={(e) => set('agedCareFundingSource', e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-[12px]">
                          <option value="">Automatic (clinical to restorative, the rest ongoing)</option>
                          <option value="RC">Restorative Care Pathway</option>
                          <option value="ON">Ongoing quarterly budget</option>
                          <option value="AT">Assistive technology (for an AT item)</option>
                          <option value="HM">Home modifications (for an HM item)</option>
                        </select>
                      </label>
                    );
                  })()}
                  {isAgedCareClient && agedCare?.services?.find((a) => shiftTypes.some((t) => t.id === a.id && t.name === form.shiftType))?.approved === false && (
                    <p className="text-[12px] text-amber-800 mt-1">{isChspBooking
                      ? 'This participant was not referred for this CHSP service, so the visit will be held. Pick a service they were referred for, or add it to their referral in Aged Care, CHSP.'
                      : "This service is not in the participant's Notice of Decision, so Services Australia will not pay for it. Pick an approved service, or update the approved services on their funding plan."}</p>
                  )}
                </>
              )}
            </FormRow>
            <FormRow label="Activity">
              <input value={form.activity} onChange={e => set('activity', e.target.value)} placeholder="What the worker will do this shift"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" />
            </FormRow>
            <FormRow label="Roster comments">
              <textarea value={form.rosterComments} onChange={e => set('rosterComments', e.target.value)} rows={2} placeholder="Internal notes for the roster (not shown to the client)"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" />
            </FormRow>
            <div className="pt-1">
              <label className="flex items-center justify-between py-1">
                <span className="text-sm text-slate-700">Override qualification / availability check</span>
                <input type="checkbox" checked={form.overrideQual} onChange={e => set('overrideQual', e.target.checked)} className="w-4 h-4 rounded border-slate-300 accent-primary-500" />
              </label>
              <label className="flex items-center justify-between py-1">
                <span className="text-sm text-slate-700">Irregular SIL <span className="text-[11px] text-slate-500">(unplanned SIL, claimed on its own with claim type IRSS, outside the weekly amount)</span></span>
                <input type="checkbox" checked={!!form.irregularSil} onChange={e => set('irregularSil', e.target.checked)} className="w-4 h-4 rounded border-slate-300 accent-primary-500" />
              </label>
              {form.overrideQual && (
                <input value={form.overrideQualReason} onChange={e => set('overrideQualReason', e.target.value)} placeholder="Reason for override (logged for NDIS audit)"
                  className="w-full mt-1.5 border border-amber-200 bg-amber-50 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
              )}
            </div>
          </SectionCard>

          {/* ── Time & Location ── */}
          <SectionCard id="sd-when" icon={Clock} accent="blue" title="When & where">
            <FormRow label="Date">
              <div className="relative">
                <Calendar size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="date"
                  value={form.shiftDate}
                  onChange={e => set('shiftDate', e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white"
                />
              </div>
            </FormRow>
            <div className="sm:pl-36">
              <SwitchRow label="Finishes the next day" hint="For overnight shifts and sleepovers" value={form.nextDay} onChange={v => set('nextDay', v)} />
            </div>
            <FormRow label="Time">
              {/* Duration presets */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  { label: 'Morning', start: '09:00', end: '13:00' },
                  { label: 'Afternoon', start: '13:00', end: '17:00' },
                  { label: 'Evening', start: '17:00', end: '21:00' },
                  { label: 'Full Day', start: '09:00', end: '17:00' },
                  { label: 'Sleepover', start: '22:00', end: '07:00' },
                ].map(p => (
                  <button key={p.label} type="button"
                    onClick={() => { set('startTime', p.start); set('endTime', p.end); set('nextDay', p.label === 'Sleepover'); }}
                    className={clsx('px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors',
                      form.startTime === p.start && form.endTime === p.end
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-primary-300 hover:text-primary-700')}>
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <input type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)}
                  className="flex-1 min-w-0 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" />
                <span className="text-slate-500 text-sm shrink-0">to</span>
                <input type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)}
                  className="flex-1 min-w-0 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" />
              </div>
              {hours > 0 && (
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold tabular-nums">
                    <Clock size={12} aria-hidden="true" /> {hours.toFixed(1)} hours
                  </span>
                  {estimatedCost > 0 && (
                    <span className="ml-auto text-xs font-semibold text-emerald-600">
                      Est. cost: ${estimatedCost.toFixed(2)}
                    </span>
                  )}
                </div>
              )}
              {/* Conflict warning */}
              {conflictWarning && (
                <div className="mt-2 flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                  <AlertCircle size={13} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Scheduling conflict detected</p>
                    {conflictWarning.map((c, i) => (
                      <p key={i}>{fmtTime12(c.start_time)}–{fmtTime12(c.end_time)} already scheduled on this day</p>
                    ))}
                  </div>
                </div>
              )}
            </FormRow>
            <div className="sm:pl-36">
              <SwitchRow label="Repeat this shift" hint="Book the same shift on a schedule" value={form.repeat} onChange={v => set('repeat', v)} />
            </div>
            {form.repeat && (() => {
              const pat = form.recurringPattern || 'weekly';
              const hasDays = ['weekly', 'fortnightly'].includes(pat);
              const dayDefs = [['M', 1, 'Monday'], ['T', 2, 'Tuesday'], ['W', 3, 'Wednesday'], ['T', 4, 'Thursday'], ['F', 5, 'Friday'], ['S', 6, 'Saturday'], ['S', 0, 'Sunday']];
              const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              const sortKey = (d) => (d === 0 ? 7 : d);
              const summary = (() => {
                const freq = { daily: 'every day', weekly: 'every week', fortnightly: 'every 2 weeks', monthly: 'every month' }[pat];
                const days = (form.recurringDays || []).slice().sort((a, b) => sortKey(a) - sortKey(b));
                const onDays = hasDays && days.length ? ` on ${days.map(d => dayNames[d]).join(', ')}` : '';
                let until = '';
                try { if (form.recurringEndDate) until = ` until ${format(new Date(form.recurringEndDate), 'd MMM yyyy')}`; } catch { /* */ }
                return `Repeats ${freq}${onDays}${until}.`;
              })();
              return (
              <div className="sm:ml-36 bg-violet-50/60 border border-violet-200 rounded-xl p-4 space-y-3.5">
                {/* Frequency — segmented */}
                <div>
                  <label className="block text-xs font-medium text-violet-900 mb-1.5">Frequency</label>
                  <div className="flex gap-0.5 bg-white border border-violet-200 rounded-xl p-0.5">
                    {[['daily', 'Daily'], ['weekly', 'Weekly'], ['fortnightly', 'Fortnightly'], ['monthly', 'Monthly']].map(([v, lbl]) => (
                      <button key={v} type="button" onClick={() => set('recurringPattern', v)}
                        className={clsx('flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors',
                          pat === v ? 'bg-primary-500 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50')}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Repeat on — circular day toggles (weekly/fortnightly) */}
                {hasDays && (
                  <div>
                    <label className="block text-xs font-medium text-violet-900 mb-1.5">Repeat on</label>
                    <div className="flex gap-1.5">
                      {dayDefs.map(([lbl, d, full], i) => {
                        const sel = (form.recurringDays || []).includes(d);
                        return (
                          <button key={i} type="button" title={full}
                            onClick={() => set('recurringDays', sel ? form.recurringDays.filter(x => x !== d) : [...(form.recurringDays || []), d])}
                            className={clsx('w-9 h-9 rounded-full text-xs font-bold transition-all',
                              sel ? 'bg-primary-500 text-white shadow-sm ring-2 ring-primary-200' : 'bg-white border border-slate-200 text-slate-400 hover:border-primary-300 hover:text-primary-500')}>
                            {lbl}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* End date */}
                <div>
                  <label className="block text-xs font-medium text-violet-900 mb-1.5">Repeat until *</label>
                  <input type="date"
                    className="w-full border border-violet-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-200"
                    value={form.recurringEndDate || ''}
                    onChange={e => set('recurringEndDate', e.target.value)}
                    min={form.shiftDate} />
                </div>

                {/* Live summary */}
                <div className="flex items-start gap-2 text-xs text-violet-800 bg-white/70 rounded-xl px-3 py-2 border border-violet-100">
                  <Repeat2 size={13} className="text-violet-500 mt-0.5 shrink-0" />
                  <span>{summary} {hasDays && !(form.recurringDays || []).length && <span className="text-violet-500/80">Repeats on the same weekday as this shift. </span>}Clashing shifts are skipped automatically.</span>
                </div>
              </div>
              );
            })()}
            {clientAddress && !overrideAddress && !form.address ? (
              <FormRow label="Shift address">
                <div className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 flex items-center gap-2">
                  <MapPin size={14} className="text-slate-400 shrink-0" />
                  <span className="flex-1">{clientAddress}</span>
                  <button type="button" onClick={() => setOverrideAddress(true)} className="text-[11px] text-primary-600 hover:underline shrink-0">Starts somewhere else</button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Using the client's address. Workers clock in here.</p>
              </FormRow>
            ) : (
              <FormRow label={clientAddress ? 'Shift address (optional)' : 'Shift address'}>
                <div className="relative">
                  {form.address.length > 0 && form.address.length < 5 && (
                    <AlertCircle size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-amber-500" />
                  )}
                  <AddressAutocomplete
                    className={clsx(
                      'w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 bg-white',
                      form.address.length > 0 && form.address.length < 5
                        ? 'pl-9 border-amber-300 focus:border-amber-400'
                        : 'border-slate-200 focus:border-primary-400'
                    )}
                    placeholder={clientAddress ? 'Where does this shift start?' : 'Enter Address'}
                    value={form.address}
                    onChange={v => { set('address', v); set('shiftAddressLat', null); set('shiftAddressLng', null); }}
                    onSelect={a => {
                      set('address', [a.street, a.suburb, a.state, a.postcode].filter(Boolean).join(', '));
                      set('shiftAddressLat', a.lat != null ? Number(a.lat) : null);
                      set('shiftAddressLng', a.lon != null ? Number(a.lon) : null);
                    }}
                  />
                </div>
                {isCustomLocation && (
                  <p className="text-[11px] text-violet-700 mt-1 flex items-center gap-1">
                    <MapPin size={11} /> Workers clock in at this address, not the {clientAddress ? "client's" : 'house'} address.
                    {form.shiftAddressLat != null ? ' Location pinned.' : ' It is placed on the map when you save.'}
                  </p>
                )}
                {clientAddress && (
                  <button type="button" onClick={() => { set('address', ''); set('shiftAddressLat', null); set('shiftAddressLng', null); setOverrideAddress(false); }}
                    className="text-[11px] text-slate-500 hover:underline mt-1">← Use the client's address instead</button>
                )}
                {form.address.length > 0 && form.address.length < 5 && (
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                    <AlertCircle size={11} /> Invalid address, please enter a full street address
                  </p>
                )}
              </FormRow>
            )}
            {/* Shift end address: same place as the start unless the shift finishes somewhere else
                (dropping the participant at a day program, a family home, respite). */}
            {!endsElsewhere ? (
              <FormRow label="Shift end address">
                <div className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 flex items-center gap-2">
                  <Flag size={14} className="text-slate-400 shrink-0" />
                  <span className="flex-1">Same as the start address</span>
                  <button type="button" onClick={() => setEndsElsewhere(true)} className="text-[11px] text-primary-600 hover:underline shrink-0">Ends somewhere else</button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Workers clock out where the shift starts.</p>
              </FormRow>
            ) : (
              <FormRow label="Shift end address">
                <AddressAutocomplete
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-400 bg-white"
                  placeholder="Where does this shift end?"
                  value={form.endAddress}
                  onChange={v => { set('endAddress', v); set('endAddressLat', null); set('endAddressLng', null); }}
                  onSelect={a => {
                    set('endAddress', [a.street, a.suburb, a.state, a.postcode].filter(Boolean).join(', '));
                    set('endAddressLat', a.lat != null ? Number(a.lat) : null);
                    set('endAddressLng', a.lon != null ? Number(a.lon) : null);
                  }}
                />
                {form.endAddress ? (
                  <p className="text-[11px] text-violet-700 mt-1 flex items-center gap-1">
                    <Flag size={11} /> Workers clock out at this address.
                    {form.endAddressLat != null ? ' Location pinned.' : ' It is placed on the map when you save.'}
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-1">Leave it empty and the shift ends where it starts.</p>
                )}
                <button type="button" onClick={() => { set('endAddress', ''); set('endAddressLat', null); set('endAddressLng', null); setEndsElsewhere(false); }}
                  className="text-[11px] text-slate-500 hover:underline mt-1">← Ends where it starts</button>
              </FormRow>
            )}
          </SectionCard>

          {/* ── Carer ── */}
          <SectionCard
            id="sd-carer" icon={UserCircle} accent="teal" title="Worker"
            extra={
              <label className="flex items-center gap-2 cursor-pointer shrink-0">
                <span className="text-xs font-medium text-slate-600">Add to Job Board</span>
                <Toggle value={form.addToJobBoard} onChange={v => set('addToJobBoard', v)} label="Add to Job Board" />
              </label>
            }
          >
            <FormRow label="Choose worker">
              <div className="space-y-2">
                <CarerSearch staff={staff} value={form.staffId} onChange={v => set('staffId', v)}
                  preferredIds={workerPrefs.preferred} excludedIds={workerPrefs.excluded} />
                {selectedStaffExcluded && (
                  <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                    <Ban size={13} className="mt-0.5 shrink-0" />
                    <span>This worker is <b>excluded</b> for {selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'this client'}. Saving will be blocked — pick another worker, or remove the exclusion on the client's profile.</span>
                  </div>
                )}
                <button onClick={runSmartMatchPanel} disabled={smartMatchLoading}
                  className={AI_BTN}>
                  {smartMatchLoading ? <Loader2 size={12} className="animate-spin text-violet-500" /> : <Sparkles size={12} className="text-violet-500" />}
                  AI Match
                </button>
                {smartMatchOpen && (
                  <div className="border border-violet-200 rounded-xl overflow-hidden bg-white shadow-sm">
                    <div className="flex items-center justify-between px-3 py-2 bg-violet-50 border-b border-violet-100">
                      <span className="text-xs font-bold text-violet-700 flex items-center gap-1.5"><Sparkles size={11}/>AI Staff Match</span>
                      <button onClick={() => setSmartMatchOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={13}/></button>
                    </div>
                    {smartMatchLoading && (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 size={18} className="animate-spin text-violet-400"/>
                      </div>
                    )}
                    {!smartMatchLoading && smartMatchResults.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-4">No recommendations available</p>
                    )}
                    {!smartMatchLoading && smartMatchResults.length > 0 && (
                      <div className="divide-y divide-slate-50 max-h-48 overflow-y-auto">
                        {smartMatchResults.map((r) => {
                          const isSelected = form.staffId === (r.staffId || r.staff_id);
                          const score = r.score ?? r.matchScore ?? r.percentage;
                          return (
                            <div key={r.staffId || r.staff_id}
                              className={clsx('flex items-start gap-2.5 px-3 py-2.5 transition-colors', isSelected ? 'bg-violet-50' : 'hover:bg-slate-50')}>
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-400 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5">
                                {r.rank || '#'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-slate-800">{r.staffName || r.staff_name}</p>
                                {score !== undefined && <p className="text-[10px] text-slate-500">Match: {typeof score === 'number' ? `${Math.round(score)}%` : score}</p>}
                                {r.reason && <p className="text-[10px] text-slate-400 truncate">{r.reason}</p>}
                              </div>
                              <button onClick={() => { set('staffId', r.staffId || r.staff_id); setSmartMatchOpen(false); }}
                                className="shrink-0 px-2 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-[10px] font-semibold transition-colors">
                                Select
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </FormRow>
            <FormRow label="Qualification match">
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={runSmartMatch} disabled={loadingMatch}
                  className={AI_BTN}>
                  {loadingMatch ? <Loader2 size={14} className="animate-spin text-violet-500" /> : <Sparkles size={14} className="text-violet-500" />}
                  Rank by qualifications
                </button>
                <button onClick={runAiRecommend} disabled={loadingAiRecs}
                  className={AI_BTN}>
                  {loadingAiRecs ? <Loader2 size={14} className="animate-spin text-indigo-500" /> : <Sparkles size={14} className="text-violet-500" />}
                  AI Recommend
                </button>
              </div>
            </FormRow>
            {aiRecs?.recommendations?.length > 0 && (
              <FormRow label="AI recommendations">
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {aiRecs.recommendations.map((r) => {
                    const isSelected = form.staffId === r.staffId;
                    const confColor = r.confidence === 'high' ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
                                    : r.confidence === 'medium' ? 'text-amber-600 bg-amber-50 border-amber-200'
                                    : 'text-slate-500 bg-slate-50 border-slate-200';
                    return (
                      <button key={r.staffId} onClick={() => set('staffId', r.staffId)}
                        className={clsx('w-full flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-colors',
                          isSelected ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-50')}>
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-400 to-violet-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5">
                          {r.rank}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-semibold text-slate-800">{r.staffName}</p>
                            <span className={clsx('text-[10px] px-1.5 py-0.5 rounded-full border font-medium', confColor)}>
                              {r.confidence}
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">{r.reason}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </FormRow>
            )}
            {smartMatches && (
              <FormRow label={smartMatches.noRequirements ? 'All carers (no requirements set)' : 'Matched carers'}>
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {smartMatches.staff.slice(0, 10).map(s => {
                    const isSelected = form.staffId === s.id;
                    const pct = s.matchScore;
                    const color = pct === 100 ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
                                : pct >= 50   ? 'text-amber-600 bg-amber-50 border-amber-200'
                                :               'text-red-500 bg-red-50 border-red-200';
                    return (
                      <button key={s.id} onClick={() => set('staffId', s.id)}
                        className={clsx('w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border text-left transition-colors',
                          isSelected ? 'border-primary-400 bg-primary-50' : 'border-slate-200 bg-white hover:bg-slate-50')}>
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                          {(s.first_name || '?')[0]}{(s.last_name || '?')[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800">{s.first_name} {s.last_name}</p>
                          {!smartMatches.noRequirements && s.missingNeeds.length > 0 && (
                            <p className="text-[10px] text-slate-400 truncate">Missing: {s.missingNeeds.join(', ')}</p>
                          )}
                        </div>
                        {!smartMatches.noRequirements && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${color}`}>{pct}%</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </FormRow>
            )}
            <div className="sm:pl-36">
              <SwitchRow label="Notify the worker" hint="They're told about this shift when you save" value={form.notifyCarer} onChange={v => set('notifyCarer', v)} />
            </div>
          </SectionCard>

          {/* ── More options (secondary sections, collapsed by default) ── */}
          <button id="sd-more" type="button" onClick={() => setShowMore(m => !m)} aria-expanded={showMore}
            className="scroll-mt-3 w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <span className="flex items-center gap-2"><ChevronRight size={15} className={clsx('text-slate-500 transition-transform', showMore && 'rotate-90')} /> More options</span>
            <span className="text-xs font-normal text-slate-500 truncate">Facility, mileage, travel and transport, signatures</span>
          </button>

          {showMore && (<>
          {/* ── Facilities ── */}
          <SectionCard icon={Building2} accent="slate" title="Facility">
            <FormRow label="Facility">
              <ModalSelect
                value={form.facilityId}
                onChange={v => set('facilityId', v)}
                placeholder="Select"
                options={facilities.map(f => ({ value: f.id, label: f.name }))}
              />
            </FormRow>
          </SectionCard>

          {/* ── Mileage ── */}
          <SectionCard icon={Car} accent="emerald" title="Mileage">
            <FormRow label="Mileage cap">
              <input type="number" min="0" value={form.mileageCap} onChange={e => set('mileageCap', parseFloat(e.target.value) || 0)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" />
            </FormRow>
            <FormRow label="Mileage">
              <input type="number" min="0" value={form.mileage} onChange={e => set('mileage', parseFloat(e.target.value) || 0)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" />
            </FormRow>
            <FormRow label="Company Vehicle">
              <select
                value={form.vehicleId}
                onChange={e => set('vehicleId', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="">No vehicle assigned</option>
                {companyVehicles.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.registration}{v.make ? ` — ${v.make}` : ''}{v.model ? ` ${v.model}` : ''}{v.color ? ` (${v.color})` : ''}
                    {v.status === 'in_use' ? ' [In Use]' : ''}
                  </option>
                ))}
              </select>
            </FormRow>
            {ratesPay && (
              <label className="flex items-start justify-between gap-4 py-1">
                <span className="min-w-0">
                  <span className="block text-sm text-slate-800">Pay travel</span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    Pays the worker's km from their previous shift today to this shift's address, measured by map at their Km rate. Same day only.
                  </span>
                </span>
                <input type="checkbox" checked={!!form.payTravel} onChange={e => set('payTravel', e.target.checked)}
                  aria-label="Pay travel" className="mt-1 w-4 h-4 shrink-0 rounded border-slate-300 accent-primary-500" />
              </label>
            )}
          </SectionCard>

          {/* ── Travel & Transport (Build 3a) ── */}
          <SectionCard icon={Car} accent="sky" title="Travel and transport"
            extra={<span className="text-[11px] text-slate-500 shrink-0">Caps and rates come from Settings</span>}
          >
            <FormRow label="Charge travel to client">
              <select value={form.chargeTravel} onChange={e => set('chargeTravel', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white">
                <option value="none">None</option>
                <option value="service_hours">Add to service hours</option>
                <option value="separate">Invoice separately</option>
              </select>
            </FormRow>
            {form.chargeTravel !== 'none' && (
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Travel hours"><input type="number" step="0.25" min="0" value={form.travelHours} onChange={e => set('travelHours', e.target.value)} placeholder="recorded" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" /></FormRow>
                <FormRow label="Travel km (worker)"><input type="number" step="0.1" min="0" value={form.travelKm} onChange={e => set('travelKm', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" /></FormRow>
                <FormRow label="Travel time rate ($/hr)"><input type="number" step="0.01" min="0" value={form.travelTimeRate} onChange={e => set('travelTimeRate', e.target.value)} placeholder="inherit" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" /></FormRow>
                <FormRow label="Travel km rate ($/km)"><input type="number" step="0.01" min="0" value={form.travelKmRate} onChange={e => set('travelKmRate', e.target.value)} placeholder="inherit" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" /></FormRow>
              </div>
            )}
            <div className="mt-1 pt-3 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-500 mb-2">Transport (client in car)</p>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Transport km"><input type="number" step="0.1" min="0" value={form.transportKm} onChange={e => set('transportKm', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" /></FormRow>
                <FormRow label="Transport rate ($/km)"><input type="number" step="0.01" min="0" value={form.transportKmRate} onChange={e => set('transportKmRate', e.target.value)} placeholder="inherit" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white" /></FormRow>
              </div>
              <FormRow label="Transport fund">
                <select value={form.transportFundId} onChange={e => set('transportFundId', e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-white">
                  <option value="">Auto (transport fund → service fund)</option>
                  {(selectedClient?.funds || []).map(f => <option key={f.id} value={f.id}>{f.fund_name}</option>)}
                </select>
              </FormRow>
              <label className="flex items-center justify-between py-1 mt-1">
                <span className="text-sm text-slate-700">Bill transport out-of-pocket (not NDIA)</span>
                <input type="checkbox" checked={form.transportOutOfPocket} onChange={e => set('transportOutOfPocket', e.target.checked)} className="w-4 h-4 rounded border-slate-300 accent-primary-500" />
              </label>
            </div>
          </SectionCard>

          {/* ── Signatures on clock out ── */}
          <SectionCard icon={PenLine} accent="amber" title="Signatures on clock out"
            extra={<Info size={13} className="text-slate-400" />}
          >
            <div className="space-y-3">
              {[
                ['requireClientSignature', 'Require client signature on clock out'],
                ['requireCarerSignature',  'Require carer signature on clock out'],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between py-1">
                  <span className="text-sm text-slate-700">{label}</span>
                  <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 accent-primary-500" />
                </div>
              ))}
            </div>
          </SectionCard>
          </>)}

          {/* Shift History */}
          {isEdit && shiftHistory.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <button onClick={() => setHistoryTab(h => !h)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                <span className="flex items-center gap-2"><FileText size={14} className="text-slate-400" /> Audit History ({shiftHistory.length})</span>
                <ChevronDown size={14} className={clsx('text-slate-400 transition-transform', historyTab && 'rotate-180')} />
              </button>
              {historyTab && (
                <div className="border-t border-slate-100 divide-y divide-slate-50 max-h-48 overflow-y-auto">
                  {shiftHistory.map(h => (
                    <div key={h.id} className="px-4 py-2.5 flex items-start gap-3">
                      <div className={clsx('w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[9px] font-bold text-white mt-0.5',
                        h.action === 'created' ? 'bg-emerald-500' : h.action === 'cancelled' || h.action === 'bulk_cancel' ? 'bg-red-500' : 'bg-blue-500')}>
                        {h.action === 'created' ? '+' : h.action.includes('cancel') ? '×' : '~'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-700 capitalize">{h.action.replace('_', ' ')}</p>
                        {h.note && <p className="text-[11px] text-slate-500 truncate">{h.note}</p>}
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {h.changed_by_name} · {h.created_at ? new Date(h.created_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Recurring scope panel — edit + cancel */}
          {isEdit && shift?.is_recurring && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-3">
              <p className="text-xs text-violet-700 font-semibold flex items-center gap-1.5">
                <Repeat2 size={13} /> This is a recurring shift
              </p>

              {/* Edit scope */}
              <div>
                <p className="text-[11px] text-violet-600 mb-1.5">Update series:</p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { scope: 'future', label: 'This + Future' },
                    { scope: 'all',    label: 'All in Series' },
                  ].map(opt => (
                    <button key={opt.scope} type="button" disabled={saving}
                      onClick={async () => {
                        setSaving(true);
                        try {
                          await api.put(`/shifts/${shift.id}/recurring-scope`, {
                            scope: opt.scope,
                            changes: { shift_type: form.shiftType, start_time: form.startTime, end_time: form.endTime, address: form.address, staff_id: form.staffId || null, instructions: form.instructions, break_minutes: form.breakMinutes || 0,
                              shiftAddress: isCustomLocation ? form.address : '', shiftAddressLat: isCustomLocation ? form.shiftAddressLat : null, shiftAddressLng: isCustomLocation ? form.shiftAddressLng : null,
                              ...endAddressPayload(),
                              ...(ratesPay ? { pay_travel: !!form.payTravel } : {}) },
                          });
                          toast.success(`Updated ${opt.label.toLowerCase()}`);
                          onSave();
                        } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
                        finally { setSaving(false); }
                      }}
                      className="px-3 py-1.5 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50">
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cancel scope */}
              <div className="border-t border-violet-200 pt-2.5">
                <p className="text-[11px] text-red-500 font-medium mb-1.5">Cancel occurrences:</p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { scope: 'single', label: 'This Only' },
                    { scope: 'future', label: 'This + Future' },
                    { scope: 'all',    label: 'All in Series' },
                  ].map(opt => (
                    <button key={opt.scope} type="button" disabled={saving}
                      onClick={async () => {
                        const labels = { single: 'this shift', future: 'this and all future shifts', all: 'all shifts in this series' };
                        if (!await confirm(`Cancel ${labels[opt.scope]}?`, { title: 'Cancel Shifts', danger: true })) return;
                        setSaving(true);
                        try {
                          const { data } = await api.delete(`/shifts/${shift.id}/recurring-scope`, { data: { scope: opt.scope } });
                          toast.success(`${data.cancelled} shift${data.cancelled !== 1 ? 's' : ''} cancelled`);
                          onSave();
                        } catch (err) { toast.error(err.response?.data?.message || 'Cancel failed'); }
                        finally { setSaving(false); }
                      }}
                      className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50">
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Cancel button — non-recurring shifts only */}
          {isEdit && !shift?.is_recurring && (
            <button onClick={handleDelete}
              className="w-full h-10 border border-red-200 bg-white text-red-600 hover:bg-red-50 rounded-lg text-sm font-semibold transition-colors">
              Cancel this shift
            </button>
          )}

          <div className="h-4" />
        </div>

        {/* ── Bottom action bar ── */}
        <div className="flex items-center justify-between px-3 sm:px-5 py-3 bg-white border-t border-slate-200 shrink-0 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={requestClose} className="h-10 px-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors shrink-0">
              {isDirty ? 'Discard changes' : 'Close'}
            </button>
            {isDirty && (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 truncate">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" aria-hidden="true" /> Unsaved changes
              </span>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            {features.publish_shifts && (
            <button onClick={() => handleSave(false)} disabled={saving}
              className="h-10 px-4 border border-slate-300 bg-white rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60">
              Save draft
            </button>
            )}
            <button onClick={() => handleSave(features.publish_shifts)} disabled={saving}
              className="h-10 inline-flex items-center gap-2 px-5 rounded-lg text-sm font-semibold text-white bg-gradient-to-br from-[#1e2a78] to-[#4f46e5] hover:from-[#18226a] hover:to-[#4338ca] shadow-[0_6px_16px_-8px_rgba(79,70,229,0.6)] transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {features.publish_shifts ? 'Save and publish' : 'Save shift'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── ViewDropdown ─────────────────────────────────────────────────────────────

function ViewDropdown({ view, setView, compact, setCompact, expand, setExpand }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="h-8 max-sm:h-10 flex items-center gap-1.5 px-2.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-sm">
        {view}
        <ChevronDown size={14} className={clsx('transition-transform', open && 'rotate-180')} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8, scale: .97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: .97 }} transition={{ duration: 0.13 }}
            className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50">
            {VIEWS.map(v => (
              <button key={v} onClick={() => { setView(v); setOpen(false); }}
                className={clsx('w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors',
                  view === v ? 'text-primary-600 font-semibold bg-primary-50' : 'text-slate-700 hover:bg-slate-50')}>
                {v}
                {view === v && <Check size={14} />}
              </button>
            ))}
            <div className="border-t border-slate-100 my-1" />
            {[['Compact view', compact, setCompact], ['Expand', expand, setExpand]].map(([label, val, setter]) => (
              <div key={label} className="flex items-center justify-between px-4 py-2.5">
                <span className="text-sm text-slate-600">{label}</span>
                <button onClick={() => setter(v => !v)}
                  className={`w-9 h-5 rounded-full transition-colors relative ${val ? 'bg-primary-500' : 'bg-slate-200'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${val ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── FilterPanel ─────────────────────────────────────────────────────────────

function FilterPanel({ open, onClose, filterStatuses, setFilterStatuses, filterTypes, setFilterTypes, filterSortBy, setFilterSortBy, filterPublished, setFilterPublished }) {
  const shiftTypes = useShiftTypes();
  const [tab, setTab] = useState('status');
  const [search, setSearch] = useState('');
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, onClose]);

  const toggleStatus = (v) => setFilterStatuses(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  const toggleType   = (v) => setFilterTypes(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const activeCount = filterStatuses.length + filterTypes.length + (filterSortBy !== 'name' ? 1 : 0) + (filterPublished !== 'all' ? 1 : 0);

  const tabs = [
    { id: 'status', label: 'Shift Status' },
    { id: 'types',  label: 'Shift Types' },
    { id: 'sort',   label: 'Sort By' },
    { id: 'other',  label: 'Others' },
  ];

  if (!open) return null;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: -10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.97 }}
      transition={{ duration: 0.15 }}
      className="absolute left-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-900">Filters</span>
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-primary-500 text-white text-[10px] font-bold">
              {activeCount}
            </span>
          )}
        </div>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
          <X size={14} />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-2.5 border-b border-slate-50">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full pl-7 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-primary-400 bg-slate-50 placeholder:text-slate-400"
            placeholder="Search filters…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-100 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={clsx('px-3 py-2.5 text-xs font-semibold whitespace-nowrap flex-1 transition-colors border-b-2',
              tab === t.id
                ? 'text-primary-600 border-primary-500 bg-primary-50/50'
                : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-50')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-4 max-h-60 overflow-y-auto">
        {tab === 'status' && (
          <div className="space-y-1">
            {STATUSES.filter(s => !search || s.label.toLowerCase().includes(search.toLowerCase())).map(s => (
              <label key={s.value} className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-slate-50 cursor-pointer group">
                <div
                  onClick={() => toggleStatus(s.value)}
                  className={clsx('w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer shrink-0',
                    filterStatuses.includes(s.value)
                      ? 'border-transparent'
                      : 'border-slate-300 group-hover:border-slate-400'
                  )}
                  style={filterStatuses.includes(s.value) ? { backgroundColor: s.color, borderColor: s.color } : {}}
                >
                  {filterStatuses.includes(s.value) && <Check size={10} className="text-white" />}
                </div>
                <div className="flex items-center gap-2 flex-1">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-sm text-slate-700">{s.label}</span>
                </div>
              </label>
            ))}
          </div>
        )}

        {tab === 'types' && (
          <div className="space-y-1">
            {shiftTypes.filter(t => !search || t.name.toLowerCase().includes(search.toLowerCase())).map(t => {
              const c = hashColor(t.name);
              return (
                <label key={t.id} className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-slate-50 cursor-pointer group">
                  <div
                    onClick={() => toggleType(t.name)}
                    className={clsx('w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer shrink-0',
                      filterTypes.includes(t.name) ? 'border-transparent' : 'border-slate-300 group-hover:border-slate-400'
                    )}
                    style={filterTypes.includes(t.name) ? { backgroundColor: c.border, borderColor: c.border } : {}}
                  >
                    {filterTypes.includes(t.name) && <Check size={10} className="text-white" />}
                  </div>
                  <div className="flex items-center gap-2 flex-1">
                    <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: c.border }} />
                    <span className="text-sm text-slate-700">{t.name}</span>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {tab === 'sort' && (
          <div className="space-y-1">
            {[
              { value: 'name',   label: 'Name (A – Z)' },
              { value: 'hours',  label: 'Hours (High – Low)' },
              { value: 'shifts', label: 'Shifts Count' },
            ].map(opt => (
              <label key={opt.value} onClick={() => setFilterSortBy(opt.value)}
                className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-slate-50 cursor-pointer">
                <div className={clsx('w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all shrink-0',
                  filterSortBy === opt.value ? 'border-primary-500' : 'border-slate-300')}>
                  {filterSortBy === opt.value && <div className="w-2 h-2 rounded-full bg-primary-500" />}
                </div>
                <span className="text-sm text-slate-700">{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {tab === 'other' && (
          <div className="space-y-1">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2 px-2">Publication Status</p>
            {[
              { value: 'all',         label: 'All Shifts' },
              { value: 'published',   label: 'Published Only' },
              { value: 'unpublished', label: 'Unpublished Only' },
            ].map(opt => (
              <label key={opt.value} onClick={() => setFilterPublished(opt.value)}
                className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-slate-50 cursor-pointer">
                <div className={clsx('w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all shrink-0',
                  filterPublished === opt.value ? 'border-primary-500' : 'border-slate-300')}>
                  {filterPublished === opt.value && <div className="w-2 h-2 rounded-full bg-primary-500" />}
                </div>
                <span className="text-sm text-slate-700">{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50">
        <button
          onClick={() => { setFilterStatuses([]); setFilterTypes([]); setFilterSortBy('name'); setFilterPublished('all'); }}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          <RotateCcw size={12} /> Reset Filters
        </button>
        <button onClick={onClose}
          className="px-4 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-xs font-semibold transition-colors">
          Apply
        </button>
      </div>
    </motion.div>
  );
}

// ─── DatePickerPopup ─────────────────────────────────────────────────────────

function DatePickerPopup({ open, currentDate, onSelect, onClose }) {
  const [pickerDate, setPickerDate] = useState(currentDate);
  const ref = useRef();

  useEffect(() => { setPickerDate(currentDate); }, [currentDate]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, onClose]);

  if (!open) return null;

  const monthStart = startOfMonth(pickerDate);
  const monthEnd = endOfMonth(pickerDate);
  const weekStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const days = [];
  let cur = weekStart;
  while (cur <= monthEnd || days.length % 7 !== 0) {
    days.push(cur);
    cur = addDays(cur, 1);
    if (days.length > 42) break;
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: -8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.97 }}
      transition={{ duration: 0.13 }}
      className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 overflow-hidden"
    >
      {/* Month nav */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <button onClick={() => setPickerDate(d => addMonths(d, -1))}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
          <ChevronLeft size={15} />
        </button>
        <span className="text-sm font-bold text-slate-900">{format(pickerDate, 'MMMM yyyy')}</span>
        <button onClick={() => setPickerDate(d => addMonths(d, 1))}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
          <ChevronRight size={15} />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 px-3 pt-3">
        {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => (
          <div key={d} className="text-center text-[11px] font-semibold text-slate-400 pb-2">{d}</div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7 px-3 pb-4 gap-y-1">
        {days.map((day, i) => {
          const isOtherMonth = day.getMonth() !== pickerDate.getMonth();
          const isSelected = isSameDay(day, currentDate);
          const isCurrent = isToday(day);
          return (
            <button key={i}
              onClick={() => { onSelect(day); onClose(); }}
              className={clsx(
                'h-8 w-full rounded-lg text-sm font-medium transition-all',
                isSelected && 'bg-primary-500 text-white shadow-sm',
                !isSelected && isCurrent && 'bg-primary-50 text-primary-600 font-bold',
                !isSelected && !isCurrent && isOtherMonth && 'text-slate-300 hover:bg-slate-50',
                !isSelected && !isCurrent && !isOtherMonth && 'text-slate-700 hover:bg-slate-100',
              )}>
              {format(day, 'd')}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-4 pb-3 border-t border-slate-100 pt-2">
        <button onClick={() => { onSelect(new Date()); onClose(); }}
          className="w-full py-2 text-xs font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
          Go to Today
        </button>
      </div>
    </motion.div>
  );
}

// ─── GridShell (shared header + structure) ────────────────────────────────────

function GridShell({ days, rows, compact, expand, onShiftClick, onAdd, onContextMenu, showStaff, getShiftsForCell, getCellId }) {
  const colCount = days.length;
  const leftW = 124;
  // Multi-week horizontal views (10-Day, Fortnightly) fit all columns on screen without a
  // horizontal scrollbar: a low floor lets the 1fr tracks stretch to fill the board, and the
  // dense card renders at that narrow width. Weekly/Daily flex to fill; Monthly stays dense.
  const colMinW = colCount > 7 && colCount <= 16 ? 60 : 80;

  return (
    <div className="flex-1 overflow-auto rounded-b-xl ring-1 ring-slate-200/70 bg-white scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${leftW}px repeat(${colCount}, minmax(${colMinW}px, 1fr))`,
          minWidth: leftW + colCount * colMinW,
        }}
      >
        {/* Header corner */}
        <div className="sticky top-0 left-0 z-30 bg-white border-r-2 border-b border-slate-100 shadow-[6px_6px_12px_-10px_rgba(15,23,42,0.2)] px-3 py-2.5 flex items-center" />

        {/* Day headers */}
        {days.map(day => {
          const today = isToday(day);
          const isWknd = isWeekendDay(day);
          const isHoliday = isPublicHoliday(day);
          return (
            <div key={day.toString()}
              className={clsx('sticky top-0 z-20 border-b-2 border-r border-slate-200 text-center shadow-[0_6px_12px_-10px_rgba(15,23,42,0.35)] backdrop-blur-sm', colCount > 7 ? 'py-1' : 'py-2',
                today ? 'border-b-primary-500 bg-primary-50/30' :
                isHoliday ? 'border-b-amber-300 bg-amber-50' :
                isWknd ? 'border-b-slate-300 bg-slate-100/70' :
                'border-b-slate-200 bg-white')}>
              <p className={clsx('text-[10px] font-bold uppercase tracking-widest',
                today ? 'text-primary-500' : isHoliday ? 'text-amber-600' : isWknd ? 'text-slate-500' : 'text-slate-400')}>
                {format(day, 'EEE')}
              </p>
              <span className={clsx(
                'inline-flex items-center justify-center mt-0.5 font-black leading-none',
                today ? (colCount > 7 ? 'w-6 h-6 rounded-full bg-primary-500 text-white text-xs shadow-sm' : 'w-7 h-7 rounded-full bg-primary-500 text-white text-sm shadow-sm') :
                isHoliday ? (colCount > 7 ? 'text-amber-700 text-base' : 'text-amber-700 text-xl') :
                isWknd ? (colCount > 7 ? 'text-slate-600 text-base' : 'text-slate-600 text-xl') : (colCount > 7 ? 'text-slate-800 text-base' : 'text-xl text-slate-800')
              )}>
                {format(day, 'd')}
              </span>
              {isHoliday && !today && (
                <p className="text-[8px] text-amber-500 font-bold leading-none mt-0.5 truncate px-1">PH</p>
              )}
            </div>
          );
        })}

        {/* Rows */}
        {rows.map((row) => (
          <React.Fragment key={row.id}>
            {row.leftCell}
            {days.map(day => (
              <DayCell
                key={day.toString()}
                id={getCellId(row.id, day)}
                shifts={getShiftsForCell(row.id, day)}
                date={day}
                compact={compact}
                expand={expand}
                showStaff={showStaff}
                onAdd={() => onAdd(day, row.entityId)}
                onShiftClick={onShiftClick}
                onContextMenu={onContextMenu}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ─── StaffGrid ────────────────────────────────────────────────────────────────

function getTodayEvvStatus(staffId, shifts) {
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const now = new Date();
  const started = shifts.filter(s =>
    s.staff_id === staffId &&
    s.shift_date === todayStr &&
    !['cancelled', 'cancelled_with_charge', 'declined'].includes(s.status) &&
    new Date(`${s.shift_date}T${s.start_time}`) <= now
  );
  if (started.length === 0) return null;
  const clocked = started.find(s => s.clock_in_time);
  if (clocked) {
    const lateMin = Math.round((new Date(clocked.clock_in_time) - new Date(`${clocked.shift_date}T${clocked.start_time}`)) / 60000);
    return lateMin > 5 ? { type: 'late', minutes: lateMin } : { type: 'ontime' };
  }
  return { type: 'missing' };
}

function StaffGrid({ days, staff, shifts, compact, expand, onShiftClick, onAdd, onContextMenu, search, sortBy, leaveMap = {}, bulkMode, selectedIds, onToggleSelect }) {
  const isOnLeave = (staffId, day) => {
    const leaves = leaveMap[staffId] || [];
    const dayStr = format(day, 'yyyy-MM-dd');
    return leaves.some(l => dayStr >= l.start.slice(0,10) && dayStr <= l.end.slice(0,10));
  };

  const getShiftsForCell = (staffId, day) =>
    shifts.filter(s => {
      if (!isSameDayStr(s.shift_date, day)) return false;
      if (staffId === 'vacant')   return !s.staff_id && !s.is_on_job_board;
      if (staffId === 'jobboard') return !!s.is_on_job_board && !s.staff_id;
      return s.staff_id === staffId;
    });

  const getStaffStats = (staffId) => {
    const seen = new Set();
    const ss = shifts.filter(s => {
      if (s.staff_id !== staffId || s.status === 'cancelled') return false;
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    });
    const totalHours = ss.reduce((acc, s) => acc + calcHours(s.start_time, s.end_time), 0);
    const totalKm = ss.reduce((acc, s) => acc + (Number(s.travel_km) || 0) + (Number(s.transport_km) || 0), 0);
    const sleepovers = ss.filter(s => s.shift_type?.toLowerCase().includes('sleepover')).length;
    return { totalHours, totalKm, sleepovers };
  };

  const vacantShifts = shifts.filter(s => !s.staff_id);

  let filteredStaff = staff.filter(s =>
    !search || `${s.first_name} ${s.last_name}`.toLowerCase().includes(search.toLowerCase())
  );

  if (sortBy === 'hours') {
    filteredStaff = [...filteredStaff].sort((a, b) => {
      const ha = getStaffStats(a.id).totalHours;
      const hb = getStaffStats(b.id).totalHours;
      return hb - ha;
    });
  } else if (sortBy === 'shifts') {
    filteredStaff = [...filteredStaff].sort((a, b) => {
      const sa = shifts.filter(s => s.staff_id === a.id).length;
      const sb = shifts.filter(s => s.staff_id === b.id).length;
      return sb - sa;
    });
  }

  const colCount = days.length;
  const leftW = 124;
  // Multi-week horizontal views (10-Day, Fortnightly) fit all columns on screen without a
  // horizontal scrollbar: a low floor lets the 1fr tracks stretch to fill the board, and the
  // dense card renders at that narrow width. Weekly/Daily flex to fill; Monthly stays dense.
  const colMinW = colCount > 7 && colCount <= 16 ? 60 : 80;

  return (
    <div className="flex-1 overflow-auto rounded-b-xl ring-1 ring-slate-200/70 bg-white scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${leftW}px repeat(${colCount}, minmax(${colMinW}px, 1fr))`,
          minWidth: leftW + colCount * colMinW,
        }}
      >
        {/* Header corner */}
        <div className="sticky top-0 left-0 z-30 bg-white border-r-2 border-b border-slate-100 shadow-[6px_6px_12px_-10px_rgba(15,23,42,0.2)] px-2.5 py-2.5 flex items-center">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Staff</span>
        </div>

        {days.map(day => {
          const today = isToday(day);
          const isWknd = isWeekendDay(day);
          const isHoliday = isPublicHoliday(day);
          return (
            <div key={day.toString()}
              className={clsx('sticky top-0 z-20 border-b-2 border-r border-slate-200 text-center shadow-[0_6px_12px_-10px_rgba(15,23,42,0.35)] backdrop-blur-sm', colCount > 7 ? 'py-1' : 'py-2',
                today ? 'border-b-primary-500 bg-primary-50/30' :
                isHoliday ? 'border-b-amber-300 bg-amber-50' :
                isWknd ? 'border-b-slate-300 bg-slate-100/70' :
                'border-b-slate-200 bg-white')}>
              <p className={clsx('text-[10px] font-bold uppercase tracking-widest',
                today ? 'text-primary-500' : isHoliday ? 'text-amber-600' : isWknd ? 'text-slate-500' : 'text-slate-400')}>
                {format(day, 'EEE')}
              </p>
              <span className={clsx(
                'inline-flex items-center justify-center mt-0.5 font-black leading-none',
                today ? (colCount > 7 ? 'w-6 h-6 rounded-full bg-primary-500 text-white text-xs shadow-sm' : 'w-7 h-7 rounded-full bg-primary-500 text-white text-sm shadow-sm') :
                isHoliday ? (colCount > 7 ? 'text-amber-700 text-base' : 'text-amber-700 text-xl') :
                isWknd ? (colCount > 7 ? 'text-slate-600 text-base' : 'text-slate-600 text-xl') : (colCount > 7 ? 'text-slate-800 text-base' : 'text-xl text-slate-800')
              )}>
                {format(day, 'd')}
              </span>
              {isHoliday && !today && (
                <p className="text-[8px] text-amber-500 font-bold leading-none mt-0.5 truncate px-1">PH</p>
              )}
            </div>
          );
        })}

        {/* Vacant row */}
        <div className="sticky left-0 z-20 bg-white border-r-2 border-b border-slate-100 shadow-[8px_0_14px_-10px_rgba(15,23,42,0.14)] px-2 py-1.5 flex items-center gap-1.5 min-h-[56px]">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-[9px] font-black shrink-0">VS</div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-amber-700 leading-tight truncate">Vacant</p>
            <p className="text-[10px] font-semibold text-amber-500 leading-tight">
              {vacantShifts.length > 0 ? `${vacantShifts.length} open` : 'All assigned'}
            </p>
          </div>
        </div>
        {days.map(day => (
          <DayCell key={day.toString()} id={`vacant_${format(day, 'yyyy-MM-dd')}`}
            shifts={getShiftsForCell('vacant', day)} date={day} compact={compact} expand={expand}
            onAdd={() => onAdd(day, null)} onShiftClick={onShiftClick} onContextMenu={onContextMenu}
            bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
        ))}

        {/* Job Board row */}
        <div className="sticky left-0 z-20 bg-white border-r-2 border-b border-slate-100 shadow-[8px_0_14px_-10px_rgba(15,23,42,0.14)] px-2 py-1.5 flex items-center gap-1.5 min-h-[56px]">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-[9px] font-black shrink-0">JB</div>
          <div>
            <p className="text-[11px] font-bold text-violet-700 leading-tight">Job Board</p>
            <p className="text-[10px] text-slate-400 leading-tight">Open</p>
          </div>
        </div>
        {days.map(day => (
          <DayCell key={day.toString()} id={`jobboard_${format(day, 'yyyy-MM-dd')}`}
            shifts={getShiftsForCell('jobboard', day)} date={day} compact={compact} expand={expand}
            onAdd={() => onAdd(day, null, null, true)} onShiftClick={onShiftClick} onContextMenu={onContextMenu}
            bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
        ))}

        {/* Staff rows */}
        {filteredStaff.map((member, idx) => {
          const { totalHours, totalKm, sleepovers } = getStaffStats(member.id);
          const overHours = totalHours > 38;
          const nearLimit = totalHours >= 30 && !overHours;
          const avatarColor = rowColorFor(member.id);
          const hoursPct = Math.min((totalHours / 38) * 100, 100);
          const barColor = overHours ? '#ef4444' : nearLimit ? '#f59e0b' : '#10b981';
          const evv = getTodayEvvStatus(member.id, shifts);
          const evvDot = evv ? (evv.type === 'ontime' ? '#16a34a' : evv.type === 'late' ? '#d97706' : '#ef4444') : null;
          // App-login recency dot (top-right): green ≤24h, amber ≤7d, grey older, faint = never.
          const lastLogin = member.last_login_at ? new Date(member.last_login_at) : null;
          const loginAgeH = lastLogin ? (Date.now() - lastLogin.getTime()) / 3600000 : null;
          const loginDot = loginAgeH == null ? '#e2e8f0' : loginAgeH <= 24 ? '#16a34a' : loginAgeH <= 168 ? '#d97706' : '#cbd5e1';
          const loginTitle = lastLogin ? `App login: ${lastLogin.toLocaleString()}` : 'Never logged into the app';
          return (
            <React.Fragment key={member.id}>
              <div className="sticky left-0 z-20 bg-white border-r-2 border-b border-slate-100 shadow-[8px_0_14px_-10px_rgba(15,23,42,0.14)] px-2 py-2 min-h-[64px] flex flex-col items-center text-center gap-1">
                <div className="relative w-8 h-8 shrink-0">
                  <Avatar photo={member.profile_photo_url} firstName={member.first_name} lastName={member.last_name}
                    size="w-8 h-8" rounded="rounded-full" text="text-[10px] font-black"
                    gradient={`linear-gradient(135deg, ${avatarColor}bb, ${avatarColor})`} />
                  {evvDot && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white"
                      style={{ backgroundColor: evvDot }} />
                  )}
                  <span title={loginTitle} className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white"
                    style={{ backgroundColor: loginDot }} />
                </div>
                <div className="min-w-0 w-full">
                  <RouterLink to={`/staff/${member.id}`} title="View staff profile" className="block text-[11px] font-bold text-slate-900 truncate leading-tight hover:text-indigo-600 hover:underline">{member.first_name} {member.last_name}</RouterLink>
                  <p className={clsx('text-[10px] font-semibold leading-tight mt-0.5', overHours ? 'text-red-500' : nearLimit ? 'text-amber-500' : 'text-slate-400')}>
                    {totalHours.toFixed(1)} hrs{sleepovers > 0 && <span className="text-violet-500 ml-1">·{sleepovers}sl</span>}{evv?.type === 'late' && <span className="text-[8px] font-bold text-amber-600 ml-1">+{evv.minutes}m</span>}
                  </p>
                  <p className="text-[10px] font-medium text-slate-400 leading-tight">{(totalKm || 0).toFixed(0)} KM</p>
                  <div className="w-full h-0.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full rounded-full transition-all" style={{ width: `${hoursPct}%`, backgroundColor: barColor }} />
                  </div>
                </div>
              </div>
              {days.map(day => (
                <DayCell key={day.toString()} id={`${member.id}_${format(day, 'yyyy-MM-dd')}`}
                  shifts={getShiftsForCell(member.id, day)} date={day} compact={compact} expand={expand}
                  onAdd={() => onAdd(day, member.id)} onShiftClick={onShiftClick} onContextMenu={onContextMenu}
                  onLeave={isOnLeave(member.id, day)}
                  bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
              ))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── ClientGrid ───────────────────────────────────────────────────────────────

function ClientGrid({ days, clients, shifts, compact, expand, onShiftClick, onAdd, onContextMenu, search, sortBy, bulkMode, selectedIds, onToggleSelect }) {
  const clientIdsOf = (s) => s.client_ids ? s.client_ids.split(',').map(id => id.trim()) : [];

  const getShiftsForCell = (clientId, day) =>
    shifts.filter(s => {
      if (!isSameDayStr(s.shift_date, day)) return false;
      return clientIdsOf(s).includes(clientId);
    });

  const getClientStats = (clientId) => {
    const seen = new Set();
    const cs = shifts.filter(s => {
      if (s.status === 'cancelled') return false;
      if (!clientIdsOf(s).includes(clientId)) return false;
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    });
    const totalHours = cs.reduce((acc, s) => acc + calcHours(s.start_time, s.end_time), 0);
    const totalKm = cs.reduce((acc, s) => acc + (Number(s.travel_km) || 0) + (Number(s.transport_km) || 0), 0);
    return { totalHours, totalKm, shiftCount: cs.length };
  };

  let filteredClients = clients.filter(c =>
    !search || `${c.first_name} ${c.last_name}`.toLowerCase().includes(search.toLowerCase())
  );

  if (sortBy === 'hours') {
    filteredClients = [...filteredClients].sort((a, b) => getClientStats(b.id).totalHours - getClientStats(a.id).totalHours);
  } else if (sortBy === 'shifts') {
    filteredClients = [...filteredClients].sort((a, b) => getClientStats(b.id).shiftCount - getClientStats(a.id).shiftCount);
  }

  const colCount = days.length;
  const leftW = 124;
  // Multi-week horizontal views (10-Day, Fortnightly) fit all columns on screen without a
  // horizontal scrollbar: a low floor lets the 1fr tracks stretch to fill the board, and the
  // dense card renders at that narrow width. Weekly/Daily flex to fill; Monthly stays dense.
  const colMinW = colCount > 7 && colCount <= 16 ? 60 : 80;

  const TYPE_COLORS = { self_managed: '#6366f1', plan_managed: '#8b5cf6', ndia_managed: '#10b981', ndis_managed: '#10b981', aged_care: '#f59e0b', private: '#64748b' };
  // Which programme funds this person. Aged care and NDIS are funded, priced and claimed under
  // completely different rules, so a coordinator scanning the board needs to know which applies
  // before they book — not after the shift form corrects them. Kept to two short words and a quiet
  // tint: the board is dense, and this is a label, not a decoration.
  const programmeOf = (c) => {
    if (c.service_type === 'aged_care' || c.client_type === 'aged_care') return { label: 'Aged care', cls: 'bg-amber-50 text-amber-700 ring-amber-200' };
    if (c.client_type === 'private') return { label: 'Private', cls: 'bg-slate-100 text-slate-600 ring-slate-200' };
    return { label: 'NDIS', cls: 'bg-teal-50 text-teal-700 ring-teal-200' };
  };

  return (
    <div className="flex-1 overflow-auto rounded-b-xl ring-1 ring-slate-200/70 bg-white scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${leftW}px repeat(${colCount}, minmax(${colMinW}px, 1fr))`,
          minWidth: leftW + colCount * colMinW,
        }}
      >
        {/* Header corner */}
        <div className="sticky top-0 left-0 z-30 bg-white border-r-2 border-b border-slate-100 shadow-[6px_6px_12px_-10px_rgba(15,23,42,0.2)] px-2.5 py-2.5 flex items-center">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Clients</span>
        </div>

        {days.map(day => {
          const today = isToday(day);
          const isWknd = isWeekendDay(day);
          const isHoliday = isPublicHoliday(day);
          return (
            <div key={day.toString()}
              className={clsx('sticky top-0 z-20 border-b-2 border-r border-slate-200 text-center shadow-[0_6px_12px_-10px_rgba(15,23,42,0.35)] backdrop-blur-sm', colCount > 7 ? 'py-1' : 'py-2',
                today ? 'border-b-primary-500 bg-primary-50/30' :
                isHoliday ? 'border-b-amber-300 bg-amber-50' :
                isWknd ? 'border-b-slate-300 bg-slate-100/70' :
                'border-b-slate-200 bg-white')}>
              <p className={clsx('text-[10px] font-bold uppercase tracking-widest',
                today ? 'text-primary-500' : isHoliday ? 'text-amber-600' : isWknd ? 'text-slate-500' : 'text-slate-400')}>
                {format(day, 'EEE')}
              </p>
              <span className={clsx(
                'inline-flex items-center justify-center mt-0.5 font-black leading-none',
                today ? (colCount > 7 ? 'w-6 h-6 rounded-full bg-primary-500 text-white text-xs shadow-sm' : 'w-7 h-7 rounded-full bg-primary-500 text-white text-sm shadow-sm') :
                isHoliday ? (colCount > 7 ? 'text-amber-700 text-base' : 'text-amber-700 text-xl') :
                isWknd ? (colCount > 7 ? 'text-slate-600 text-base' : 'text-slate-600 text-xl') : (colCount > 7 ? 'text-slate-800 text-base' : 'text-xl text-slate-800')
              )}>
                {format(day, 'd')}
              </span>
              {isHoliday && !today && (
                <p className="text-[8px] text-amber-500 font-bold leading-none mt-0.5 truncate px-1">PH</p>
              )}
            </div>
          );
        })}

        {/* Client rows */}
        {filteredClients.map((client, idx) => {
          const { totalHours, totalKm, shiftCount } = getClientStats(client.id);
          const avatarColor = TYPE_COLORS[client.client_type] || rowColorFor(client.id);
          return (
            <React.Fragment key={client.id}>
              <div className="sticky left-0 z-20 bg-white border-r-2 border-b border-slate-100 shadow-[8px_0_14px_-10px_rgba(15,23,42,0.14)] px-2 py-2 min-h-[64px] flex flex-col items-center text-center gap-1">
                <Avatar photo={client.profile_photo_url} firstName={client.first_name} lastName={client.last_name}
                  size="w-8 h-8" rounded="rounded-lg" text="text-[10px] font-black"
                  gradient={`linear-gradient(135deg, ${avatarColor}bb, ${avatarColor})`} />
                <div className="min-w-0 w-full">
                  <RouterLink to={`/clients/${client.id}`} title="View client profile" className="block text-[11px] font-bold text-slate-900 truncate leading-tight hover:text-indigo-600 hover:underline">{client.first_name} {client.last_name}</RouterLink>
                  {(() => { const pg = programmeOf(client); return (
                    <span className={`inline-block mt-0.5 px-1 py-px rounded text-[9px] font-semibold leading-tight ring-1 ${pg.cls}`}>{pg.label}</span>
                  ); })()}
                  <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{totalHours.toFixed(1)} hrs · {shiftCount}sh</p>
                  <p className="text-[10px] font-medium text-slate-400 leading-tight">{(totalKm || 0).toFixed(0)} KM</p>
                  {/* Brevity-style per-row quick links: funding + services/calendar */}
                  <div className="flex items-center justify-center gap-1.5 mt-0.5">
                    <RouterLink to={`/clients/${client.id}`} title="Funding" className="text-slate-300 hover:text-emerald-600"><CreditCard size={11} /></RouterLink>
                    <RouterLink to={`/clients/${client.id}/calendar`} title="Client services calendar" className="text-slate-300 hover:text-indigo-600"><Calendar size={11} /></RouterLink>
                  </div>
                </div>
              </div>
              {days.map(day => (
                <DayCell key={day.toString()} id={`client_${client.id}_${format(day, 'yyyy-MM-dd')}`}
                  shifts={getShiftsForCell(client.id, day)} date={day} compact={compact} expand={expand}
                  showStaff
                  onAdd={() => onAdd(day, null, client.id)} onShiftClick={onShiftClick} onContextMenu={onContextMenu}
                  bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
              ))}
            </React.Fragment>
          );
        })}

        {filteredClients.length === 0 && (
          <div className="col-span-full flex items-center justify-center py-16 text-sm text-slate-400">
            No clients found
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FacilitiesGrid ───────────────────────────────────────────────────────────

function FacilitiesGrid({ days, facilities, shifts, compact, expand, onShiftClick, onAdd, onContextMenu, search, bulkMode, selectedIds, onToggleSelect }) {
  const getShiftsForCell = (facilityId, day) =>
    shifts.filter(s => {
      if (!isSameDayStr(s.shift_date, day)) return false;
      return s.facility_id === facilityId;
    });

  const getFacilityStats = (facilityId) => {
    const fs = shifts.filter(s => s.facility_id === facilityId);
    return { shiftCount: fs.length };
  };

  const filteredFacilities = facilities.filter(f =>
    !search || f.name?.toLowerCase().includes(search.toLowerCase())
  );

  const colCount = days.length;
  const leftW = 124;
  // Multi-week horizontal views (10-Day, Fortnightly) fit all columns on screen without a
  // horizontal scrollbar: a low floor lets the 1fr tracks stretch to fill the board, and the
  // dense card renders at that narrow width. Weekly/Daily flex to fill; Monthly stays dense.
  const colMinW = colCount > 7 && colCount <= 16 ? 60 : 80;

  const FAC_COLORS = ['#0ea5e9', '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="flex-1 overflow-auto rounded-b-xl ring-1 ring-slate-200/70 bg-white scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${leftW}px repeat(${colCount}, minmax(${colMinW}px, 1fr))`,
          minWidth: leftW + colCount * colMinW,
        }}
      >
        {/* Header corner */}
        <div className="sticky top-0 left-0 z-30 bg-white border-r-2 border-b border-slate-100 shadow-[6px_6px_12px_-10px_rgba(15,23,42,0.2)] px-2.5 py-2.5 flex items-center">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Facilities</span>
        </div>

        {days.map(day => {
          const today = isToday(day);
          const isWknd = isWeekendDay(day);
          const isHoliday = isPublicHoliday(day);
          return (
            <div key={day.toString()}
              className={clsx('sticky top-0 z-20 border-b-2 border-r border-slate-200 text-center shadow-[0_6px_12px_-10px_rgba(15,23,42,0.35)] backdrop-blur-sm', colCount > 7 ? 'py-1' : 'py-2',
                today ? 'border-b-primary-500 bg-primary-50/30' :
                isHoliday ? 'border-b-amber-300 bg-amber-50' :
                isWknd ? 'border-b-slate-300 bg-slate-100/70' :
                'border-b-slate-200 bg-white')}>
              <p className={clsx('text-[10px] font-bold uppercase tracking-widest',
                today ? 'text-primary-500' : isHoliday ? 'text-amber-600' : isWknd ? 'text-slate-500' : 'text-slate-400')}>
                {format(day, 'EEE')}
              </p>
              <span className={clsx(
                'inline-flex items-center justify-center mt-0.5 font-black leading-none',
                today ? (colCount > 7 ? 'w-6 h-6 rounded-full bg-primary-500 text-white text-xs shadow-sm' : 'w-7 h-7 rounded-full bg-primary-500 text-white text-sm shadow-sm') :
                isHoliday ? (colCount > 7 ? 'text-amber-700 text-base' : 'text-amber-700 text-xl') :
                isWknd ? (colCount > 7 ? 'text-slate-600 text-base' : 'text-slate-600 text-xl') : (colCount > 7 ? 'text-slate-800 text-base' : 'text-xl text-slate-800')
              )}>
                {format(day, 'd')}
              </span>
              {isHoliday && !today && (
                <p className="text-[8px] text-amber-500 font-bold leading-none mt-0.5 truncate px-1">PH</p>
              )}
            </div>
          );
        })}

        {/* Facility rows */}
        {filteredFacilities.map((facility, idx) => {
          const { shiftCount } = getFacilityStats(facility.id);
          const avatarColor = FAC_COLORS[idx % FAC_COLORS.length];
          return (
            <React.Fragment key={facility.id}>
              <div className="sticky left-0 z-20 bg-white border-r-2 border-b border-slate-100 shadow-[8px_0_14px_-10px_rgba(15,23,42,0.14)] px-2 py-1.5 min-h-[64px] flex items-center gap-1.5">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0"
                  style={{ background: `linear-gradient(135deg, ${avatarColor}bb, ${avatarColor})` }}>
                  <Building2 size={12} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold text-slate-900 truncate leading-tight">{facility.name}</p>
                  <p className="text-[10px] text-slate-400 leading-tight">{shiftCount} shift{shiftCount !== 1 ? 's' : ''}</p>
                  {facility.address && (
                    <p className="text-[9px] text-slate-300 truncate leading-tight flex items-center gap-0.5">
                      <MapPin size={7} />{facility.address}
                    </p>
                  )}
                </div>
              </div>
              {days.map(day => (
                <DayCell key={day.toString()} id={`facility_${facility.id}_${format(day, 'yyyy-MM-dd')}`}
                  shifts={getShiftsForCell(facility.id, day)} date={day} compact={compact} expand={expand}
                  onAdd={() => onAdd(day, null)} onShiftClick={onShiftClick} onContextMenu={onContextMenu}
                  bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
              ))}
            </React.Fragment>
          );
        })}

        {filteredFacilities.length === 0 && (
          <div className="col-span-full flex items-center justify-center py-16 text-sm text-slate-400">
            No facilities found. Add facilities in the Facilities section.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── GroupGrid (By-Group view) ────────────────────────────────────────────────
// Rows = group homes (+ an "Ad-hoc Groups" row for group shifts with no home).
// Cells = group shifts (is_group). Clicking opens the GroupShiftModal (participant
// management); Add creates a new group shift. Reuses DayCell/ShiftCard verbatim.
function GroupGrid({ days, groupHomes, shifts, compact, expand, onShiftClick, onAdd, onContextMenu, search, bulkMode, selectedIds, onToggleSelect }) {
  const groupShifts = shifts.filter(s => s.is_group);
  const getShiftsForCell = (homeId, day) =>
    groupShifts.filter(s => isSameDayStr(s.shift_date, day) && (homeId === '__adhoc__' ? !s.group_home_id : s.group_home_id === homeId));

  const rows = [
    ...groupHomes.filter(h => !search || h.name?.toLowerCase().includes(search.toLowerCase())),
    { id: '__adhoc__', name: 'Ad-hoc Groups', color: '#64748b' },
  ];

  const colCount = days.length;
  const leftW = 124;
  // Multi-week horizontal views (10-Day, Fortnightly) fit all columns on screen without a
  // horizontal scrollbar: a low floor lets the 1fr tracks stretch to fill the board, and the
  // dense card renders at that narrow width. Weekly/Daily flex to fill; Monthly stays dense.
  const colMinW = colCount > 7 && colCount <= 16 ? 60 : 80;

  return (
    <div className="flex-1 overflow-auto rounded-b-xl ring-1 ring-slate-200/70 bg-white scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `${leftW}px repeat(${colCount}, minmax(${colMinW}px, 1fr))`, minWidth: leftW + colCount * colMinW }}>
        <div className="sticky top-0 left-0 z-30 bg-white border-r-2 border-b border-slate-100 shadow-[6px_6px_12px_-10px_rgba(15,23,42,0.2)] px-2.5 py-2.5 flex items-center">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Groups</span>
        </div>
        {days.map(day => {
          const today = isToday(day); const isWknd = isWeekendDay(day); const isHoliday = isPublicHoliday(day);
          return (
            <div key={day.toString()} className={clsx('sticky top-0 z-20 border-b-2 border-r border-slate-200 text-center shadow-[0_6px_12px_-10px_rgba(15,23,42,0.35)] backdrop-blur-sm', colCount > 7 ? 'py-1' : 'py-2',
              today ? 'border-b-primary-500 bg-primary-50/30' : isHoliday ? 'border-b-amber-300 bg-amber-50' : isWknd ? 'border-b-slate-300 bg-slate-100/70' : 'border-b-slate-200 bg-white')}>
              <p className={clsx('text-[10px] font-bold uppercase tracking-widest', today ? 'text-primary-500' : isHoliday ? 'text-amber-600' : isWknd ? 'text-slate-500' : 'text-slate-400')}>{format(day, 'EEE')}</p>
              <span className={clsx('inline-flex items-center justify-center mt-0.5 font-black leading-none',
                today ? (colCount > 7 ? 'w-6 h-6 rounded-full bg-primary-500 text-white text-xs shadow-sm' : 'w-7 h-7 rounded-full bg-primary-500 text-white text-sm shadow-sm') : isHoliday ? (colCount > 7 ? 'text-amber-700 text-base' : 'text-amber-700 text-xl') : isWknd ? (colCount > 7 ? 'text-slate-600 text-base' : 'text-slate-600 text-xl') : (colCount > 7 ? 'text-slate-800 text-base' : 'text-xl text-slate-800'))}>{format(day, 'd')}</span>
            </div>
          );
        })}

        {rows.map(home => {
          const homeShiftCount = groupShifts.filter(s => home.id === '__adhoc__' ? !s.group_home_id : s.group_home_id === home.id).length;
          if (home.id === '__adhoc__' && homeShiftCount === 0) return null;
          return (
            <React.Fragment key={home.id}>
              <div className="sticky left-0 z-20 bg-white border-r-2 border-b border-slate-100 shadow-[8px_0_14px_-10px_rgba(15,23,42,0.14)] px-2 py-1.5 min-h-[64px] flex items-center gap-1.5">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: `linear-gradient(135deg, ${(home.color || '#0891b2')}bb, ${home.color || '#0891b2'})` }}>
                  <Home size={12} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold text-slate-900 truncate leading-tight">{home.name}</p>
                  <p className="text-[10px] text-slate-400 leading-tight">{homeShiftCount} group shift{homeShiftCount !== 1 ? 's' : ''}</p>
                  {home.suburb && <p className="text-[9px] text-slate-300 truncate leading-tight flex items-center gap-0.5"><MapPin size={7} />{home.suburb}</p>}
                </div>
              </div>
              {days.map(day => (
                <DayCell key={day.toString()} id={`group_${home.id}_${format(day, 'yyyy-MM-dd')}`}
                  shifts={getShiftsForCell(home.id, day)} date={day} compact={compact} expand={expand}
                  onAdd={() => onAdd(day, null)} onShiftClick={onShiftClick} onContextMenu={onContextMenu}
                  bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
              ))}
            </React.Fragment>
          );
        })}

        {groupHomes.length === 0 && groupShifts.length === 0 && (
          <div className="col-span-full flex items-center justify-center py-16 text-sm text-slate-400">
            No group homes or group shifts yet. Add homes in Group Services, or click a cell to create a group shift.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MonthDayCell — droppable cell for Monthly view ──────────────────────────

function MonthDayCell({ day, shifts, compact, onShiftClick, onAdd, onContextMenu, otherMonth, today, bulkMode, selectedIds, onToggleSelect }) {
  // Use vacant_ prefix so handleDragEnd treats it as unassigned (staff stays the same in month view)
  const dropId = `vacant_${format(day, 'yyyy-MM-dd')}`;
  const { isOver, setNodeRef } = useDroppable({ id: dropId });

  return (
    <div ref={setNodeRef} onClick={() => onAdd(day)}
      className={clsx(
        'border-r border-slate-100 p-2 cursor-pointer group transition-colors relative',
        isOver && 'bg-primary-50 ring-2 ring-inset ring-primary-400',
        !isOver && today && 'bg-blue-50/30',
        !isOver && otherMonth && !today && 'bg-slate-50/60',
        !isOver && !today && !otherMonth && 'hover:bg-slate-50/70',
      )}>
      <span className={clsx(
        'inline-flex w-6 h-6 items-center justify-center rounded-full mb-1 text-sm font-bold',
        today ? 'bg-primary-500 text-white shadow-sm' : otherMonth ? 'text-slate-300' : 'text-slate-700'
      )}>
        {format(day, 'd')}
      </span>
      {shifts.slice(0, compact ? 2 : 3).map(s => (
        <ShiftCard key={s.id} shift={s} compact onClick={onShiftClick} onContextMenu={onContextMenu}
          bulkMode={bulkMode} isSelected={selectedIds?.has(s.id)} onToggleSelect={onToggleSelect} />
      ))}
      {shifts.length > (compact ? 2 : 3) && (
        <p className="text-[10px] text-slate-400 font-semibold mt-0.5">+{shifts.length - (compact ? 2 : 3)} more</p>
      )}
      <div className="absolute bottom-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
          <Plus size={9} className="text-white" />
        </div>
      </div>
    </div>
  );
}

// ─── MonthView ─────────────────────────────────────────────────────────────────

function MonthView({ days, currentDate, shifts, compact, onShiftClick, onAdd, onContextMenu, bulkMode, selectedIds, onToggleSelect }) {
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  const monthNum = currentDate.getMonth();

  return (
    <div className="flex-1 overflow-auto">
      <div className="grid grid-cols-7 border-b border-slate-100 bg-white sticky top-0 z-10">
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d, i) => (
          <div key={d} className={clsx('py-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest border-r border-slate-100', i === 6 && 'border-r-0')}>{d}</div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b border-slate-100" style={{ minHeight: compact ? 80 : 130 }}>
          {week.map(day => {
            const dayShifts = shifts.filter(s => isSameDayStr(s.shift_date, day));
            const otherMonth = day.getMonth() !== monthNum;
            const today = isToday(day);
            return (
              <MonthDayCell
                key={day.toString()}
                day={day}
                shifts={dayShifts}
                compact={compact}
                onShiftClick={onShiftClick}
                onAdd={onAdd}
                onContextMenu={onContextMenu}
                otherMonth={otherMonth}
                today={today}
                bulkMode={bulkMode}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── AutoRosterModal ─────────────────────────────────────────────────────────
// Pattern-driven proposal: each row is a shift inferred from the client's usual days, with
// the best ELIGIBLE worker pre-picked (banned staff excluded, day-availability respected,
// favourites first). The admin can deselect a shift or SWAP the worker (recommended +
// alternatives). Nothing is created until "Create".
function AutoRosterModal({ data, loading, creating, onClose, onCreate }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (data?.shifts) {
      setRows(data.shifts.map(s => ({ ...s, chosenStaffId: s.staffId || '' })));
      setSelected(new Set(data.shifts.map((_, i) => i)));
    }
  }, [data]);

  const toggle = i => setSelected(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const setWorker = (i, staffId) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, chosenStaffId: staffId } : r));

  // Worker options for a row: recommended (top pick) + alternatives — all already filtered
  // for the banned list, day availability, leave and clashes by the backend.
  const optionsFor = (s) => {
    const opts = [];
    if (s.staffId) {
      const rec = s.patternNote && s.reason?.startsWith(s.patternNote) ? s.reason.slice(s.patternNote.length + 3) : (s.reason || '');
      opts.push({ staffId: s.staffId, staffName: s.staffName, reason: rec, recommended: true });
    }
    (s.alternatives || []).forEach(a => { if (a.staffId !== s.staffId) opts.push({ staffId: a.staffId, staffName: a.staffName, reason: a.reason }); });
    return opts;
  };
  const reasonFor = (s) => {
    if (!s.chosenStaffId) return `${s.patternNote || 'Pattern'} · unassigned`;
    const chosen = optionsFor(s).find(o => o.staffId === s.chosenStaffId);
    return `${s.patternNote || ''}${chosen?.reason ? ' · ' + chosen.reason : ''}`.trim();
  };

  const handleCreate = () => onCreate(rows.filter((_, i) => selected.has(i)).map(r => ({ ...r, staffId: r.chosenStaffId || null })));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center shrink-0">
            <Sparkles size={15} className="text-white"/>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800">AI Auto-Roster</h3>
            <p className="text-xs text-slate-400">
              {data ? `${data.shifts?.length || 0} shifts from client patterns · ${data.staffCount} staff` : 'Reading shift patterns…'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16}/></button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {loading && (
            <div className="flex flex-col items-center gap-3 py-16 text-slate-400">
              <Loader2 size={28} className="animate-spin text-violet-500"/>
              <p className="text-sm">Reading each client's usual days + matching available workers…</p>
            </div>
          )}
          {!loading && data && rows.length === 0 && (
            <p className="text-center text-slate-400 py-12 text-sm max-w-md mx-auto">{data.note || 'No shifts could be generated.'}</p>
          )}
          {!loading && rows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-500">Each shift comes from the client's usual pattern. Swap the worker if needed; deselect any you don't want.</p>
                <div className="flex gap-2">
                  <button onClick={() => setSelected(new Set(rows.map((_, i) => i)))} className="text-xs text-violet-600 hover:underline">All</button>
                  <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500 hover:underline">None</button>
                </div>
              </div>
              {rows.map((s, i) => {
                const opts = optionsFor(s);
                return (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${selected.has(i) ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white'}`}>
                  <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} className="mt-1.5 rounded accent-violet-600"/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">{s.clientName}</span>
                      {s.shiftType && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 truncate max-w-[180px]">{s.shiftType}</span>}
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-mono">{s.date}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{s.startTime}–{s.endTime}</span>
                      <span className="text-xs text-slate-500">{s.hours}h</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-slate-400 shrink-0">Worker</span>
                      <select value={s.chosenStaffId} onChange={e => setWorker(i, e.target.value)}
                        className="flex-1 min-w-0 text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:border-violet-400">
                        {opts.map(o => <option key={o.staffId} value={o.staffId}>{o.staffName}{o.recommended ? ' (recommended)' : ''}{o.reason ? ` — ${o.reason}` : ''}</option>)}
                        <option value="">{opts.length ? '— Leave unassigned —' : 'No eligible worker — assign later'}</option>
                      </select>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1 truncate">{reasonFor(s)}</p>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
            <span className="text-sm text-slate-500">{selected.size} of {rows.length} selected</span>
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreate} disabled={selected.size === 0 || creating}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60">
                {creating ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
                Create {selected.size} Shift{selected.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ─── BulkScheduleModal ───────────────────────────────────────────────────────

function BulkScheduleModal({ staff, clients, onClose, onSave }) {
  const shiftTypes = useShiftTypes();
  const features   = useFeatures();
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState(0);

  // Step 1 — staff↔client assignments
  const [assignments, setAssignments] = useState(
    staff.map(s => ({
      staffId:  s.id,
      name:     `${s.first_name || ''} ${s.last_name || ''}`.trim(),
      initials: `${(s.first_name || '?')[0]}${(s.last_name || '?')[0]}`.toUpperCase(),
      selected: true,
      clientId: '',
    }))
  );
  const [staffSearch, setStaffSearch] = useState('');

  // Step 2 — schedule config
  const [rangeType, setRangeType]   = useState('week');
  const [startDate, setStartDate]   = useState(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  const [endDate, setEndDate]       = useState(format(addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 6), 'yyyy-MM-dd'));
  const [activeDays, setActiveDays] = useState([1, 2, 3, 4, 5]); // Mon–Fri
  const [shiftType, setShiftType]   = useState('');
  const [startTime, setStartTime]   = useState('09:00');
  const [endTime, setEndTime]       = useState('17:00');
  const [breakMins, setBreakMins]   = useState(30);
  const [status, setStatus]         = useState('booked');
  const [isPublished, setIsPublished] = useState(false);

  const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const ROW_C = ['#6366f1','#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6','#06b6d4','#f97316','#ec4899','#14b8a6'];

  // Sync end date when range type or start changes
  useEffect(() => {
    const s = parseISO(startDate);
    if (rangeType === 'week')  setEndDate(format(addDays(startOfWeek(s, { weekStartsOn: 1 }), 6), 'yyyy-MM-dd'));
    if (rangeType === 'month') setEndDate(format(endOfMonth(s), 'yyyy-MM-dd'));
  }, [rangeType, startDate]);

  const toggleDay = (d) => setActiveDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  const toggleStaff = (id) => setAssignments(prev => prev.map(a => a.staffId === id ? { ...a, selected: !a.selected } : a));
  const setClient = (staffId, clientId) => setAssignments(prev => prev.map(a => a.staffId === staffId ? { ...a, clientId } : a));
  const selectAll = (v) => setAssignments(prev => prev.map(a => ({ ...a, selected: v })));

  const selectedAssignments = assignments.filter(a => a.selected);

  // All dates in range that match active days
  const datesInRange = (() => {
    try {
      const s = parseISO(startDate);
      const e = parseISO(endDate);
      const result = [];
      let cur = s;
      while (cur <= e) {
        if (activeDays.includes(cur.getDay())) result.push(cur);
        cur = addDays(cur, 1);
      }
      return result;
    } catch { return []; }
  })();

  const totalShifts = selectedAssignments.length * datesInRange.length;
  const hours = calcHours(startTime, endTime);

  const filteredAssignments = assignments.filter(a =>
    !staffSearch || a.name.toLowerCase().includes(staffSearch.toLowerCase())
  );

  const handleCreate = async () => {
    if (selectedAssignments.length === 0) return toast.error('Select at least one staff member');
    if (datesInRange.length === 0) return toast.error('No dates match the selected days');
    setCreating(true);
    setProgress(0);
    const tasks = [];
    for (const a of selectedAssignments) {
      for (const date of datesInRange) {
        tasks.push({ staffId: a.staffId, clientId: a.clientId, date: format(date, 'yyyy-MM-dd') });
      }
    }
    let done = 0;
    const CHUNK = 5;
    for (let i = 0; i < tasks.length; i += CHUNK) {
      await Promise.all(tasks.slice(i, i + CHUNK).map(t =>
        api.post('/shifts', {
          staffId:      t.staffId,
          clientIds:    t.clientId ? [t.clientId] : [],
          shiftDate:    t.date,
          startTime,
          endTime,
          shiftType,
          breakMinutes: breakMins,
          status,
          isPublished: features.publish_shifts ? isPublished : true,
        }).catch(() => null)
      ));
      done = Math.min(i + CHUNK, tasks.length);
      setProgress(Math.round((done / tasks.length) * 100));
    }
    toast.success(`${done} shifts created successfully`);
    setCreating(false);
    onSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={!creating ? onClose : undefined} />

      <motion.div initial={{ scale: 0.96, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 12 }} transition={{ duration: 0.2 }}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col z-10 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-primary-600 to-indigo-600 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">Bulk Schedule</h2>
            <p className="text-xs text-primary-200 mt-0.5">Create shifts for multiple staff across a period</p>
          </div>
          <button onClick={onClose} disabled={creating}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors">
            <X size={15} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 px-6 pt-4 pb-2 shrink-0">
          {[{ n: 1, label: 'Assign Staff & Clients' }, { n: 2, label: 'Schedule Details' }].map((s, i) => (
            <React.Fragment key={s.n}>
              <button onClick={() => !creating && setStep(s.n)}
                className={clsx('flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all',
                  step === s.n ? 'bg-primary-50 text-primary-700' : 'text-slate-400 hover:text-slate-600')}>
                <span className={clsx('w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                  step === s.n ? 'bg-primary-500 text-white' : step > s.n ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500')}>
                  {step > s.n ? <Check size={11} /> : s.n}
                </span>
                {s.label}
              </button>
              {i === 0 && <div className="flex-1 h-px bg-slate-200 mx-2" />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-4" style={{ scrollbarWidth: 'thin' }}>

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <div>
              <p className="text-xs text-slate-500 mb-3">Select staff and assign each one a client for this bulk schedule.</p>

              {/* Search + select all */}
              <div className="flex items-center gap-3 mb-3">
                <div className="relative flex-1">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100"
                    placeholder="Search staff…" value={staffSearch} onChange={e => setStaffSearch(e.target.value)} />
                </div>
                <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  <div onClick={() => selectAll(assignments.every(a => a.selected) ? false : true)}
                    className={clsx('w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer',
                      assignments.every(a => a.selected) ? 'bg-primary-500 border-primary-500' : 'border-slate-300')}>
                    {assignments.every(a => a.selected) && <Check size={11} className="text-white" />}
                  </div>
                  <span className="text-sm font-medium text-slate-600">Select All</span>
                </label>
                <span className="text-xs text-slate-400 whitespace-nowrap">{selectedAssignments.length} selected</span>
              </div>

              {/* Staff list */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                {/* Table header */}
                <div className="grid grid-cols-[auto_1fr_auto_1.4fr] items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <span className="w-5" />
                  <span>Staff Member</span>
                  <span className="text-slate-300">→</span>
                  <span>Assigned Client</span>
                </div>
                <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                  {filteredAssignments.map((a, idx) => (
                    <div key={a.staffId}
                      className={clsx('grid grid-cols-[auto_1fr_auto_1.4fr] items-center gap-3 px-4 py-3 transition-colors',
                        a.selected ? 'bg-white' : 'bg-slate-50/60 opacity-60')}>
                      {/* Checkbox */}
                      <div onClick={() => toggleStaff(a.staffId)}
                        className={clsx('w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all shrink-0',
                          a.selected ? 'bg-primary-500 border-primary-500' : 'border-slate-300 hover:border-primary-400')}>
                        {a.selected && <Check size={11} className="text-white" />}
                      </div>
                      {/* Staff */}
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                          style={{ background: `linear-gradient(135deg, ${ROW_C[idx % ROW_C.length]}cc, ${ROW_C[idx % ROW_C.length]})` }}>
                          {a.initials}
                        </div>
                        <span className="text-sm font-semibold text-slate-800 truncate">{a.name}</span>
                      </div>
                      {/* Arrow */}
                      <ChevronRight size={14} className="text-slate-300" />
                      {/* Client dropdown */}
                      <div className="relative">
                        <select
                          value={a.clientId}
                          onChange={e => setClient(a.staffId, e.target.value)}
                          disabled={!a.selected}
                          className="w-full appearance-none border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 pr-7 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <option value="">No client</option>
                          {clients.map(c => (
                            <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                          ))}
                        </select>
                        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                  ))}
                  {filteredAssignments.length === 0 && (
                    <div className="py-8 text-center text-sm text-slate-400">No staff match your search</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (
            <div className="space-y-5 pt-1">

              {/* Date range */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Date Range</p>
                <div className="flex items-center gap-2 mb-4">
                  {[['week','This Week'],['month','This Month'],['custom','Custom']].map(([v, l]) => (
                    <button key={v} onClick={() => setRangeType(v)}
                      className={clsx('px-4 py-1.5 rounded-xl text-xs font-semibold border transition-all',
                        rangeType === v ? 'bg-primary-500 text-white border-primary-500 shadow-sm' : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50')}>
                      {l}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Start date</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">End date</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                      disabled={rangeType !== 'custom'}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400 disabled:opacity-50 disabled:cursor-not-allowed" />
                  </div>
                </div>
              </div>

              {/* Days of week */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Days of Week</p>
                <div className="flex gap-2 flex-wrap">
                  {[1,2,3,4,5,6,0].map(d => (
                    <button key={d} onClick={() => toggleDay(d)}
                      className={clsx('w-12 h-12 rounded-xl text-sm font-bold border-2 transition-all',
                        activeDays.includes(d)
                          ? 'bg-primary-500 border-primary-500 text-white shadow-sm'
                          : 'border-slate-200 text-slate-500 bg-white hover:border-primary-300 hover:text-primary-600')}>
                      {DAY_LABELS[d]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Shift details */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Shift Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Shift Type</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full pointer-events-none"
                        style={{ backgroundColor: hashColor(shiftType).border }} />
                      <select value={shiftType} onChange={e => setShiftType(e.target.value)}
                        className="w-full appearance-none border border-slate-200 rounded-xl pl-8 pr-8 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400">
                        <option value="">Select type…</option>
                        {shiftTypes.filter(t => t.is_active).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                      </select>
                      <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Status</label>
                    <div className="relative">
                      <select value={status} onChange={e => setStatus(e.target.value)}
                        className="w-full appearance-none border border-slate-200 rounded-xl px-3 pr-8 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400">
                        <option value="booked">Booked</option>
                        <option value="pending">Pending</option>
                      </select>
                      <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Start Time</label>
                    <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">End Time</label>
                    <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400" />
                  </div>
                  {features.advanced_break_tracking && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Break (mins)</label>
                    <input type="number" min="0" step="15" value={breakMins} onChange={e => setBreakMins(parseInt(e.target.value) || 0)}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400" />
                  </div>
                  )}
                  {features.publish_shifts && (
                  <div className="flex items-center gap-3 pt-5">
                    <Toggle value={isPublished} onChange={setIsPublished} />
                    <span className="text-sm text-slate-600">Publish immediately</span>
                  </div>
                  )}
                </div>
              </div>

              {/* Preview summary */}
              <div className={clsx('rounded-2xl p-4 border-2',
                totalShifts > 0 ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50')}>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Preview</p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  {[
                    { label: 'Staff Members', value: selectedAssignments.length, color: 'text-primary-600' },
                    { label: 'Working Days',  value: datesInRange.length,        color: 'text-indigo-600' },
                    { label: 'Total Shifts',  value: totalShifts,                color: 'text-emerald-600' },
                  ].map(item => (
                    <div key={item.label} className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm">
                      <p className={clsx('text-2xl font-black', item.color)}>{item.value}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.label}</p>
                    </div>
                  ))}
                </div>
                {hours > 0 && totalShifts > 0 && (
                  <p className="text-xs text-slate-500 text-center mt-3">
                    Each shift: <strong>{hours.toFixed(1)}h</strong>
                    {breakMins > 0 && <> · {breakMins}min break</>}
                    {' · '}<strong>{(hours * totalShifts).toFixed(0)} total hours</strong>
                  </p>
                )}
              </div>

              {/* Progress bar while creating */}
              {creating && (
                <div className="rounded-2xl p-4 border border-primary-200 bg-primary-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-primary-700">Creating shifts…</span>
                    <span className="text-sm font-bold text-primary-600">{progress}%</span>
                  </div>
                  <div className="w-full h-2.5 bg-primary-100 rounded-full overflow-hidden">
                    <motion.div className="h-full bg-primary-500 rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/80 shrink-0">
          <button onClick={onClose} disabled={creating}
            className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <div className="flex items-center gap-2.5">
            {step === 2 && (
              <button onClick={() => setStep(1)} disabled={creating}
                className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-white transition-colors disabled:opacity-50">
                <ChevronLeft size={14} /> Back
              </button>
            )}
            {step === 1 ? (
              <button onClick={() => setStep(2)} disabled={selectedAssignments.length === 0}
                className="flex items-center gap-1.5 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                Next <ChevronRight size={14} />
              </button>
            ) : (
              <button onClick={handleCreate} disabled={creating || totalShifts === 0}
                className={clsx('flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-colors shadow-sm',
                  creating || totalShifts === 0
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-emerald-500 hover:bg-emerald-600 text-white')}>
                {creating
                  ? <><Loader2 size={14} className="animate-spin" /> Creating…</>
                  : <><Check size={14} /> Create {totalShifts} Shifts</>
                }
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── BulkCancelModal ─────────────────────────────────────────────────────────

function BulkCancelModal({ staff, clients, onClose, onDone }) {
  const confirm = useConfirm();
  const today = new Date();
  const fmt = isoDate;
  const [range, setRange] = useState({ start: fmt(today), end: fmt(today) });
  const [staffId, setStaffId] = useState('');
  const [clientId, setClientId] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: range.start, end_date: range.end });
      if (staffId) params.set('staff_id', staffId);
      if (clientId) params.set('client_id', clientId);
      params.set('status', 'booked');
      const { data } = await api.get(`/shifts?${params}`);
      setPreview(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to preview'); }
    finally { setLoading(false); }
  };

  const confirmCancel = async () => {
    if (!preview?.length) return;
    if (!await confirm(`Cancel ${preview.length} shift${preview.length !== 1 ? 's' : ''}? This cannot be undone.`, { title: 'Bulk Cancel Shifts', danger: true })) return;
    setCancelling(true);
    try {
      const { data } = await api.post('/shifts/bulk-cancel', { start_date: range.start, end_date: range.end, staff_id: staffId || undefined, client_id: clientId || undefined, reason });
      toast.success(`${data.cancelled} shift${data.cancelled !== 1 ? 's' : ''} cancelled`);
      onDone();
    } catch (err) { toast.error(err.response?.data?.message || 'Bulk cancel failed'); }
    finally { setCancelling(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800 flex items-center gap-2"><Trash2 size={16} className="text-red-500" /> Bulk Cancel Shifts</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">From</label>
              <input type="date" value={range.start} onChange={e => setRange(r => ({ ...r, start: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">To</label>
              <input type="date" value={range.end} onChange={e => setRange(r => ({ ...r, end: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Staff (optional)</label>
              <select value={staffId} onChange={e => setStaffId(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400 appearance-none">
                <option value="">All staff</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Client (optional)</label>
              <select value={clientId} onChange={e => setClientId(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400 appearance-none">
                <option value="">All clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Reason (optional)</label>
            <input placeholder="e.g. Public holiday, service disruption…" value={reason} onChange={e => setReason(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary-400" />
          </div>
          <button onClick={loadPreview} disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
            Preview Affected Shifts
          </button>
          {preview !== null && (
            preview.length === 0 ? (
              <div className="text-center py-4 text-sm text-slate-400">No booked shifts found in this range</div>
            ) : (
              <div className="border border-red-200 rounded-xl overflow-hidden">
                <div className="bg-red-50 px-4 py-2 text-xs font-semibold text-red-700">{preview.length} shift{preview.length !== 1 ? 's' : ''} will be cancelled</div>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                  {preview.slice(0, 20).map(s => (
                    <div key={s.id} className="px-4 py-2 flex items-center justify-between text-xs">
                      <span className="text-slate-700 font-medium">{s.shift_date?.slice(0,10)} · {fmtTime12(s.start_time)}–{fmtTime12(s.end_time)}</span>
                      <span className="text-slate-500">{s.staff_name || 'Unassigned'}</span>
                    </div>
                  ))}
                  {preview.length > 20 && <div className="px-4 py-2 text-center text-xs text-slate-400">+ {preview.length - 20} more</div>}
                </div>
                <div className="px-4 py-3 bg-red-50 border-t border-red-200">
                  <button onClick={confirmCancel} disabled={cancelling}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors">
                    {cancelling ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                    Cancel {preview.length} Shift{preview.length !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MoreActionsDropdown ─────────────────────────────────────────────────────

function MoreActionsDropdown({ onBulkSchedule, onBulkCancel, onAutoRoster, onAssistant, onNotify, onRefresh, loadingRoster }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const items = [
    { icon: Users,    label: 'Bulk Schedule',  desc: 'Create shifts for multiple staff',  action: onBulkSchedule, color: 'text-indigo-600' },
    { icon: Trash2,   label: 'Bulk Cancel',    desc: 'Cancel shifts in a date range',    action: onBulkCancel,  color: 'text-red-500'    },
    { icon: Sparkles, label: 'AI Auto-Roster', desc: 'Generate a roster with AI',        action: onAutoRoster,  color: 'text-violet-600', loading: loadingRoster },
    { icon: ListChecks, label: 'Scheduling Assistant', desc: 'Stage & review batch assignments', action: onAssistant, color: 'text-violet-600' },
    { icon: Bell, label: 'Notify upcoming shifts', desc: 'Push staff about the next 2 days', action: onNotify, color: 'text-amber-600' },
    null,
    { icon: RefreshCw, label: 'Refresh',       desc: 'Reload schedule data',             action: onRefresh,     color: 'text-slate-500'  },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="More actions" aria-expanded={open} aria-haspopup="menu"
        className={clsx(
          'w-8 h-8 max-sm:w-10 max-sm:h-10 flex items-center justify-center border rounded-lg transition-colors',
          open ? 'border-primary-300 bg-primary-50 text-primary-600' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
        )}>
        <MoreVertical size={14} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.13 }}
            className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 py-1.5 z-50">
            {items.map((item, i) =>
              item === null ? (
                <div key={i} className="my-1 border-t border-slate-100" />
              ) : (
                <button key={item.label}
                  onClick={() => { item.action(); setOpen(false); }}
                  disabled={item.loading}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 transition-colors text-left disabled:opacity-50">
                  <span className={clsx('w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-slate-50', item.color)}>
                    {item.loading ? <Loader2 size={13} className="animate-spin" /> : <item.icon size={13} />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800">{item.label}</p>
                    <p className="text-[10px] text-slate-400 leading-tight">{item.desc}</p>
                  </div>
                </button>
              )
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Attendance / EVV Drawer ─────────────────────────────────────────────────

function AttendanceDrawer({ shifts, onClose }) {
  const now = new Date();
  const todayStr = format(now, 'yyyy-MM-dd');

  const alerts = shifts
    .filter(s => s.staff_id && !['cancelled', 'cancelled_with_charge', 'declined'].includes(s.status) && s.shift_date && s.start_time)
    .filter(s => s.shift_date === todayStr) // only today's shifts
    .map(s => {
      const shiftStart = new Date(`${s.shift_date}T${s.start_time}`);
      const shiftEnd   = new Date(`${s.shift_date}T${s.end_time}`);
      if (now < shiftStart) return null; // shift hasn't started yet today

      let issue = null;
      if (s.clock_in_time) {
        const clockIn = new Date(s.clock_in_time);
        const lateMin = Math.round((clockIn - shiftStart) / 60000);
        if (lateMin > 5) issue = { level: 'late', label: `Late clock-in +${lateMin}m`, time: format(clockIn, 'h:mm a'), color: '#d97706' };
        else issue = { level: 'ok', label: `Clocked in ${format(clockIn, 'h:mm a')}`, color: '#16a34a' };
      } else {
        issue = { level: 'missing', label: 'No clock-in', color: '#ef4444' };
      }

      if (s.clock_in_time && !s.clock_out_time && now > shiftEnd) {
        issue = { ...issue, label: issue.label + ' · No clock-out', level: 'missing-out', color: '#ef4444' };
      }

      return { shift: s, issue };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const order = { missing: 0, 'missing-out': 1, late: 2, ok: 3 };
      return (order[a.issue.level] ?? 9) - (order[b.issue.level] ?? 9);
    });

  const missing  = alerts.filter(a => a.issue.level === 'missing' || a.issue.level === 'missing-out');
  const lates    = alerts.filter(a => a.issue.level === 'late');
  const ontime   = alerts.filter(a => a.issue.level === 'ok');

  const Section = ({ title, color, items }) => items.length === 0 ? null : (
    <div className="mb-4">
      <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color }}>{title} ({items.length})</p>
      <div className="space-y-1.5">
        {items.map(({ shift: s, issue }) => (
          <div key={s.id} className="bg-white rounded-xl border border-slate-100 px-3 py-2.5 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-900 truncate">{s.staff_name || 'Unassigned'}</p>
                <p className="text-[10px] text-slate-500 truncate">{s.shift_type || 'Shift'} · {s.client_names ? s.client_names.split(',')[0].trim() : ''}</p>
                <p className="text-[10px] text-slate-400">{format(parseISO(s.shift_date), 'd MMM')} · {fmtTime(s.start_time)}–{fmtTime(s.end_time)}</p>
              </div>
              <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                style={{ background: issue.color + '18', color: issue.color }}>
                {issue.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 32, stiffness: 300 }}
      className="fixed right-0 top-0 bottom-0 w-[320px] bg-[#f8f9fb] border-l border-slate-200 shadow-2xl z-40 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-100 shrink-0">
        <div>
          <p className="text-sm font-bold text-slate-900">EVV / Attendance</p>
          <p className="text-[10px] text-slate-400">Today · {missing.length} missing · {lates.length} late · {ontime.length} on time</p>
        </div>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: 'thin' }}>
        {alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center gap-2">
            <Check size={28} className="text-emerald-400" />
            <p className="text-sm font-semibold text-slate-600">All clear</p>
            <p className="text-xs text-slate-400">No attendance issues for today</p>
          </div>
        ) : (
          <>
            <Section title="Missing clock-in / out" color="#ef4444" items={missing} />
            <Section title="Late arrivals" color="#d97706" items={lates} />
            <Section title="Clocked in" color="#16a34a" items={ontime} />
          </>
        )}
      </div>
    </motion.div>
  );
}

// ─── Main Scheduler ───────────────────────────────────────────────────────────

function BulkOpModal({ op, count, staff, shiftTypes, busy, onClose, onRun }) {
  const [days, setDays] = useState(7);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [nextDay, setNextDay] = useState(false);
  const [staffId, setStaffId] = useState('');
  const [force, setForce] = useState(false);
  const [serviceType, setServiceType] = useState('');
  const [fromStart, setFromStart] = useState('');
  const [toStart, setToStart] = useState('');
  const [includeStaff, setIncludeStaff] = useState(true);
  const TITLES = { day: 'Change Day', time: 'Change Time', worker: 'Switch Worker', service: 'Change Service Type', copy: 'Copy Services' };
  const activeTypes = (shiftTypes || []).filter(t => t.is_active !== false);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">{TITLES[op]} <span className="text-slate-400 font-normal">· {count} selected</span></h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {op === 'day' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Move by (days)</label>
              <input type="number" className="input-field w-40" value={days} onChange={e => setDays(e.target.value)} />
              <p className="text-xs text-slate-400 mt-1">Negative moves earlier. Clashes are flagged, not blocked.</p>
            </div>
          )}
          {op === 'time' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Start</label><input type="time" className="input-field" value={startTime} onChange={e => setStartTime(e.target.value)} /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">End</label><input type="time" className="input-field" value={endTime} onChange={e => setEndTime(e.target.value)} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={nextDay} onChange={e => setNextDay(e.target.checked)} /> Ends next day (overnight)</label>
            </>
          )}
          {op === 'worker' && (
            <>
              <label className="block text-xs font-medium text-slate-600 mb-1">Assign to</label>
              <select className="input-field" value={staffId} onChange={e => setStaffId(e.target.value)}>
                <option value="">Select worker…</option>
                {(staff || []).map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} /> Assign even if it causes a clash</label>
              <button onClick={() => onRun('switch-worker', { staffId: null }, 'Unassigned')} disabled={busy} className="text-sm text-slate-500 hover:text-slate-700 underline">Remove worker (leave unassigned)</button>
            </>
          )}
          {op === 'service' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Service type</label>
              <select className="input-field" value={serviceType} onChange={e => setServiceType(e.target.value)}>
                <option value="">Select…</option>
                {activeTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
          )}
          {op === 'copy' && (
            <>
              <p className="text-xs text-slate-500">Duplicates the selected shifts forward by a fixed number of days. Pick the <strong>first day of the week your selected shifts are in</strong>, then the <strong>first day of the week you want the copies in</strong>. The gap between the two dates is added to every shift, so each copy keeps the same weekday and time.</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Copy shifts from — week starting</label><input type="date" className="input-field" value={fromStart} onChange={e => { setFromStart(e.target.value); if (e.target.value && !toStart) { const [y, m, d] = e.target.value.split('-').map(Number); const nd = new Date(y, m - 1, d + 7); setToStart(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}-${String(nd.getDate()).padStart(2, '0')}`); } }} /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Paste copies into — week starting</label><input type="date" className="input-field" value={toStart} onChange={e => setToStart(e.target.value)} /></div>
              </div>
              {fromStart && toStart && toStart > fromStart && (() => {
                const [fy, fm, fd] = fromStart.split('-').map(Number);
                const [ty, tm, td] = toStart.split('-').map(Number);
                const gap = Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000);
                return <p className="text-xs text-primary-600 font-medium">Each selected shift will move forward {gap} day{gap !== 1 ? 's' : ''} (keeping its weekday & time).</p>;
              })()}
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={includeStaff} onChange={e => setIncludeStaff(e.target.checked)} /> Keep assigned workers</label>
            </>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
          <button disabled={busy} className="btn-primary btn-sm" onClick={() => {
            if (op === 'day') onRun('change-day', { days: parseInt(days, 10) }, 'Moved');
            else if (op === 'time') onRun('change-time', { startTime, endTime, nextDay }, 'Retimed');
            else if (op === 'worker') { if (!staffId) return; onRun('switch-worker', { staffId, force }, 'Reassigned'); }
            else if (op === 'service') { if (!serviceType) return; onRun('change-service-type', { serviceType }, 'Service changed'); }
            else if (op === 'copy') { if (!fromStart || !toStart) return; if (toStart <= fromStart) return toast.error('New start must be after the original start — pick a future date so shifts copy forward, not onto the same day.'); onRun('copy-services', { fromStart, toStart, includeStaff }, 'Copied', { keepSelection: true }); }
          }}>{busy ? 'Working…' : 'Apply'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Applied-filter chip ──────────────────────────────────────────────────────
function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 h-6 pl-2 pr-0.5 rounded-full bg-white border border-primary-200 text-[11px] font-medium text-primary-800 shrink-0">
      {label}
      <button type="button" onClick={onRemove} aria-label={`Remove filter: ${label}`}
        className="w-5 h-5 flex items-center justify-center rounded-full text-primary-500 hover:bg-primary-100 hover:text-primary-800">
        <X size={11} />
      </button>
    </span>
  );
}

// ─── Board key ────────────────────────────────────────────────────────────────
// What the card colours mean, drawn as real cards: the body tint is the shift's state, the thin
// top bar is its service. Opens from the status bar.
const KEY_STATES = [
  ['#eff6ff', 'Booked / published'],
  ['#fef9c3', 'Needs a worker, or no service type'],
  ['#fffbeb', 'Pending, or a warning to check'],
  ['#fef2f2', 'Issue: clash, no fund or no budget'],
  ['#f8fafc', 'Draft (not published)'],
  ['#ecfdf5', 'Completed / delivered'],
  ['#f1f5f9', 'Cancelled'],
];

function BoardKey({ shiftTypes }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const services = (shiftTypes || []).filter((t) => t.is_active);
  return (
    <div ref={ref} className="relative shrink-0">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className={clsx('h-7 inline-flex items-center gap-1.5 px-2.5 rounded-lg border text-[11px] font-semibold transition-colors',
          open ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')}>
        <Info size={12} /> Key
      </button>
      {open && (
        <div role="dialog" aria-label="What the colours mean"
          className="absolute right-0 bottom-full mb-2 w-80 max-w-[90vw] bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50">
          <p className="text-xs font-semibold text-slate-800">Card colour: the shift's state</p>
          <ul className="mt-2 space-y-1.5">
            {KEY_STATES.map(([bg, label]) => (
              <li key={label} className="flex items-center gap-2.5 text-xs text-slate-700">
                <span className="w-8 h-5 rounded border border-slate-200/80 shrink-0" style={{ backgroundColor: bg, borderTop: '3px solid #94a3b8' }} aria-hidden="true" />
                {label}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs font-semibold text-slate-800">Top bar: the service</p>
          {services.length ? (
            <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 max-h-40 overflow-y-auto">
              {services.map((t) => (
                <li key={t.id} className="flex items-center gap-2 text-xs text-slate-700 min-w-0">
                  <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: t.color || hashColor(t.name).border }} aria-hidden="true" />
                  <span className="truncate">{t.name}</span>
                </li>
              ))}
            </ul>
          ) : <p className="mt-1 text-xs text-slate-500">Service colours are set in Settings › Service types.</p>}
        </div>
      )}
    </div>
  );
}

// The bulk bar's "More" menu: the actions that cancel or delete, kept apart from everyday edits.
function BulkMoreMenu({ busy, onUncancel, onCancel, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const item = 'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 disabled:opacity-50';
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu"
        className="text-sm px-2.5 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 inline-flex items-center gap-1">
        More <ChevronUp size={13} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 bottom-full mb-2 w-52 bg-white text-slate-700 rounded-xl shadow-xl border border-slate-200 py-1 z-50">
          <button role="menuitem" type="button" disabled={busy} onClick={() => { setOpen(false); onUncancel(); }} className={item}>
            <RotateCcw size={14} className="text-slate-500" /> Uncancel
          </button>
          <button role="menuitem" type="button" onClick={() => { setOpen(false); onCancel(); }} className={clsx(item, 'text-red-600')}>
            <Ban size={14} /> Cancel shifts…
          </button>
          <button role="menuitem" type="button" disabled={busy} onClick={() => { setOpen(false); onDelete(); }} className={clsx(item, 'text-red-600')}>
            <Trash2 size={14} /> Delete…
          </button>
        </div>
      )}
    </div>
  );
}

export default function Scheduler() {
  const confirm = useConfirm();
  const shiftTypes = useShiftTypes();
  const features = useFeatures();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView]               = useState('Weekly');
  const [viewMode, setViewMode]       = useState('Client');
  // Which programme's participants the board shows: NDIS and aged care are funded, priced and
  // claimed under different rules, and coordinators usually roster one or the other at a time.
  const [programme, setProgramme]     = useState(() => {
    try { const v = localStorage.getItem('scheduler.programme'); return ['all', 'ndis', 'aged_care'].includes(v) ? v : 'all'; } catch { return 'all'; }
  });
  useEffect(() => { try { localStorage.setItem('scheduler.programme', programme); } catch { /* storage off */ } }, [programme]);
  const [compact, setCompact]         = useState(false);
  const [expand, setExpand]           = useState(false);
  const [search, setSearch]           = useState('');
  const [shifts, setShifts]           = useState([]);
  const [staff, setStaff]             = useState([]);
  const [clients, setClients]         = useState([]);
  const [facilities, setFacilities]   = useState([]);
  const [groupHomes, setGroupHomes]   = useState([]);
  const [groupShiftModal, setGroupShiftModal] = useState(null);  // {shiftId?} | null
  const [loading, setLoading]         = useState(true);   // first load only (skeleton)
  const [refreshing, setRefreshing]   = useState(false);  // later reloads keep the board on screen
  const hasLoadedRef = useRef(false);
  const [mobileTools, setMobileTools] = useState(false);  // phone: show the extra toolbar controls
  const [modal, setModal]             = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);
  const [defaultStaff, setDefaultStaff] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [activeShift, setActiveShift] = useState(null);
  const [filterOpen, setFilterOpen]       = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [defaultClientId, setDefaultClientId] = useState(null);
  const [defaultJobBoard, setDefaultJobBoard] = useState(false);
  const [selectedIds, setSelectedIds]     = useState(new Set());
  const [boardLayout, setBoardLayout]     = useState('board');   // 'board' (grid) | 'list' (table)
  const [bulkMode, setBulkMode]           = useState(false);
  const [unlockedShifts, setUnlockedShifts] = useState(() => new Set()); // shifts unlocked for dragging
  const toggleLock = useCallback((id) => setUnlockedShifts(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  }), []);
  const [pendingMove, setPendingMove]     = useState(null);   // {shiftId,...} awaiting move confirmation
  const [bulkModal, setBulkModal]         = useState(false);
  const [bulkOp, setBulkOp]               = useState(null);   // 'day'|'time'|'worker'|'service'|'copy'
  const [bulkBusy, setBulkBusy]           = useState(false);
  const [bulkCancelModal, setBulkCancelModal] = useState(false);
  const [bulkCancelPolicyOpen, setBulkCancelPolicyOpen] = useState(false);
  const [rosterModal, setRosterModal]     = useState(false);
  const [rosterData, setRosterData]       = useState(null);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [rosterCreating, setRosterCreating] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);

  // Filter state — persisted to localStorage
  const [filteredOnly, setFilteredOnly] = useState(false);
  const [filterStatuses, setFilterStatuses] = useState(() => { try { return JSON.parse(localStorage.getItem('sc_filterStatuses') || '[]'); } catch { return []; } });
  const [filterTypes, setFilterTypes]       = useState(() => { try { return JSON.parse(localStorage.getItem('sc_filterTypes') || '[]'); } catch { return []; } });
  const [filterSortBy, setFilterSortBy]     = useState(() => localStorage.getItem('sc_filterSortBy') || 'name');
  const [filterPublished, setFilterPublished] = useState(() => localStorage.getItem('sc_filterPublished') || 'all');

  useEffect(() => { localStorage.setItem('sc_filterStatuses', JSON.stringify(filterStatuses)); }, [filterStatuses]);
  useEffect(() => { localStorage.setItem('sc_filterTypes', JSON.stringify(filterTypes)); }, [filterTypes]);
  useEffect(() => { localStorage.setItem('sc_filterSortBy', filterSortBy); }, [filterSortBy]);
  useEffect(() => { localStorage.setItem('sc_filterPublished', filterPublished); }, [filterPublished]);

  // Leave data for overlay
  const [leaveMap, setLeaveMap] = useState({});

  const filterRef = useRef();
  const datePickerRef = useRef();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const days = (() => {
    if (view === 'Weekly') return getWeekDays(currentDate);
    if (view === '10-Day') return getTenDays(currentDate);
    if (view === 'Fortnightly') return getFortnightDays(currentDate);
    if (view === 'Monthly') return getMonthDays(currentDate);
    return [startOfDay(currentDate)];
  })();

  const rangeLabel = (() => {
    if (view === 'Daily') return format(currentDate, 'EEEE, d MMMM yyyy');
    if (view === 'Monthly') return format(currentDate, 'MMMM yyyy');
    const first = days[0], last = days[days.length - 1];
    if (format(first, 'MMM yyyy') === format(last, 'MMM yyyy')) return format(first, 'MMMM yyyy');
    if (format(first, 'yyyy') === format(last, 'yyyy')) return `${format(first, 'MMM')} – ${format(last, 'MMM yyyy')}`;
    return `${format(first, 'MMM yyyy')} – ${format(last, 'MMM yyyy')}`;
  })();

  // ?location=<id> opens the board for one branch: its shifts, and only the staff who
  // work there. Absent, the board behaves exactly as before — every branch at once.
  const branchId = new URLSearchParams(window.location.search).get('location') || '';
  const [branchName, setBranchName] = useState('');
  useEffect(() => {
    if (!branchId) { setBranchName(''); return; }
    api.get(`/locations/${branchId}`)
      .then(r => setBranchName(r.data?.name || ''))
      .catch(() => setBranchName(''));
  }, [branchId]);

  const loadData = useCallback(() => {
    // The first load shows a skeleton. After that the board stays mounted (dimmed, with a thin
    // progress bar) so a save or a week step doesn't send a long roster back to the top.
    if (hasLoadedRef.current) setRefreshing(true); else setLoading(true);
    const startStr = format(days[0], 'yyyy-MM-dd');
    const endStr   = format(days[days.length - 1], 'yyyy-MM-dd');
    Promise.all([
      api.get(`/shifts?start_date=${startStr}&end_date=${endStr}` + (branchId ? `&location_id=${branchId}` : '')),
      api.get('/staff' + (branchId ? `?location_id=${branchId}` : '')),
      api.get('/clients?limit=2000'), // load all clients (picker filters client-side; needs funds for the selected one)
      api.get('/facilities').catch(() => ({ data: [] })),
      api.get(`/leave?status=approved`).catch(() => ({ data: [] })),
      api.get('/groups/group-homes').catch(() => ({ data: [] })),
    ])
      .then(([shiftsRes, staffRes, clientsRes, facilitiesRes, leaveRes, groupHomesRes]) => {
        setShifts(shiftsRes.data);
        setStaff(staffRes.data);
        setClients(clientsRes.data.clients || clientsRes.data);
        setFacilities(Array.isArray(facilitiesRes.data) ? facilitiesRes.data : (facilitiesRes.data.facilities || []));
        setGroupHomes(Array.isArray(groupHomesRes.data) ? groupHomesRes.data : (groupHomesRes.data.groupHomes || []));
        // Build leaveMap: { staffId: [{start_date, end_date}] }
        const lm = {};
        (Array.isArray(leaveRes.data) ? leaveRes.data : []).forEach(lr => {
          if (!lm[lr.staff_id]) lm[lr.staff_id] = [];
          lm[lr.staff_id].push({ start: lr.start_date, end: lr.end_date });
        });
        setLeaveMap(lm);
        hasLoadedRef.current = true;
      })
      .catch(() => toast.error('Failed to load scheduler'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [view, currentDate, branchId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Apply filters to shifts
  const activeClientIds = new Set((clients || []).map(c => c.id));
  // The "Cancelled" filter (value 'cancelled') must also match a charged cancellation
  // ('cancelled_with_charge'), otherwise short-notice cancellations never show up.
  const statusInFilter = (st) => filterStatuses.includes(st) || (st === 'cancelled_with_charge' && filterStatuses.includes('cancelled'));
  // Hide shifts that belong only to archived/deactivated clients (active client list) — not a filter.
  const onActiveClients = (s) => {
    if (!activeClientIds.size) return true;
    const cids = (s.client_ids || '').split(',').map(x => x.trim()).filter(Boolean);
    return !cids.length || cids.some(id => activeClientIds.has(id));
  };
  // A client is aged care when the profile says so; everyone else on the board is NDIS (private
  // clients are grouped with NDIS here, as the client grid's own label does for funding rules).
  const isAgedCare = (c) => c && (c.service_type === 'aged_care' || c.client_type === 'aged_care');
  const programmeOk = (c) => programme === 'all' || (programme === 'aged_care' ? isAgedCare(c) : !isAgedCare(c));
  const clientById = new Map((clients || []).map((c) => [String(c.id), c]));
  const onProgramme = (s) => {
    if (programme === 'all') return true;
    const cids = (s.client_ids || '').split(',').map((x) => x.trim()).filter(Boolean);
    if (!cids.length) return programme === 'ndis';
    return cids.some((id) => programmeOk(clientById.get(id)));
  };
  const boardShifts = shifts.filter(onActiveClients).filter(onProgramme);
  const filteredShifts = boardShifts.filter(s => {
    // Cancelled/declined shifts are shown by default now (they read as grey/cancelled cards). A status
    // filter still narrows the board — picking e.g. "Booked" hides them via the filter check below.
    if (filterStatuses.length > 0 && !statusInFilter(s.status)) return false;
    if (filterTypes.length > 0 && !filterTypes.includes(s.shift_type)) return false;
    if (filterPublished === 'published' && !s.is_published) return false;
    if (filterPublished === 'unpublished' && s.is_published) return false;
    return true;
  });

  // Sort order isn't a filter — it hides nothing — so it no longer counts as one.
  const activeFilterCount = filterStatuses.length + filterTypes.length + (filterPublished !== 'all' ? 1 : 0);
  const hiddenByFilters = boardShifts.length - filteredShifts.length;
  const showFilterStrip = activeFilterCount > 0 || filteredOnly || !!search;
  const clearFilters = () => { setSearch(''); setFilterStatuses([]); setFilterTypes([]); setFilterSortBy('name'); setFilterPublished('all'); setFilteredOnly(false); };
  const SORT_LABELS = { hours: 'hours (high to low)', shifts: 'number of shifts' };

  // Brevity "Filtered / All": when on, the board only shows rows (staff/clients/homes)
  // that actually have a shift in the visible period — keeps the board manageable.
  const rowStaffIds = new Set(filteredShifts.map(s => s.staff_id).filter(Boolean));
  const rowClientIds = new Set(filteredShifts.flatMap(s => (s.client_ids || '').split(',').map(x => x.trim()).filter(Boolean)));
  const rowHomeIds = new Set(filteredShifts.map(s => s.group_home_id).filter(Boolean));
  const visibleStaff = filteredOnly ? (staff || []).filter(s => rowStaffIds.has(s.id)) : staff;
  const programmeClients = (clients || []).filter(programmeOk);
  const visibleClients = filteredOnly ? programmeClients.filter(c => rowClientIds.has(c.id)) : programmeClients;
  const visibleHomes = filteredOnly ? (groupHomes || []).filter(h => rowHomeIds.has(h.id)) : groupHomes;
  // Responsive density: measure the actual board width and switch cards to compact when
  // each day column would be too narrow for a full card. So collapsing the sidebar (wider
  // columns) shows fuller cards, and tight layouts (Fortnightly / narrow screens) go compact.
  const boardRef = useRef(null);
  const [boardW, setBoardW] = useState(0);
  useEffect(() => {
    const el = boardRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => { for (const e of entries) setBoardW(e.contentRect.width); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Multi-week views (10-Day / Fortnightly) must fit all their columns on screen without a
  // horizontal scrollbar, so they use a low column floor and let the 1fr tracks stretch to
  // fill the board. At those narrow widths they render the dense card built to fit cleanly.
  const multiWeek = days.length > 7 && days.length <= 16; // 10-Day, Fortnightly
  const colFloor = multiWeek ? 60 : 80;
  const perColW = days.length ? Math.max(colFloor, (boardW - 124) / days.length) : 999; // 124 = grid left column
  const effCompact = expand ? false : (compact || multiWeek || (boardW > 0 ? perColW < 92 : false));

  const resetView = () => { setSearch(''); setFilterStatuses([]); setFilterTypes([]); setFilterSortBy('name'); setFilterPublished('all'); setFilteredOnly(false); setCurrentDate(new Date()); };

  const navigate = (dir) => {
    if (view === 'Daily') setCurrentDate(d => addDays(d, dir));
    else if (view === 'Weekly') setCurrentDate(d => addWeeks(d, dir));
    else if (view === '10-Day') setCurrentDate(d => addDays(d, dir * 10));
    else if (view === 'Fortnightly') setCurrentDate(d => addWeeks(d, dir * 2));
    else setCurrentDate(d => addMonths(d, dir));
  };

  const handleDragStart = ({ active }) => {
    const shift = shifts.find(s => `shift_${s.id}` === active.id);
    setActiveShift(shift || null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveShift(null);
    if (!over) return;
    const shiftId = active.id.replace('shift_', '');
    const cellId = over.id;
    const dateMatch = cellId.match(/(\d{4}-\d{2}-\d{2})$/);
    if (!dateMatch) return;

    const newShiftDate = dateMatch[1];
    const isVacant   = cellId.startsWith('vacant_');
    const isJobBoard = cellId.startsWith('jobboard_');
    const isClient   = cellId.startsWith('client_');
    const isFacility = cellId.startsWith('facility_');

    // For staff cells the id is `${staffId}_${date}` — strip the trailing date to get the staff UUID.
    // For all non-staff rows (vacant, jobboard, client, facility) the shift stays unassigned.
    const newStaffId = (isVacant || isJobBoard || isClient || isFacility)
      ? null
      : cellId.slice(0, cellId.length - newShiftDate.length - 1); // strip `_YYYY-MM-DD`

    const shift = shifts.find(s => s.id === shiftId);
    if (!shift) return;

    // No-op: same date AND same staff assignment (treat null/undefined as equivalent)
    const shiftCurrentDate  = shift.shift_date ? shift.shift_date.split('T')[0] : null;
    const shiftCurrentStaff = shift.staff_id ?? null;
    if (shiftCurrentDate === newShiftDate && shiftCurrentStaff === (newStaffId ?? null) && !isJobBoard) return;

    // Don't move yet — stage the move and ask the user to confirm the new position first.
    const member = newStaffId ? staff.find(m => m.id === newStaffId) : null;
    const toWho = isJobBoard ? 'the Job Board' : member ? `${member.first_name} ${member.last_name}` : 'Unassigned';
    const shiftLabel = `${fmtTime(shift.start_time)}–${fmtTime(shift.end_time)}${shift.client_names ? ` · ${shift.client_names.split(',')[0].trim()}` : ''}`;
    setPendingMove({ shiftId, newShiftDate, newStaffId, isJobBoard, toWho, shiftLabel, fromDate: shiftCurrentDate });
  };

  // Apply a staged move (after the user confirms it), then re-lock the shift.
  const commitMove = async () => {
    const m = pendingMove;
    if (!m) return;
    setPendingMove(null);
    const { shiftId, newShiftDate, newStaffId, isJobBoard } = m;
    const shift = shifts.find(s => s.id === shiftId);
    if (!shift) return;
    const optimisticStatus = shift.status === 'completed' || shift.status === 'cancelled' || shift.status === 'declined'
      ? shift.status
      : (newStaffId ? 'booked' : 'pending');
    setShifts(prev => prev.map(s =>
      s.id === shiftId
        ? { ...s, shift_date: newShiftDate, staff_id: newStaffId, is_on_job_board: isJobBoard, status: optimisticStatus }
        : s
    ));
    try {
      const { data: updated } = await withComplianceOverride(confirm, (extra) => api.patch(`/shifts/${shiftId}/reschedule`, {
        shiftDate: newShiftDate, staffId: newStaffId, isJobBoard, ...extra,
      }));
      setShifts(prev => prev.map(s => s.id === shiftId ? { ...s, ...updated } : s));
      toast.success('Shift rescheduled');
      setUnlockedShifts(prev => { const n = new Set(prev); n.delete(shiftId); return n; }); // re-lock after move
    } catch (err) {
      if (!err.cancelled) toast.error(err.response?.data?.message || 'Failed to reschedule');
      loadData(); // revert optimistic update
    }
  };

  const unpublishedCount = shifts.filter(s => !s.is_published).length;
  const publishAll = async () => {
    const toPublish = shifts.filter(s => !s.is_published);
    if (!toPublish.length) return;
    try {
      const results = await Promise.allSettled(
        toPublish.map(s => api.put(`/shifts/${s.id}`, { isPublished: true }))
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed    = results.filter(r => r.status === 'rejected').length;
      if (succeeded > 0) toast.success(`${succeeded} shift${succeeded !== 1 ? 's' : ''} published`);
      if (failed > 0) toast.error(`${failed} shift${failed !== 1 ? 's' : ''} failed to publish`);
      loadData();
    } catch {
      toast.error('Failed to publish shifts');
    }
  };

  const runAutoRoster = async () => {
    const weekStart = format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    setLoadingRoster(true);
    setRosterModal(true);
    setRosterData(null);
    try {
      const { data } = await api.post('/ai/auto-roster', { weekStart });
      setRosterData(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Auto-roster failed');
      setRosterModal(false);
    } finally {
      setLoadingRoster(false);
    }
  };

  const createRosterShifts = async (selectedShifts) => {
    setRosterCreating(true);
    let created = 0;
    for (const s of selectedShifts) {
      try {
        await api.post('/shifts', {
          staffId:    s.staffId || '',
          clientIds:  s.clientId ? [s.clientId] : [],
          shiftDate:  s.date,
          startTime:  s.startTime,
          endTime:    s.endTime,
          shiftType:  s.shiftType || '',
          isPublished: features.publish_shifts ? false : true,
        });
        created++;
      } catch { /* skip conflicting shifts */ }
    }
    toast.success(`Created ${created} of ${selectedShifts.length} shifts`);
    setRosterModal(false);
    setRosterData(null);
    loadData();
    setRosterCreating(false);
  };

  const openAddModal = (date, staffId, clientId, jobBoard = false) => {
    setDefaultDate(date);
    setDefaultStaff(staffId || null);
    setDefaultClientId(clientId || null);
    setDefaultJobBoard(!!jobBoard);
    setModal({});
  };

  const handleContextMenu = (e, shift) => {
    setContextMenu({ x: e.clientX, y: e.clientY, shift });
  };

  const duplicateShift = async (shift) => {
    try {
      const clientIdsArr = shift.client_ids
        ? shift.client_ids.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      await api.post('/shifts', {
        clientIds:            clientIdsArr,
        staffId:              shift.staff_id || '',
        shiftDate:            shift.shift_date?.split('T')[0],
        startTime:            shift.start_time?.slice(0, 5),
        endTime:              shift.end_time?.slice(0, 5),
        shiftType:            shift.shift_type,
        additionalShiftTypes: shift.additional_shift_types || [],
        instructions:         shift.instructions,
        address:              shift.address,
        priceBookId:          shift.price_book_id || null,
        facilityId:           shift.facility_id || null,
        mileageCap:           shift.mileage_cap,
        companyVehicle:       shift.company_vehicle || false,
        vehicleId:            shift.vehicle_id || '',
        bonusAmount:          shift.bonus_amount,
        breakMinutes:         shift.break_minutes || 0,
        areaFacility:         shift.area_facility,
        status:               'booked',
        isPublished:          features.publish_shifts ? false : true,
      });
      toast.success('Shift duplicated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to duplicate shift');
    }
  };

  const cancelShift = async (shift) => {
    if (!await confirm('Cancel this shift?', { title: 'Cancel Shift', danger: true })) return;
    try {
      await api.delete(`/shifts/${shift.id}`);
      toast.success('Shift cancelled');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel shift');
    }
  };

  const uncancelShift = async (shift) => {
    if (!await confirm('Restore this cancelled shift back to active?', { title: 'Uncancel Shift' })) return;
    try {
      await api.post(`/shifts/${shift.id}/uncancel`);
      toast.success('Shift restored');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to un-cancel shift');
    }
  };

  const onToggleSelect = (shiftId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(shiftId) ? next.delete(shiftId) : next.add(shiftId);
      return next;
    });
  };

  const notifyUpcoming = async () => {
    try {
      const { data } = await api.post('/shifts/notify-upcoming', { days: 2 });
      toast.success(`Notified ${data.notified} of ${data.staff} staff about shifts in the next ${data.days} days`);
    } catch { toast.error('Failed to notify staff'); }
  };

  const bulkAction = async (action) => {
    if (!selectedIds.size) return;
    try {
      const { data } = await api.post('/shifts/bulk-action', { ids: [...selectedIds], action });
      const n = data?.updated ?? selectedIds.size;
      toast.success(`${n} shift${n === 1 ? '' : 's'} ${action}d`);
      if (data?.skipped) toast(`${data.skipped} skipped: already completed, invoiced or cancelled`, { icon: '⚠️' });
      setSelectedIds(new Set());
      setBulkMode(false);
      loadData();
    } catch { toast.error('Bulk action failed'); }
  };

  const bulkUncancel = async () => {
    if (!selectedIds.size) return;
    if (!await confirm('Restore the selected cancelled shift(s) back to active? Non-cancelled and already-invoiced shifts are skipped.', { title: 'Uncancel Shifts' })) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/shifts/bulk-action', { ids: [...selectedIds], action: 'uncancel' });
      const restored = data.updated ?? 0;
      toast.success(`Restored ${restored} shift${restored !== 1 ? 's' : ''}`);
      if (data.skipped) toast(`${data.skipped} skipped`, { icon: '⚠️' });
      setSelectedIds(new Set());
      setBulkMode(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.message || 'Bulk uncancel failed'); }
    finally { setBulkBusy(false); }
  };

  // Phase-2 bulk ops (change-day/time/worker/service, recalc, undo, copy)
  const runBulk = async (path, body, label, { keepSelection } = {}) => {
    if (!selectedIds.size && path !== 'copy-services') return;
    setBulkBusy(true);
    try {
      const { data } = await withComplianceOverride(confirm, (extra) => api.post(`/shifts/bulk/${path}`, { ids: [...selectedIds], ...extra, ...body }));
      const done = data.updated ?? data.reverted ?? data.recalculated ?? data.created ?? data.deleted ?? 0;
      toast.success(`${label}: ${done} shift${done !== 1 ? 's' : ''}`);
      if (data.skipped?.length) toast(`${data.skipped.length} skipped`, { icon: '⚠️' });
      if (data.conflicts?.length) toast(`${data.conflicts.length} clash${data.conflicts.length !== 1 ? 'es' : ''} flagged`, { icon: '⚠️' });
      setBulkOp(null);
      if (!keepSelection) { setSelectedIds(new Set()); setBulkMode(false); }
      loadData();
      return data;
    } catch (e) { if (!e.cancelled) toast.error(e.response?.data?.message || 'Bulk action failed'); }
    finally { setBulkBusy(false); }
  };

  const VIEW_MODES = ['Staff', 'Client', 'Facilities', 'Group'];

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
    <DragLockContext.Provider value={{ unlocked: unlockedShifts, toggleLock }}>
      <div className="flex flex-col h-[calc(100dvh-80px)] -mx-4 -my-4 md:-mx-6">

        {/* ── Toolbar ───────────────────────────────────────────────────────── */}
        {/* One control height (32px; 40px on a phone), one radius, labelled icon buttons. */}
        <div className="flex flex-wrap items-center gap-2 gap-y-2 px-4 py-2.5 bg-gradient-to-b from-white to-slate-50/60 border-b border-slate-200/80 shadow-[0_1px_2px_-1px_rgba(15,23,42,0.06)] shrink-0">

          {/* View mode tabs */}
          <div role="tablist" aria-label="Board view" className="flex items-center gap-0.5 bg-slate-100/80 ring-1 ring-slate-200/60 rounded-lg p-0.5 shrink-0">
            {VIEW_MODES.map(m => {
              const Icon = m === 'Staff' ? UserCircle : m === 'Client' ? Users : m === 'Group' ? Home : Building2;
              return (
                <button key={m} role="tab" aria-selected={viewMode === m} onClick={() => setViewMode(m)}
                  className={clsx('h-7 max-sm:h-9 flex items-center gap-1.5 px-3 text-xs font-semibold rounded-md transition-all',
                    viewMode === m
                      ? 'bg-white shadow-sm text-slate-900 ring-1 ring-slate-200/70'
                      : 'text-slate-500 hover:text-slate-700')}>
                  <Icon size={12} aria-hidden="true" /> {m}
                </button>
              );
            })}
          </div>

          {/* Programme: all participants, NDIS only, or aged care only */}
          <div role="tablist" aria-label="Programme" className="flex items-center gap-0.5 bg-slate-100/80 ring-1 ring-slate-200/60 rounded-lg p-0.5 shrink-0">
            {[['all', 'All clients'], ['ndis', 'NDIS'], ['aged_care', 'Aged care']].map(([key, label]) => (
              <button key={key} role="tab" aria-selected={programme === key} onClick={() => setProgramme(key)}
                className={clsx('h-7 max-sm:h-9 flex items-center gap-1.5 px-3 text-xs font-semibold rounded-md transition-all',
                  programme === key
                    ? key === 'aged_care' ? 'bg-amber-50 shadow-sm text-amber-800 ring-1 ring-amber-200'
                      : key === 'ndis' ? 'bg-teal-50 shadow-sm text-teal-800 ring-1 ring-teal-200'
                      : 'bg-white shadow-sm text-slate-900 ring-1 ring-slate-200/70'
                    : 'text-slate-500 hover:text-slate-700')}>
                {key !== 'all' && <span aria-hidden="true" className={clsx('h-1.5 w-1.5 rounded-full', key === 'aged_care' ? 'bg-amber-500' : 'bg-teal-500')} />}
                {label}
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-slate-200 mx-1 shrink-0 max-sm:hidden" />

          {/* Today */}
          <button onClick={() => setCurrentDate(new Date())}
            className="h-8 max-sm:h-10 px-2.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 bg-white hover:bg-slate-50 transition-colors shrink-0">
            Today
          </button>

          {/* Date nav */}
          <div className="flex items-center gap-0.5 shrink-0">
            <button onClick={() => navigate(-1)} aria-label="Previous period"
              className="w-8 h-8 max-sm:w-10 max-sm:h-10 flex items-center justify-center border border-slate-200 rounded-lg bg-white hover:bg-slate-50 text-slate-500 transition-colors">
              <ChevronLeft size={14} />
            </button>
            <button onClick={() => navigate(1)} aria-label="Next period"
              className="w-8 h-8 max-sm:w-10 max-sm:h-10 flex items-center justify-center border border-slate-200 rounded-lg bg-white hover:bg-slate-50 text-slate-500 transition-colors">
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Range label + date picker */}
          <div ref={datePickerRef} className="relative flex items-center gap-1.5 shrink-0">
            <span className="text-sm font-bold text-slate-800 whitespace-nowrap">{rangeLabel}</span>
            <button onClick={() => setDatePickerOpen(o => !o)} aria-label="Pick a date" aria-expanded={datePickerOpen}
              className={clsx('w-8 h-8 max-sm:w-10 max-sm:h-10 flex items-center justify-center border rounded-lg bg-white hover:bg-slate-50 transition-colors',
                datePickerOpen ? 'border-primary-300 text-primary-500' : 'border-slate-200 text-slate-500')}>
              <Calendar size={13} />
            </button>
            <AnimatePresence>
              {datePickerOpen && (
                <DatePickerPopup
                  open={datePickerOpen}
                  currentDate={currentDate}
                  onSelect={(d) => setCurrentDate(d)}
                  onClose={() => setDatePickerOpen(false)}
                />
              )}
            </AnimatePresence>
          </div>

          {/* Phone: the extra controls fold behind one button */}
          <button onClick={() => setMobileTools(o => !o)} aria-expanded={mobileTools} aria-controls="sched-tools"
            className={clsx('sm:hidden h-10 inline-flex items-center gap-1.5 px-3 border rounded-lg text-xs font-medium shrink-0',
              mobileTools || showFilterStrip ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600')}>
            <Filter size={13} /> Options{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>

          <div id="sched-tools" className={clsx('contents', !mobileTools && 'max-sm:hidden')}>
            {/* Search */}
            <div className="relative flex-1 max-w-[200px] max-sm:max-w-none max-sm:basis-full ml-1 max-sm:ml-0">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                aria-label="Search the board"
                className="w-full h-8 max-sm:h-10 pl-7 pr-7 text-xs border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:border-primary-400 focus:bg-white placeholder:text-slate-400 transition-colors"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button onClick={() => setSearch('')} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-600">
                  <X size={12} />
                </button>
              )}
            </div>

            {/* Filter */}
            <div ref={filterRef} className="relative shrink-0">
              <button
                onClick={() => setFilterOpen(o => !o)}
                aria-expanded={filterOpen}
                className={clsx(
                  'h-8 max-sm:h-10 flex items-center gap-1.5 px-2.5 border rounded-lg text-xs font-medium transition-colors',
                  activeFilterCount > 0
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                )}>
                <Filter size={12} />
                Filter
                {activeFilterCount > 0 && (
                  <span className="min-w-[16px] h-4 px-1 rounded-full bg-primary-500 text-white text-[10px] font-bold flex items-center justify-center leading-none tabular-nums">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <AnimatePresence>
                {filterOpen && (
                  <FilterPanel
                    open={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    filterStatuses={filterStatuses} setFilterStatuses={setFilterStatuses}
                    filterTypes={filterTypes} setFilterTypes={setFilterTypes}
                    filterSortBy={filterSortBy} setFilterSortBy={setFilterSortBy}
                    filterPublished={filterPublished} setFilterPublished={setFilterPublished}
                  />
                )}
              </AnimatePresence>
            </div>

            {/* Hide empty rows — only show staff/clients/homes with a shift in the period */}
            <button onClick={() => setFilteredOnly(o => !o)} aria-pressed={filteredOnly}
              title="Show only the staff, clients or homes that have a shift in this period"
              className={clsx('h-8 max-sm:h-10 flex items-center gap-1.5 px-2.5 border rounded-lg text-xs font-medium transition-colors shrink-0',
                filteredOnly ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')}>
              {filteredOnly && <Check size={12} />} Hide empty rows
            </button>

            {/* Reset view */}
            {showFilterStrip && (
              <button onClick={resetView} title="Clear filters and jump to today"
                className="h-8 max-sm:h-10 flex items-center gap-1.5 px-2.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 bg-white hover:bg-slate-50 shrink-0">
                <RotateCcw size={12} /> Reset
              </button>
            )}

            {/* EVV / Attendance toggle */}
            <button
              onClick={() => setShowAttendance(o => !o)}
              aria-pressed={showAttendance}
              className={clsx(
                'h-8 max-sm:h-10 flex items-center gap-1.5 px-2.5 border rounded-lg text-xs font-medium transition-colors shrink-0',
                showAttendance
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              )}>
              <Clock size={12} /> EVV
            </button>

            {/* Bulk edit — toggles the List view (table) where all bulk modifications live */}
            <button
              onClick={() => setBoardLayout(b => b === 'list' ? 'board' : 'list')}
              aria-pressed={boardLayout === 'list'}
              className={clsx(
                'h-8 max-sm:h-10 flex items-center gap-1.5 px-2.5 border rounded-lg text-xs font-medium transition-colors shrink-0',
                boardLayout === 'list'
                  ? 'border-violet-300 bg-violet-50 text-violet-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              )}>
              <Check size={12} /> {boardLayout === 'list' ? 'Back to Board' : 'Bulk Edit'}
            </button>

            {/* Select — multi-select shifts directly on the board, then bulk-act from the bottom bar */}
            {boardLayout !== 'list' && (
              <button
                onClick={() => { setBulkMode(m => { if (m) setSelectedIds(new Set()); return !m; }); }}
                aria-pressed={bulkMode}
                className={clsx(
                  'h-8 max-sm:h-10 flex items-center gap-1.5 px-2.5 border rounded-lg text-xs font-medium transition-colors shrink-0',
                  bulkMode
                    ? 'border-violet-300 bg-violet-50 text-violet-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                )}>
                <Check size={12} /> {bulkMode ? 'Selecting…' : 'Select'}
              </button>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 shrink-0">
            {/* Refresh — reload the schedule */}
            <button onClick={loadData} disabled={loading || refreshing} title="Refresh schedule" aria-label="Refresh schedule"
              className="h-8 flex items-center gap-1.5 px-2.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50 max-sm:hidden">
              <RefreshCw size={13} className={loading || refreshing ? 'animate-spin' : ''} />
              <span className="hidden lg:inline">Refresh</span>
            </button>

            <ViewDropdown view={view} setView={setView} compact={compact} setCompact={setCompact}
              expand={expand} setExpand={setExpand} />

            <div className="w-px h-5 bg-slate-200 max-sm:hidden" />

            {/* Auto Roster — label collapses to icon below xl to save space (also in More on phones) */}
            <button onClick={runAutoRoster} disabled={loadingRoster} title="AI Auto Roster" aria-label="Auto roster"
              className="h-8 flex items-center gap-1.5 px-2.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition-colors disabled:opacity-50 max-sm:hidden">
              {loadingRoster ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}<span className="hidden xl:inline">Auto Roster</span>
            </button>

            {/* Scheduling Assistant — staged batch auto-assign (also in More on phones) */}
            <button onClick={() => setAssistantOpen(true)} title="Scheduling Assistant" aria-label="Scheduling assistant"
              className="h-8 flex items-center gap-1.5 px-2.5 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 text-xs font-semibold hover:bg-violet-100 transition-colors max-sm:hidden">
              <ListChecks size={13} /><span className="hidden xl:inline">Assistant</span>
            </button>

            {/* Primary: Add Shift */}
            <button onClick={() => { setDefaultDate(null); setDefaultStaff(null); setModal({}); }}
              className="h-8 max-sm:h-10 flex items-center gap-1.5 px-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-xs font-bold shadow-sm transition-colors shrink-0">
              <Plus size={13} /> Add Shift
            </button>

            {/* Publish all — prominent when there are drafts */}
            {features.publish_shifts && unpublishedCount > 0 && (
              <button onClick={publishAll}
                className="h-8 max-sm:h-10 flex items-center gap-1.5 px-3 rounded-lg text-xs font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors shadow-sm">
                <Send size={12} />
                Publish {unpublishedCount}
              </button>
            )}

            {/* More actions dropdown */}
            <MoreActionsDropdown
              onBulkSchedule={() => setBulkModal(true)}
              onBulkCancel={() => setBulkCancelModal(true)}
              onAutoRoster={runAutoRoster}
              onAssistant={() => setAssistantOpen(true)}
              onNotify={notifyUpcoming}
              onRefresh={loadData}
              loadingRoster={loadingRoster}
            />
          </div>
        </div>

        {/* ── Applied filters: always said out loud, one removable chip each ── */}
        {showFilterStrip && (
          <div role="region" aria-label="Applied filters"
            className="flex items-center gap-1.5 px-4 py-1.5 bg-primary-50/50 border-b border-primary-100 shrink-0 overflow-x-auto [scrollbar-width:thin]">
            <span className="text-[11px] font-semibold text-primary-800 shrink-0">Showing only:</span>
            {filterStatuses.map(v => (
              <FilterChip key={`st-${v}`} label={`Status: ${(STATUSES.find(s => s.value === v) || {}).label || v}`}
                onRemove={() => setFilterStatuses(p => p.filter(x => x !== v))} />
            ))}
            {filterTypes.map(v => (
              <FilterChip key={`ty-${v}`} label={`Service: ${serviceName(shiftTypes, v)}`}
                onRemove={() => setFilterTypes(p => p.filter(x => x !== v))} />
            ))}
            {filterPublished !== 'all' && (
              <FilterChip label={filterPublished === 'published' ? 'Published shifts' : 'Unpublished shifts'} onRemove={() => setFilterPublished('all')} />
            )}
            {filteredOnly && <FilterChip label="Rows with shifts" onRemove={() => setFilteredOnly(false)} />}
            {search && <FilterChip label={`Search: “${search}”`} onRemove={() => setSearch('')} />}
            {hiddenByFilters > 0 && (
              <span className="text-[11px] text-slate-600 shrink-0 tabular-nums">· {hiddenByFilters} shift{hiddenByFilters !== 1 ? 's' : ''} hidden</span>
            )}
            {filterSortBy !== 'name' && SORT_LABELS[filterSortBy] && (
              <span className="text-[11px] text-slate-500 shrink-0">· Sorted by {SORT_LABELS[filterSortBy]}</span>
            )}
            <button onClick={clearFilters} className="ml-auto shrink-0 text-[11px] font-semibold text-primary-700 hover:text-primary-900 hover:underline px-1">
              Clear all
            </button>
          </div>
        )}

        {/* ── Grid ──────────────────────────────────────────────────────────── */}
        <div ref={boardRef} aria-busy={loading || refreshing}
          className={clsx('relative flex-1 flex flex-col min-h-0 min-w-0 transition-opacity duration-150',
            refreshing && 'opacity-60 pointer-events-none')}>
        {refreshing && (
          <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden z-30" role="progressbar" aria-label="Updating the board">
            <div className="h-full w-1/3 bg-primary-500 sched-progress" />
          </div>
        )}
        {boardLayout === 'list' ? (
          <ServiceScheduleList
            embedded staff={staff} serviceTypes={shiftTypes} clients={clients}
            defaultFrom={format(days[0], 'yyyy-MM-dd')} defaultTo={format(days[days.length - 1], 'yyyy-MM-dd')}
            locationId={branchId}
          />
        ) : loading ? (
          <div className="flex-1 overflow-hidden" aria-label="Loading schedule">
            <div className="grid" style={{ gridTemplateColumns: `124px repeat(${Math.max(1, Math.min(days.length, 14))}, minmax(0, 1fr))` }}>
              {Array.from({ length: 7 * (Math.max(1, Math.min(days.length, 14)) + 1) }).map((_, i) => {
                const cols = Math.max(1, Math.min(days.length, 14)) + 1;
                const row = Math.floor(i / cols); const col = i % cols;
                return (
                  <div key={i} className={clsx('border-r border-b border-slate-200 p-2', row === 0 ? 'h-12 bg-white' : 'h-16')}>
                    {row === 0 ? <div className="h-3 w-10 mx-auto rounded bg-slate-200 animate-pulse" />
                      : col === 0 ? <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-slate-200 animate-pulse" /><div className="h-3 w-14 rounded bg-slate-200 animate-pulse" /></div>
                      : (row + col) % 3 === 0 ? <div className="h-10 rounded-md bg-slate-100 animate-pulse" /> : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : view === 'Monthly' ? (
          <MonthView days={days} currentDate={currentDate} shifts={filteredShifts} compact={compact}
            onShiftClick={bulkMode ? undefined : setModal} onAdd={openAddModal} onContextMenu={handleContextMenu}
            bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
        ) : viewMode === 'Staff' ? (
          <StaffGrid days={days} staff={visibleStaff} shifts={filteredShifts} compact={effCompact} expand={expand}
            onShiftClick={bulkMode ? undefined : setModal} onAdd={openAddModal} onContextMenu={handleContextMenu}
            search={search} sortBy={filterSortBy} leaveMap={leaveMap}
            bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
        ) : viewMode === 'Client' ? (
          <ClientGrid days={days} clients={visibleClients} shifts={filteredShifts} compact={effCompact} expand={expand}
            onShiftClick={bulkMode ? undefined : setModal} onAdd={openAddModal} onContextMenu={handleContextMenu}
            search={search} sortBy={filterSortBy}
            bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
        ) : viewMode === 'Facilities' ? (
          <FacilitiesGrid days={days} facilities={facilities} shifts={filteredShifts} compact={effCompact} expand={expand}
            onShiftClick={bulkMode ? undefined : setModal} onAdd={openAddModal} onContextMenu={handleContextMenu}
            search={search} bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
        ) : (
          <GroupGrid days={days} groupHomes={visibleHomes} shifts={filteredShifts} compact={effCompact} expand={expand}
            onShiftClick={bulkMode ? undefined : (s) => setGroupShiftModal({ shiftId: s.id })}
            onAdd={() => setGroupShiftModal({})} onContextMenu={handleContextMenu}
            search={search} bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
        )}
        </div>

        {/* ── Status bar ────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-4 px-4 py-1.5 bg-white border-t border-slate-100 shrink-0">
          {[
            { label: 'Total', value: filteredShifts.length, color: 'text-slate-700' },
            { label: 'Booked', value: filteredShifts.filter(s => s.status === 'booked').length, color: 'text-blue-600' },
            { label: 'Done', value: filteredShifts.filter(s => s.status === 'completed').length, color: 'text-emerald-600' },
          ].map(stat => (
            <div key={stat.label} className="flex items-baseline gap-1 shrink-0">
              <span className={clsx('text-sm font-bold tabular-nums', stat.color)}>{stat.value}</span>
              <span className="text-[11px] text-slate-500">{stat.label}</span>
            </div>
          ))}
          {(() => {
            const vacant = filteredShifts.filter(s => !s.staff_id && s.status !== 'cancelled' && s.status !== 'cancelled_with_charge').length;
            return (
              <button type="button" onClick={() => { setViewMode('Staff'); setFilteredOnly(false); }}
                title="Open the Staff view, where vacant shifts sit in the Vacant row at the top"
                className={clsx('flex items-baseline gap-1 shrink-0 rounded px-1 -mx-1 hover:bg-amber-50', vacant > 0 ? 'text-amber-700' : 'text-slate-500')}>
                <span className="text-sm font-bold tabular-nums">{vacant}</span>
                <span className="text-[11px] underline decoration-dotted underline-offset-2">Vacant</span>
              </button>
            );
          })()}
          <div className="flex-1" />
          <BoardKey shiftTypes={shiftTypes} />
        </div>
      </div>

      {/* ── DnD overlay ─────────────────────────────────────────────────────── */}
      <DragOverlay dropAnimation={null}>
        {activeShift ? (
          <div className="w-36 rotate-2 opacity-90">
            <ShiftCard shift={activeShift} isOverlay />
          </div>
        ) : null}
      </DragOverlay>

      {/* ── Context menu ────────────────────────────────────────────────────── */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x} y={contextMenu.y} shift={contextMenu.shift}
          onClose={() => setContextMenu(null)}
          onEdit={() => setModal(contextMenu.shift)}
          onDelete={() => cancelShift(contextMenu.shift)}
          onDuplicate={() => duplicateShift(contextMenu.shift)}
          onUncancel={() => uncancelShift(contextMenu.shift)}
        />
      )}

      {/* ── Shift modal ─────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {modal !== null && (
          <ShiftModal
            shift={modal?.id ? modal : null}
            defaultDate={defaultDate}
            defaultStaffId={defaultStaff}
            defaultClientId={defaultClientId}
            defaultJobBoard={defaultJobBoard}
            clients={clients}
            staff={staff}
            facilities={facilities}
            onClose={() => setModal(null)}
            onSave={() => { setModal(null); loadData(); }}
          />
        )}
      </AnimatePresence>

      {/* ── Bulk schedule modal ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {bulkModal && (
          <BulkScheduleModal
            staff={staff}
            clients={clients}
            onClose={() => setBulkModal(false)}
            onSave={() => { setBulkModal(false); loadData(); }}
          />
        )}
      </AnimatePresence>

      {/* AI Auto-Roster Modal */}
      <AnimatePresence>
        {rosterModal && (
          <AutoRosterModal
            data={rosterData}
            loading={loadingRoster}
            creating={rosterCreating}
            onClose={() => { setRosterModal(false); setRosterData(null); }}
            onCreate={createRosterShifts}
          />
        )}
      </AnimatePresence>

      {/* Scheduling Assistant — staged batch auto-assign */}
      <AnimatePresence>
        {assistantOpen && (
          <SchedulingAssistantModal
            currentDate={currentDate}
            onClose={() => setAssistantOpen(false)}
            onDone={() => { setAssistantOpen(false); loadData(); }}
          />
        )}
      </AnimatePresence>

      {/* Group shift create / manage (By-Group view) */}
      {groupShiftModal && (
        <GroupShiftModal
          shiftId={groupShiftModal.shiftId}
          groupHomes={groupHomes}
          onClose={() => setGroupShiftModal(null)}
          onSaved={loadData}
        />
      )}

      {/* Bulk Cancel Modal */}
      {bulkCancelPolicyOpen && (
        <BulkCancelPolicyModal
          shiftIds={[...selectedIds]}
          onClose={() => setBulkCancelPolicyOpen(false)}
          onDone={() => { setBulkCancelPolicyOpen(false); setSelectedIds(new Set()); setBulkMode(false); loadData(); }}
        />
      )}

      {bulkCancelModal && (
        <BulkCancelModal
          staff={staff}
          clients={clients}
          onClose={() => setBulkCancelModal(false)}
          onDone={() => { setBulkCancelModal(false); loadData(); }}
        />
      )}

      {/* EVV / Attendance drawer */}
      <AnimatePresence>
        {showAttendance && (
          <AttendanceDrawer shifts={filteredShifts} onClose={() => setShowAttendance(false)} />
        )}
      </AnimatePresence>

      {/* Bulk action floating bar */}
      {selectedIds.size > 0 && (
        <div role="toolbar" aria-label="Bulk actions"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-slate-900 text-white rounded-2xl px-3 py-2 shadow-2xl flex-wrap max-w-[95vw] justify-center">
          <span className="text-sm font-semibold tabular-nums pl-1">{selectedIds.size} selected</span>
          <button onClick={() => { setSelectedIds(new Set()); setBulkMode(false); }} className="text-xs text-slate-300 hover:text-white hover:underline">Clear</button>
          <div className="w-px h-5 bg-white/20" />
          <div className="flex items-center gap-1" role="group" aria-label="Edit">
            {[['day', 'Day'], ['time', 'Time'], ['worker', 'Worker'], ['service', 'Service'], ['copy', 'Copy']].map(([op, lbl]) => (
              <button key={op} onClick={() => setBulkOp(op)} className="text-sm px-2.5 py-1.5 bg-white/10 rounded-lg hover:bg-white/20">{lbl}</button>
            ))}
          </div>
          <div className="w-px h-5 bg-white/20" />
          <div className="flex items-center gap-1" role="group" aria-label="Status">
            <button onClick={() => bulkAction('publish')} className="text-sm px-2.5 py-1.5 bg-blue-600 rounded-lg hover:bg-blue-500">Publish</button>
            <button onClick={() => bulkAction('complete')} className="text-sm px-2.5 py-1.5 bg-green-600 rounded-lg hover:bg-green-500">Complete</button>
            <button onClick={async () => { if (await confirm('Undo complete on the selected shifts? This deletes their timesheets. Invoiced shifts are skipped.', { title: 'Undo complete', danger: true, confirmLabel: 'Undo complete' })) runBulk('undo-complete', {}, 'Reverted'); }}
              disabled={bulkBusy} className="text-sm px-2.5 py-1.5 bg-white/10 rounded-lg hover:bg-white/20">Undo complete</button>
            <button onClick={() => runBulk('recalc-fees', {}, 'Recalculated', { keepSelection: true })} disabled={bulkBusy}
              title="Re-price the selected shifts from their current rates" className="text-sm px-2.5 py-1.5 bg-white/10 rounded-lg hover:bg-white/20">Recalculate fees</button>
          </div>
          <div className="w-px h-5 bg-white/20" />
          <BulkMoreMenu busy={bulkBusy}
            onUncancel={bulkUncancel}
            onCancel={() => setBulkCancelPolicyOpen(true)}
            onDelete={async () => { if (await confirm(`Permanently delete ${selectedIds.size} shift${selectedIds.size !== 1 ? 's' : ''}? This can't be undone. Invoiced shifts are skipped.`, { title: 'Delete shifts', danger: true, confirmLabel: `Delete ${selectedIds.size}` })) runBulk('delete', {}, 'Deleted'); }} />
          <button onClick={() => { setSelectedIds(new Set()); setBulkMode(false); }} aria-label="Clear selection" className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10"><X size={16}/></button>
        </div>
      )}
      {bulkOp && (
        <BulkOpModal op={bulkOp} count={selectedIds.size} staff={staff} shiftTypes={shiftTypes} busy={bulkBusy}
          onClose={() => setBulkOp(null)} onRun={runBulk} />
      )}
      {/* Confirm a drag-move before it's applied */}
      {pendingMove && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setPendingMove(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1.5"><RotateCcw size={16} className="text-primary-500" /><h3 className="font-semibold text-slate-800">Confirm move</h3></div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Move <b className="text-slate-800">{pendingMove.shiftLabel}</b> to <b className="text-slate-800">{pendingMove.newShiftDate}</b>
              {pendingMove.toWho ? <> → <b className="text-slate-800">{pendingMove.toWho}</b></> : ''}?
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPendingMove(null)} className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={commitMove} className="px-3 py-1.5 rounded-lg text-sm font-bold bg-primary-500 hover:bg-primary-600 text-white">Confirm move</button>
            </div>
          </div>
        </div>
      )}
    </DragLockContext.Provider>
    </DndContext>
  );
}
